import React, { useState, useEffect, useRef } from 'react';
import { adminAPI, departementsAPI, calendrierAPI, authAPI } from '../services/api';
import ChampDate from './ChampDate';
import AppLayout from './AppLayout';
import { estSuperAdmin, estAdminComposante, departementsAdministres } from '../services/droits';
import { NAV_STRUCTURE } from './AppSidebar';
import './AdminPanel.css';
import { CourrielLigneCarte, CourrielModale, CourrielConfiguration } from './AdminCourriel';
import { EnteteLigneCarte, EnteteModale } from './AdminEnteteDocuments';
// Lot D.1 : l'onglet Documents (conservation, purge, inventaire).
import AdminDocuments from './AdminDocuments';
// Aide contextuelle, étape A3 : les manques signalés depuis le « ? » des modules.
import AdminAide from './AdminAide';
import { aideAPI } from '../services/api';

// Colonnes de la matrice des droits — dérivées de NAV_STRUCTURE (source unique des
// modules, cf. spec §4.1). La catégorie « Gestion » (Administration) est exclue :
// l'accès au panneau ne dépend pas de la matrice mais de `is_super_admin` /
// `user_departements.role_dept` (spec §4.2).
const MODULES_MATRICE = NAV_STRUCTURE
    .filter(section => !section.adminOnly)
    .flatMap(section => section.items.map(item => ({
        key: item.key,
        cat: section.cat,
        label: item.label,
        icon: item.icon
    })));

// Trois niveaux (spec §4.1) — les libellés et l'ordre du cycle viennent de la maquette
const NIVEAUX = ['modifier', 'consulter', 'masque'];
const NIVEAU_COURT = { modifier: 'M', consulter: 'C', masque: '–' };
const NIVEAU_LONG = { modifier: 'Modifier', consulter: 'Consulter', masque: 'Masqué' };
const NIVEAU_CLASSE = { modifier: 'lv-mod', consulter: 'lv-con', masque: 'lv-mask' };
const SEG_CLASSE = { modifier: 'on-mod', consulter: 'on-con', masque: 'on-mask' };
// D'où vient le niveau appliqué (le serveur renvoie la source, pas seulement le niveau)
const SOURCE_LABEL = {
    exception: 'Exception',
    profil: 'Profil',
    defaut: 'Défaut',
    'super-admin': 'Super-admin'
};

// Onglets et icônes repris de doc/maquettes/admin_maquette.html (constante TABS).
// SUPER_ONLY = onglets réservés au super-administrateur (spec §2).
const TABS = [
    { key: 'users',       label: 'Utilisateurs',        icon: 'fa-users' },
    { key: 'droits',      label: 'Profils & droits',    icon: 'fa-user-shield' },
    { key: 'depts',       label: 'Départements',        icon: 'fa-building-columns' },
    { key: 'sig',         label: 'Signatures & délég.', icon: 'fa-signature' },
    { key: 'calendriers', label: 'Calendriers',         icon: 'fa-calendar-days' },
    { key: 'api',         label: 'API & connexions',    icon: 'fa-plug' },
    { key: 'docs',        label: 'Documents',           icon: 'fa-box-archive' },
    { key: 'config',      label: 'Configuration',       icon: 'fa-sliders' },
    { key: 'aide',        label: 'Aide',                icon: 'fa-circle-question' },
];
// Onglets réservés au super-administrateur. « Départements » n'en fait PAS partie :
// un administrateur de département y accède en périmètre réduit — il y voit ses
// départements et n'y modifie que leur identité (nom, description, logo), la spec §3
// lui accordant le logo de son département. Créer, supprimer, activer un département
// et nommer ses administrateurs restent des actes globaux, masqués pour lui.
const SUPER_ONLY = ['calendriers', 'api', 'docs', 'config', 'aide'];

/**
 * Lot CO-a — l'onglet « depts » porte le nom de ce que chacun y gère : « Composantes »
 * (super-admin), « Départements » (administrateur de composante), « Mon département ».
 */
function ongletAffiche(onglet, user) {
    if (onglet.key !== 'depts') return onglet;
    if (estSuperAdmin(user)) return { ...onglet, label: 'Composantes', icon: 'fa-sitemap' };
    if (estAdminComposante(user)) return { ...onglet, label: 'Départements' };
    return { ...onglet, label: 'Mon département' };
}

/** Les départements regroupés sous leur composante (l'API les trie par composante puis par nom). */
function parComposante(departements) {
    const groupes = [];
    for (const d of departements) {
        const cle = d.composante_id ?? 0;
        let g = groupes.find(x => x.id === cle);
        if (!g) { g = { id: cle, code: d.composante_code, nom: d.composante_nom, departements: [] }; groupes.push(g); }
        g.departements.push(d);
    }
    return groupes;
}
const DEFAULT_TAB_ORDER = TABS.map(t => t.key);

// Étapes de livraison des onglets encore à construire (cf. doc/SUIVI_DEV.md §2)
// Tous les onglets sont livrés : plus aucun encart « à venir ».
const ETAPES_A_VENIR = {};

/**
 * Périmètre du panneau : départements que l'utilisateur peut administrer.
 * Super-administrateur = tous les départements actifs ; administrateur de
 * département = uniquement ceux où `role_dept = 'admin'` — pas ceux dont il est
 * simple membre (spec §3).
 */
function departementsDuPerimetre(user, departements) {
    const actifs = (departements || []).filter(d => d.actif);
    const administres = departementsAdministres(user); // null = tous
    return administres === null ? actifs : actifs.filter(d => administres.includes(d.id));
}

/** Modale de confirmation — remplace window.confirm (charte : jamais alert/confirm/prompt). */
function ModaleConfirmation({ titre, message, libelleConfirmer, danger, onConfirmer, onAnnuler }) {
    return (
        <div className="admin-modal-overlay" onClick={onAnnuler}>
            <div className="admin-modal sm" onClick={e => e.stopPropagation()}>
                <div className="admin-modal-h">
                    <i className={`fa-solid ${danger ? 'fa-triangle-exclamation' : 'fa-circle-question'}`}
                       style={danger ? { color: '#dc2626' } : undefined} />
                    {titre}
                    <button className="x" onClick={onAnnuler} aria-label="Fermer">&times;</button>
                </div>
                <div className="admin-modal-b" style={{ fontSize: 13, color: '#334155' }}>{message}</div>
                <div className="admin-modal-f">
                    <button className="a-btn a-btn-secondary" onClick={onAnnuler}>Annuler</button>
                    <button className={`a-btn ${danger ? 'a-btn-danger' : 'a-btn-primary'}`} onClick={onConfirmer}>
                        <i className="fa-solid fa-check" /> {libelleConfirmer || 'Confirmer'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function AdminPanel({ user, onLogout, onViewDepartement, onNavigate }) {
    const savedOrder = user?.preferences?.adminTabOrder;
    const initialOrder = Array.isArray(savedOrder) && savedOrder.length === DEFAULT_TAB_ORDER.length ? savedOrder : DEFAULT_TAB_ORDER;
    // Lot D.1 : le lien du bandeau de conservation ouvre l'onglet Documents (?onglet=docs, ou l'événement s'il est déjà ouvert).
    const [activeTab, setActiveTab] = useState(() => {
        const demande = new URLSearchParams(window.location.search).get('onglet');
        return TABS.some(t => t.key === demande) ? demande : initialOrder[0];
    });
    useEffect(() => {
        const ouvrir = (e) => { if (TABS.some(t => t.key === e.detail)) setActiveTab(e.detail); };
        window.addEventListener('admin:onglet', ouvrir);
        return () => window.removeEventListener('admin:onglet', ouvrir);
    }, []);
    const [users, setUsers] = useState([]);
    const [departements, setDepartements] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showUserForm, setShowUserForm] = useState(false);
    // Lot INV-b (v83) : l'accès au compte — choix de la fiche, boîtes d'envoi réglées, filtre, menu « … », lien à copier
    const [formAcces, setFormAcces] = useState('invitation');
    const [boitesReglees, setBoitesReglees] = useState([]);
    const [filtreAcces, setFiltreAcces] = useState('');
    const [menuAcces, setMenuAcces] = useState(null);
    const [lienCopie, setLienCopie] = useState(null);       // { user, lien, type, expire_le, copie, raison }
    // Lot INV-c : version de test (point 4), « Inviter les N comptes », journal des accès
    const [versionTest, setVersionTest] = useState(false);
    const [inviterN, setInviterN] = useState(null);         // { coches: [ids], enCours, essai, bilan }
    const [journal, setJournal] = useState(null);           // { jours, lignes, evenements, filtre, recherche, chargement }
    const [editingUser, setEditingUser] = useState(null);
    const [showDeptForm, setShowDeptForm] = useState(false);
    const [editingDept, setEditingDept] = useState(null);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [avertissement, setAvertissement] = useState('');

    // Calendriers
    const [annees, setAnnees] = useState([]);
    // Suppression d'une année : confirmation par re-saisie du libellé exact (spec §9),
    // en modale. L'ancien champ en ligne dans le tableau était un piège : le libellé
    // n'était qu'un `placeholder` — il ressemblait à un champ déjà rempli — et le
    // bouton de validation restait désactivé sans dire pourquoi.
    const [anneeASupprimer, setAnneeASupprimer] = useState(null);
    const [texteSuppression, setTexteSuppression] = useState('');
    // Création / régénération d'une année universitaire (lot 3)
    const [showAnneeForm, setShowAnneeForm] = useState(false);
    const [anneeARegenerer, setAnneeARegenerer] = useState(null); // null = création
    const [formAnneeDebut, setFormAnneeDebut] = useState(new Date().getFullYear());
    const [formAnneeZone, setFormAnneeZone] = useState('C');
    const [anneeEnCours, setAnneeEnCours] = useState(false);

    // Logos
    const [logoUploading, setLogoUploading] = useState({});

    // Ordre des onglets — chargé depuis les préférences utilisateur.
    // Les préférences d'avant le rétrofit ne contenaient que 3 onglets : on
    // complète avec les nouveaux plutôt que de repartir de zéro.
    const [tabOrder, setTabOrder] = useState(() => {
        const saved = user?.preferences?.adminTabOrder;
        if (!Array.isArray(saved) || saved.length === 0) return DEFAULT_TAB_ORDER;
        const connus = saved.filter(k => DEFAULT_TAB_ORDER.includes(k));
        const manquants = DEFAULT_TAB_ORDER.filter(k => !connus.includes(k));
        return [...connus, ...manquants];
    });
    const dragTab = useRef(null);
    const [dragOverTab, setDragOverTab] = useState(null);

    // Gouvernance : le super-administrateur voit tous les onglets et bascule de
    // département ; l'administrateur de département est plafonné (spec §3).
    const superAdmin = estSuperAdmin(user);
    // Aide contextuelle (A3) : le compteur des signalements « nouveaux », sur l'onglet Aide.
    const [aideNouveaux, setAideNouveaux] = useState(0);
    useEffect(() => {
        if (!superAdmin) return;
        aideAPI.signalements({ suite: 'nouveau' }).then((r) => setAideNouveaux(r.data.nouveaux)).catch(() => { /* compteur illisible : non affiché */ });
    }, [superAdmin]);
    // Lot CO-a : l'étage du milieu — administre les départements de sa composante sans en être membre
    const adminComposante = !superAdmin && estAdminComposante(user);
    const peutNommer = superAdmin || adminComposante;
    const [composantes, setComposantes] = useState([]);
    const [compoForm, setCompoForm] = useState(null);       // null = fermée
    const [candidatsCompo, setCandidatsCompo] = useState([]);
    const [formDeptComposante, setFormDeptComposante] = useState('');
    // Lot CO-b : premier administrateur (création), récapitulatif de réussite, suppression définitive
    const [formPremierAdmin, setFormPremierAdmin] = useState('');
    const [candidatsPremier, setCandidatsPremier] = useState([]);
    const [erreurDept, setErreurDept] = useState('');
    const [creationReussie, setCreationReussie] = useState(null);
    const [suppressionDept, setSuppressionDept] = useState(null);
    // S2 : périmètre de l'onglet Signatures — '' = le département de la barre du haut ; « c<id> » / « d<id> »
    const [perimetreSig, setPerimetreSig] = useState('');
    const [replies, setReplies] = useState(() => {
        try { return JSON.parse(localStorage.getItem('admin.composantes.repliees') || '[]'); } catch { return []; }
    });
    // Périmètre de départ : le premier département ADMINISTRÉ (pas un département
    // dont on serait simple membre — le serveur refuserait la requête).
    const [deptCourantId, setDeptCourantId] = useState(() => {
        const liens = user?.departements || [];
        return ((liens.find(d => d.role_dept === 'admin') || liens[0])?.id) ?? null;
    });
    const [depMenuOuvert, setDepMenuOuvert] = useState(false);
    // Le périmètre de l'onglet Signatures suit la barre du haut, sauf choix explicite (S2)
    const perimetreSigEffectif = perimetreSig || deptCourantId;
    useEffect(() => { setPerimetreSig(''); }, [deptCourantId]);

    // Confirmation (remplace window.confirm)
    const [confirmation, setConfirmation] = useState(null);
    const demanderConfirmation = (options) => setConfirmation(options);

    // Onglet Utilisateurs — filtres de la maquette (recherche, profil, statut)
    const [profils, setProfils] = useState([]);
    const [enseignants, setEnseignants] = useState([]);
    const [formNomAffiche, setFormNomAffiche] = useState('');
    const [formEnseignant, setFormEnseignant] = useState('');
    const [formProfil, setFormProfil] = useState('');
    const [rechercheUser, setRechercheUser] = useState('');
    const [filtreProfil, setFiltreProfil] = useState('');
    const [filtreStatut, setFiltreStatut] = useState('');
    // Filtre Département de l'onglet Utilisateurs — '' = tous les départements du
    // périmètre. C'est lui, et non le sélecteur de la topbar, qui borne la liste.
    const [filtreDept, setFiltreDept] = useState('');

    // User form
    const [formEmail, setFormEmail] = useState('');
    const [formPassword, setFormPassword] = useState('');
    const [formNom, setFormNom] = useState('');
    const [formPrenom, setFormPrenom] = useState('');
    const [formRole, setFormRole] = useState(2);
    const [formDepts, setFormDepts] = useState([]);
    // Département auquel s'applique le profil choisi dans la fiche
    const [deptFiche, setDeptFiche] = useState(null);

    // ---- Onglet « Configuration » (0.C.8) ----
    const [settings, setSettings] = useState(null);
    // Lot 3.S-a — l'envoi des courriels : état résumé par département (cartes),
    // environnement (redirection du serveur de test, Mailpit), modale ouverte.
    const [etatsCourriel, setEtatsCourriel] = useState({});
    const [envCourriel, setEnvCourriel] = useState(null);
    const [courrielDept, setCourrielDept] = useState(null);
    const [etatsEntete, setEtatsEntete] = useState({});     // lot ADM-E (D28) : l'en-tête des documents de chaque carte
    const [enteteDept, setEnteteDept] = useState(null);
    // Suivi de l'occupation (lot 5.3) : version de migration réelle, poids de la
    // base, tables les plus lourdes. Lecture seule.
    const [occupation, setOccupation] = useState(null);
    const [formNomAppli, setFormNomAppli] = useState('');
    const [formUrlPublique, setFormUrlPublique] = useState('');
    // Lot 3.4 de Stage (écart E85) : le plafond d'un document déposé, en Mo.
    const [formTailleDoc, setFormTailleDoc] = useState('10');
    const [formDureeSession, setFormDureeSession] = useState('24');
    const [formLongueurMdp, setFormLongueurMdp] = useState('10');
    const [logoUnivEnCours, setLogoUnivEnCours] = useState(false);

    // ---- Onglet « Signatures & délégations » (0.C.5) ----
    const [signatures, setSignatures] = useState([]);
    const [chiffrementOk, setChiffrementOk] = useState(true);
    const [delegations, setDelegations] = useState([]);
    // Dépôt : les fichiers choisis, puis la ré-authentification (step-up)
    const [depotPour, setDepotPour] = useState(null);
    const [fichierSignature, setFichierSignature] = useState(null);
    const [fichierTampon, setFichierTampon] = useState(null);
    const [motDePasseReauth, setMotDePasseReauth] = useState('');
    const [depotEnCours, setDepotEnCours] = useState(false);
    const [showDelegForm, setShowDelegForm] = useState(false);
    const [formDelegTitulaire, setFormDelegTitulaire] = useState('');
    const [formDelegDelegue, setFormDelegDelegue] = useState('');
    const [formDelegDu, setFormDelegDu] = useState('');
    const [formDelegAu, setFormDelegAu] = useState('');
    const [formDelegMotif, setFormDelegMotif] = useState('');

    // ---- Onglet « API & connexions » (0.C.6 / 0.C.7) ----
    const [sousOngletApi, setSousOngletApi] = useState('cles');
    const [apiKeys, setApiKeys] = useState([]);
    const [apiJournal, setApiJournal] = useState([]);
    const [ssoConfig, setSsoConfig] = useState(null);
    const [showApiForm, setShowApiForm] = useState(false);
    const [formApiNom, setFormApiNom] = useState('');
    const [formApiScopes, setFormApiScopes] = useState([]);
    const [formApiPerso, setFormApiPerso] = useState(false);
    const [formApiExpiration, setFormApiExpiration] = useState('');
    // Clé révélée : affichée UNE SEULE FOIS, ni l'API ni la base ne peuvent la redonner
    const [cleRevelee, setCleRevelee] = useState(null);
    const [cleCopiee, setCleCopiee] = useState(false);

    // ---- Onglet « Profils & droits » (0.C.3) ----
    // Cet onglet travaille sur le département du SÉLECTEUR : une matrice de droits
    // n'a de sens que pour un département donné.
    const [sousOngletDroits, setSousOngletDroits] = useState('matrice');
    const [usersDuDeptDroits, setUsersDuDeptDroits] = useState([]);
    const [ficheUserId, setFicheUserId] = useState(null);
    const [ficheDroits, setFicheDroits] = useState(null);
    const [showProfilForm, setShowProfilForm] = useState(false);
    const [profilDepuisGlobal, setProfilDepuisGlobal] = useState(false);
    const [formProfilNom, setFormProfilNom] = useState('');
    const [formProfilBase, setFormProfilBase] = useState('');

    // Dept form
    const [formDeptCode, setFormDeptCode] = useState('');
    const [formDeptNom, setFormDeptNom] = useState('');
    const [formDeptDesc, setFormDeptDesc] = useState('');
    // Administrateurs du département — une liste déroulante par administrateur
    // (maquette : `select#dAdmin`, étendu à plusieurs lignes). Une entrée vide vaut
    // « Aucun » : c'est ainsi qu'on retire un administrateur.
    const [formDeptAdmins, setFormDeptAdmins] = useState(['']);
    // Candidats : les comptes rattachés AU DÉPARTEMENT de la fiche ouverte — et non
    // la liste globale, qui proposait des collègues d'un autre département.
    const [candidatsAdmin, setCandidatsAdmin] = useState([]);

    // Deux périmètres, deux déclencheurs : le sélecteur de la topbar (périmètre de
    // travail) et le filtre Département de l'onglet Utilisateurs (liste affichée).
    // Le serveur borne de toute façon la réponse au périmètre du compte.
    useEffect(() => {
        loadAll(deptCourantId, filtreDept);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deptCourantId, filtreDept]);

    // Onglet « API & connexions » : chargé à l'entrée, pas au démarrage du panneau —
    // les clés et le journal n'intéressent que cet onglet.
    useEffect(() => {
        if (activeTab === 'api') chargerApi();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    // Onglet « Départements » : l'état d'envoi des courriels de chaque carte (lot 3.S-a)
    useEffect(() => {
        if (activeTab === 'depts') { chargerEtatsCourriel(); chargerEtatsEntete(); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    // Onglet « Configuration » : chargé à l'entrée
    useEffect(() => {
        if (activeTab === 'config') { chargerSettings(); chargerOccupation(); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    // Onglet « Signatures & délégations » : dépend du département affiché, ou du périmètre choisi
    // (S2, lot CO-a : toute une composante pour son administrateur et le super-admin)
    useEffect(() => {
        if (activeTab === 'sig' && perimetreSigEffectif) chargerSignatures(perimetreSigEffectif);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, perimetreSigEffectif]);

    // Onglet « Profils & droits » : la liste des utilisateurs du département et les
    // droits de la personne affichée sont chargés à l'entrée dans l'onglet, et
    // rechargés si l'on change de département dans la topbar.
    useEffect(() => {
        if (activeTab !== 'droits' || !deptCourantId) return;
        (async () => {
            const liste = await chargerUsersDuDeptDroits(deptCourantId);
            const choisi = (liste || []).find(u => u.id === ficheUserId) || (liste || [])[0] || null;
            setFicheUserId(choisi?.id ?? null);
            if (choisi) await chargerFicheDroits(choisi.id, deptCourantId);
            else setFicheDroits(null);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, deptCourantId]);

    // Le périmètre de départ vient du jeton ; dès que la liste réelle des
    // départements est connue, on se recale dessus (département désactivé entre-temps,
    // compte rattaché à un département dont il n'est que membre…).
    useEffect(() => {
        if (departements.length === 0) return;
        const visibles = departementsDuPerimetre(user, departements);
        if (!visibles.some(d => d.id === deptCourantId)) {
            setDeptCourantId(visibles[0]?.id ?? null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [departements]);

    // `initialise` : le voile de chargement ne couvre que la PREMIÈRE ouverture du
    // panneau. Un changement de département rafraîchit les listes sans faire
    // disparaître la topbar et son sélecteur.
    const [initialise, setInitialise] = useState(false);

    // `deptTravail` : périmètre du sélecteur (fiches, profils, enseignants).
    // `deptListe`   : filtre Département de la liste ('' = tous les départements).
    const loadAll = async (deptTravail = deptCourantId, deptListe = filtreDept) => {
        if (!initialise) setLoading(true);
        try {
            const [usersRes, deptsRes, rolesRes, anneesRes, profilsRes] = await Promise.all([
                adminAPI.getUsers(deptListe || undefined),
                adminAPI.getDepartements(),
                adminAPI.getRoles(),
                calendrierAPI.getAllAvecStats(),
                // TOUS les profils visibles (globaux + ceux des départements du
                // périmètre) : chaque écran filtre ensuite ceux qui le concernent.
                // Les charger par département rendait invisible un profil créé par
                // l'administrateur d'un autre département.
                adminAPI.getProfils()
            ]);
            // 2026-10-10 : TOUT le périmètre (le serveur le borne : un administrateur de département ne voit que ses
            // départements) — la fiche d'un compte filtre ensuite sur SES départements cochés. Chargée sur le seul
            // département de travail, la liste était vide pour un compte d'un autre département.
            adminAPI.getEnseignants()
                .then(r => setEnseignants(r.data.enseignants || []))
                .catch(() => setEnseignants([]));
            setUsers(usersRes.data.users);
            setBoitesReglees(usersRes.data.boites_reglees || []);
            setVersionTest(usersRes.data.version_test === true);
            setDepartements(deptsRes.data.departements);
            setRoles(rolesRes.data.roles);
            setAnnees(anneesRes.data.annees || []);
            setProfils(profilsRes.data.profils || []);
            // Lot CO-a : les composantes du périmètre (aucune pour un administrateur de département)
            if (superAdmin || adminComposante) {
                adminAPI.getComposantes()
                    .then(r => setComposantes(r.data.composantes || []))
                    .catch(() => setComposantes([]));
            }
        } catch (err) {
            setError('Erreur chargement données');
            console.error(err);
        } finally {
            setLoading(false);
            setInitialise(true);
        }
    };

    // ---- Années universitaires : création et régénération (lot 3) ----
    //
    // Une année est transverse à tous les départements : sa création est un acte du
    // super-administrateur, ici et non dans le Paramétrage d'un département.

    const ZONES = [
        { valeur: 'A', libelle: 'Zone A' },
        { valeur: 'B', libelle: 'Zone B' },
        { valeur: 'C', libelle: 'Zone C' },
        // « NC » n'efface rien : on ne va pas chercher les vacances, on garde celles
        // déjà enregistrées. Le libellé le dit, pour qu'on ne s'attende pas à un vidage.
        { valeur: 'NC', libelle: 'Aucune — ne pas récupérer les vacances (les existantes sont conservées)' }
    ];

    const ouvrirCreationAnnee = () => {
        const derniere = [...annees].sort((a, b) => (b.annee_debut || 0) - (a.annee_debut || 0))[0];
        // Année suivante et zone de la dernière créée : le cas normal ne demande aucune saisie
        setFormAnneeDebut((derniere?.annee_debut || new Date().getFullYear() - 1) + 1);
        setFormAnneeZone(derniere?.zone || 'C');
        setAnneeARegenerer(null);
        setShowAnneeForm(true);
    };

    const ouvrirRegeneration = (annee) => {
        setFormAnneeDebut(annee.annee_debut);
        setFormAnneeZone(annee.zone || 'C');
        setAnneeARegenerer(annee);
        setShowAnneeForm(true);
    };

    const fermerFormAnnee = () => { setShowAnneeForm(false); setAnneeARegenerer(null); };

    const handleSaveAnnee = async () => {
        setAnneeEnCours(true);
        try {
            const res = anneeARegenerer
                ? await calendrierAPI.regenererAnnee(anneeARegenerer.id, formAnneeZone)
                : await calendrierAPI.creerAnnee(parseInt(formAnneeDebut, 10), formAnneeZone);
            // L'API officielle publie les dates de vacances tardivement : la génération
            // réussit sans elles et le dit, au lieu d'échouer ou de se taire.
            if (res.data.avertissement) showAvertissement(res.data.avertissement, res.data.message);
            else showMsg(res.data.message);
            fermerFormAnnee();
            const rafraichi = await calendrierAPI.getAllAvecStats();
            setAnnees(rafraichi.data.annees || []);
        } catch (err) {
            showErr(err.response?.data?.error || "Erreur lors de la génération du calendrier");
        } finally {
            setAnneeEnCours(false);
        }
    };

    const handleToggleVisibilite = async (annee) => {
        try {
            await calendrierAPI.toggleVisibilite(annee.id);
            setAnnees(prev => prev.map(a => a.id === annee.id ? { ...a, visible: !a.visible } : a));
            showMsg(`Année ${annee.libelle} ${annee.visible ? 'cachée' : 'rendue visible'}`);
        } catch (err) {
            showErr(err.response?.data?.error || 'Erreur');
        }
    };

    const handleActiverAnnee = (annee) => demanderConfirmation({
        titre: 'Activer une année universitaire',
        message: `Rendre « ${annee.libelle} » l'année active ? L'année actuellement active sera désactivée.`,
        libelleConfirmer: 'Activer',
        action: () => activerAnnee(annee)
    });

    const activerAnnee = async (annee) => {
        try {
            await calendrierAPI.activerAnnee(annee.id);
            setAnnees(prev => prev.map(a => ({ ...a, active: a.id === annee.id, visible: a.id === annee.id ? true : a.visible })));
            showMsg(`Année ${annee.libelle} activée`);
        } catch (err) {
            showErr(err.response?.data?.error || "Erreur lors de l'activation");
        }
    };

    const ouvrirSuppressionAnnee = (annee) => {
        setAnneeASupprimer(annee);
        setTexteSuppression('');
    };

    const fermerSuppressionAnnee = () => {
        setAnneeASupprimer(null);
        setTexteSuppression('');
    };

    const handleDeleteAnnee = async (annee) => {
        try {
            await calendrierAPI.deleteAnnee(annee.id);
            setAnnees(prev => prev.filter(a => a.id !== annee.id));
            fermerSuppressionAnnee();
            showMsg(`Année ${annee.libelle} supprimée`);
        } catch (err) {
            showErr(err.response?.data?.error || 'Erreur suppression');
            fermerSuppressionAnnee();
        }
    };

    const showMsg = (msg) => { setMessage(msg); setError(''); setAvertissement(''); setTimeout(() => setMessage(''), 3000); };
    const showErr = (msg) => { setError(msg); setMessage(''); };
    // Avertissement : ni succès ni échec — l'action a abouti, mais incomplètement
    // (typiquement : calendrier généré sans les vacances, pas encore publiées).
    const showAvertissement = (msg, succes) => { setAvertissement(msg); setMessage(succes || ''); setError(''); };

    // ============================================
    // USERS
    // ============================================

    const resetUserForm = () => {
        setFormEmail(''); setFormPassword(''); setFormNom('');
        setFormPrenom(''); setFormRole(null); setFormDepts([]);
        setFormNomAffiche(''); setFormEnseignant(''); setFormProfil('');
        setDeptFiche(null); setFormAcces('invitation');
        setEditingUser(null); setShowUserForm(false);
    };

    const openCreateUser = () => {
        resetUserForm();
        // Département de la fiche : celui du filtre s'il y en a un, sinon le périmètre
        // de travail de la topbar. Il est pré-coché et porte le profil choisi.
        const dept = deptFiltreId || deptCourantId;
        setDeptFiche(dept);
        if (dept) setFormDepts([dept]);
        setShowUserForm(true);
    };

    const openEditUser = (u) => {
        setFormEmail(u.email);
        setFormPassword('');
        setFormNom(u.nom);
        setFormPrenom(u.prenom);
        setFormNomAffiche(u.nom_affiche || '');
        setFormEnseignant(u.enseignant_id ? String(u.enseignant_id) : '');
        setFormRole(u.role_id);
        setFormDepts(u.departements ? u.departements.map(d => d.id) : []);
        // Le profil est propre à UN département : en périmètre « tous », on prend le
        // premier rattachement du compte, et l'intitulé du champ le nomme pour qu'il
        // n'y ait aucune ambiguïté. Le réglage département par département arrive
        // avec l'écran « Par utilisateur » (0.C.3).
        const dept = deptFiltreId || (u.departements || [])[0]?.id || deptCourantId;
        setDeptFiche(dept);
        const lien = (u.departements || []).find(d => d.id === dept);
        setFormProfil(lien?.profil?.id ? String(lien.profil.id) : '');
        setEditingUser(u);
        setShowUserForm(true);
    };

    const handleSaveUser = async () => {
        if (!formEmail || !formNom || !formPrenom) {
            showErr('Email, nom et prénom obligatoires');
            return;
        }
        // Lot INV-b (Q5) : le mot de passe n'est saisi que pour « Définir le mot de passe moi-même » (super-administrateur)
        if (!editingUser && formAcces === 'mot_de_passe' && !formPassword) {
            showErr('Mot de passe obligatoire pour « Définir le mot de passe moi-même »');
            return;
        }

        const commun = {
            email: formEmail, nom: formNom, prenom: formPrenom,
            nomAffiche: formNomAffiche,
            enseignantId: formEnseignant ? parseInt(formEnseignant, 10) : null,
            departementIds: formDepts,
            departementId: deptFiche,
            profilId: formProfil ? parseInt(formProfil, 10) : null
        };

        try {
            if (editingUser) {
                const data = { ...commun, roleId: formRole };
                if (formPassword && superAdmin) data.password = formPassword;
                await adminAPI.updateUser(editingUser.id, data);
                showMsg('Utilisateur modifié');
            } else {
                const r = await adminAPI.createUser({ ...commun, acces: formAcces, ...(formAcces === 'mot_de_passe' ? { password: formPassword } : {}) });
                const inv = r.data?.invitation;
                // L'invitation part APRÈS la création : un échec ne défait pas le compte, on le dit et on propose la suite.
                if (!inv) showMsg('Utilisateur créé');
                else if (inv.envoyee) showMsg(`Compte créé — invitation envoyée à ${formEmail} (valable jusqu'au ${dateHeureFr(inv.expire_le)})`);
                else if (inv.code === 'BOITE') { const cree = { id: r.data.user.id, email: r.data.user.email, nom_affiche: r.data.user.nom_affiche };
                    resetUserForm(); await loadAll(); return ouvrirLienCopie(cree, "Compte créé. La boîte d'envoi du département n'est pas réglée : l'invitation n'a pas pu partir par courriel. Voici son lien, à lui transmettre vous-même."); }
                else showErr(`Compte créé, mais l'invitation n'est pas partie : ${inv.erreur || 'erreur inconnue'} — menu « … » → Renvoyer l'invitation`);
            }
            resetUserForm();
            await loadAll();
        } catch (err) {
            showErr(err.response?.data?.error || 'Erreur sauvegarde');
        }
    };

    // R2 (lot CO-a) — maquette : retirerDuDepartement. Le compte, ses autres départements et sa
    // signature ne changent pas.
    const handleRetirerDuDepartement = (u, d) => demanderConfirmation({
        titre: `Retirer ${u.nom_affiche || u.email} de ${d.code}`,
        message: `Son profil et ses exceptions dans ${d.code} sont retirés ; son compte, ses autres départements et sa signature ne changent pas.`,
        libelleConfirmer: 'Retirer',
        danger: true,
        action: async () => {
            try {
                await adminAPI.retirerDuDepartement(u.id, d.id);
                showMsg(`${u.nom_affiche || u.email} retiré de ${d.code}`);
                await loadAll();
            } catch (err) {
                showErr(err.response?.data?.error || 'Erreur lors du retrait');
            }
        }
    });

    // ── Lot INV-b : les actes d'accès (menu « … » de la ligne) ──────────────────────────────────────────────────
    const nomDe = (u) => u.nom_affiche || `${u.prenom || ''} ${u.nom || ''}`.trim() || u.email;
    const boiteReglee = (u) => (u.departements || []).some(d => boitesReglees.includes(d.id)) || (u.is_super_admin && boitesReglees.length > 0);
    const deptEnvoi = (u) => { const ids = (u.departements || []).map(d => d.id); return ids.includes(deptFiltreId) ? deptFiltreId : ids.includes(deptCourantId) ? deptCourantId : ids[0]; };
    const handleInviter = (u) => { setMenuAcces(null); demanderConfirmation({
        titre: u.acces?.etat === 'non_invite' ? "Envoyer l'invitation" : "Renvoyer l'invitation",
        message: `${nomDe(u)} recevra à ${u.email} un lien personnel (valable 72 h) pour créer son mot de passe.`
            + (u.acces?.etat === 'invite' ? ' Son lien actuel ne marchera plus.' : ''),
        libelleConfirmer: 'Envoyer',
        action: async () => {
            try {
                const r = await adminAPI.inviterUser(u.id, deptEnvoi(u));
                if (r.data.envoyee) showMsg(`Invitation envoyée à ${u.email} (valable jusqu'au ${dateHeureFr(r.data.expire_le)})`);
                else showErr(`L'invitation n'est pas partie : ${r.data.erreur || 'erreur inconnue'}`);
                await loadAll();
            } catch (err) { showErr(err.response?.data?.error || "Erreur lors de l'envoi de l'invitation"); }
        }
    }); };
    const handleReinitialiser = (u) => { setMenuAcces(null); demanderConfirmation({
        titre: 'Envoyer un lien de réinitialisation',
        message: `${nomDe(u)} recevra à ${u.email} un lien (valable 1 h) pour choisir un nouveau mot de passe. Son mot de passe actuel reste valable tant qu'il n'en a pas choisi un autre ; ensuite, ses sessions ouvertes seront fermées.`,
        libelleConfirmer: 'Envoyer',
        action: async () => {
            try {
                const r = await adminAPI.reinitialiserUser(u.id, deptEnvoi(u));
                if (r.data.envoyee) showMsg(`Lien de réinitialisation envoyé à ${u.email} (valable 1 h)`);
                else showErr(`Le lien n'est pas parti : ${r.data.erreur || 'erreur inconnue'}`);
                await loadAll();
            } catch (err) { showErr(err.response?.data?.error || "Erreur lors de l'envoi du lien"); }
        }
    }); };
    // « Copier le lien » (maquette ④) : un NOUVEAU lien, montré une seule fois (seule son empreinte est gardée).
    const ouvrirLienCopie = async (u, raison = null) => {
        setMenuAcces(null);
        try {
            const r = await adminAPI.lienUser(u.id);
            setLienCopie({ user: u, ...r.data, copie: false, raison });
            await loadAll();
        } catch (err) { showErr(err.response?.data?.error || 'Erreur lors de la création du lien'); }
    };
    const copierLien = async () => {
        try { await navigator.clipboard.writeText(lienCopie.lien); setLienCopie(l => ({ ...l, copie: true })); }
        catch (_) { const z = document.getElementById('lien-a-copier'); if (z) { const s = window.getSelection(); const rg = document.createRange(); rg.selectNodeContents(z); s.removeAllRanges(); s.addRange(rg); } }
    };

    // ── Lot INV-c : « Inviter les N comptes » (maquette ③) ──────────────────────────────────────────────────────
    // Candidats : comptes actifs, sans mot de passe, sur lesquels on a le droit d'agir (R2). Un compte DÉJÀ invité dont le
    // lien vit encore est proposé DÉCOCHÉ : le renvoyer rendrait son lien actuel inutilisable.
    // ⚠️ `users` (le périmètre affiché), PAS `usersFiltres` : déclaré plus bas, il valait « zone morte » ici — page blanche
    // mesurée au banc image le 2026-10-18 alors que tout compilait ; et la recherche en cours ne doit pas changer qui est invité.
    const candidatsInvitation = users.filter(u => u.actif && ['non_invite', 'expire', 'invite'].includes(u.acces?.etat)
        && !(u.actions_compte && !u.actions_compte.possible));
    const aInviter = candidatsInvitation.filter(u => u.acces.etat !== 'invite');
    // une FONCTION : deptFiltreId / deptCourantId sont déclarés plus bas (zone morte au premier rendu, mesuré au banc image)
    const deptGroupe = () => deptFiltreId || deptCourantId;
    const ouvrirInviterN = () => setInviterN({ coches: aInviter.map(u => u.id), enCours: false, essai: null, bilan: null });
    const basculerInvite = (id) => setInviterN(x => ({ ...x, coches: x.coches.includes(id) ? x.coches.filter(i => i !== id) : [...x.coches, id] }));
    const envoyerEssaiInvitation = async () => {
        setInviterN(x => ({ ...x, essai: { enCours: true } }));
        try { const r = await adminAPI.essaiInvitation(deptGroupe()); setInviterN(x => ({ ...x, essai: { ok: r.data.envoye, a: r.data.a, erreur: r.data.erreur } })); }
        catch (err) { setInviterN(x => ({ ...x, essai: { ok: false, erreur: err.response?.data?.error || "L'essai n'est pas parti" } })); }
    };
    const envoyerInvitationsN = async () => {
        setInviterN(x => ({ ...x, enCours: true, erreur: null }));
        try {
            const r = await adminAPI.inviterPlusieurs(inviterN.coches, deptGroupe());
            setInviterN(x => ({ ...x, enCours: false, bilan: r.data }));
            await loadAll();
        } catch (err) { setInviterN(x => ({ ...x, enCours: false, erreur: err.response?.data?.error || "L'envoi n'a pas abouti" })); }
    };
    // ── Lot INV-c : le journal des accès (maquette ⑤) ───────────────────────────────────────────────────────────
    const chargerJournal = async (jours) => {
        setJournal(j => ({ filtre: '', recherche: '', ...(j || {}), jours, chargement: true }));
        try { const r = await adminAPI.journalAcces(jours, deptFiltreId || undefined); setJournal(j => ({ ...j, chargement: false, lignes: r.data.lignes, evenements: r.data.evenements })); }
        catch (err) { setJournal(j => ({ ...j, chargement: false, lignes: [], erreur: err.response?.data?.error || 'Lecture du journal impossible' })); }
    };

    const handleDeleteUser = (u) => demanderConfirmation({
        titre: 'Désactiver un utilisateur',
        message: `Désactiver le compte ${u.email} ? Il ne pourra plus se connecter ; le compte est conservé et peut être réactivé.`,
        libelleConfirmer: 'Désactiver',
        danger: true,
        action: () => desactiverUser(u)
    });

    const desactiverUser = async (u) => {
        try {
            await adminAPI.deleteUser(u.id);
            showMsg('Utilisateur désactivé');
            await loadAll();
        } catch (err) {
            showErr(err.response?.data?.error || 'Erreur suppression');
        }
    };

    const handleReactivateUser = async (u) => {
        try {
            await adminAPI.reactivateUser(u.id);
            showMsg('Utilisateur réactivé');
            await loadAll();
        } catch (err) {
            showErr(err.response?.data?.error || 'Erreur réactivation');
        }
    };

    const toggleDept = (deptId) => {
        setFormDepts(prev =>
            prev.includes(deptId) ? prev.filter(id => id !== deptId) : [...prev, deptId]
        );
    };

    // ============================================
    // DÉPARTEMENTS
    // ============================================

    const resetDeptForm = () => {
        setFormDeptCode(''); setFormDeptNom(''); setFormDeptDesc('');
        setFormDeptAdmins(['']); setCandidatsAdmin([]);
        setFormPremierAdmin(''); setCandidatsPremier([]); setErreurDept('');
        setEditingDept(null); setShowDeptForm(false);
    };

    /** CB3 (lot CO-b) : les comptes de la composante (super-admin : tous), ni soi-même ni super-admin. */
    const chargerCandidatsPremier = async (composanteId) => {
        setFormPremierAdmin('');
        try {
            const r = await adminAPI.getComptesComposante(composanteId);
            setCandidatsPremier(r.data.comptes || []);
        } catch { setCandidatsPremier([]); }
    };

    // Lot CO-a : un département naît DANS une composante (obligatoire côté serveur).
    // Lot CO-b : avec son semis et son premier administrateur.
    const openCreateDept = (composanteId) => {
        resetDeptForm();
        const c = String(composanteId || composantes[0]?.id || '');
        setFormDeptComposante(c);
        if (c) chargerCandidatsPremier(c);
        setShowDeptForm(true);
    };

    // ============================================
    // COMPOSANTES (lot CO-a) — super-administrateur
    // ============================================

    /** Repli d'un bloc de composante — confort mémorisé par navigateur, jamais une donnée. */
    const basculerRepli = (id) => setReplies(prev => {
        const suivant = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
        try { localStorage.setItem('admin.composantes.repliees', JSON.stringify(suivant)); } catch { /* navigation privée */ }
        return suivant;
    });
    const toutReplier = (oui) => {
        const suivant = oui ? composantes.map(c => c.id) : [];
        try { localStorage.setItem('admin.composantes.repliees', JSON.stringify(suivant)); } catch { /* navigation privée */ }
        setReplies(suivant);
    };

    const openCompo = async (c) => {
        setCompoForm({
            id: c?.id ?? null, code: c?.code || '', nom: c?.nom || '',
            admins: (c?.admins || []).length ? c.admins.map(a => String(a.id)) : [''],
            nbDepartements: c?.nb_departements ?? 0
        });
        try {
            // Un administrateur de composante n'a pas à être membre d'un département (CO1) : tout compte actif
            const r = await adminAPI.getUsers();
            setCandidatsCompo((r.data.users || []).filter(u => u.actif && !u.is_super_admin));
        } catch { setCandidatsCompo([]); }
    };

    const saveCompo = async () => {
        const f = compoForm;
        if (!f.code.trim() || !f.nom.trim()) { showErr('Code et nom sont obligatoires'); return; }
        try {
            let id = f.id;
            if (id) await adminAPI.updateComposante(id, { nom: f.nom.trim() });
            else id = (await adminAPI.createComposante({ code: f.code.trim(), nom: f.nom.trim() })).data.composante.id;
            await adminAPI.setComposanteAdmins(id, [...new Set(f.admins.filter(v => v !== '').map(v => parseInt(v, 10)))]);
            setCompoForm(null);
            showMsg(`Composante ${f.nom.trim()} enregistrée`);
            await loadAll();
        } catch (err) {
            showErr(err.response?.data?.error || 'Erreur enregistrement de la composante');
        }
    };

    const supprimerCompo = (c) => demanderConfirmation({
        titre: 'Supprimer une composante',
        message: `Supprimer la composante ${c.nom} ? Elle n'a aucun département ; ses administrateurs perdent leur rôle.`,
        libelleConfirmer: 'Supprimer',
        danger: true,
        action: async () => {
            try {
                await adminAPI.deleteComposante(c.id);
                setCompoForm(null);
                showMsg('Composante supprimée');
                await loadAll();
            } catch (err) {
                showErr(err.response?.data?.error || 'Erreur suppression de la composante');
            }
        }
    });

    // Nommer un administrateur de département (maquette : liste déroulante
    // « Admin(s) de département » de la fiche département). Acte du
    // super-administrateur : c'est lui qui désigne les gouvernances locales (spec §3).
    // Les candidats sont les comptes actifs RATTACHÉS À CE DÉPARTEMENT : on administre
    // un département dont on est membre, et la liste reste cohérente avec l'onglet
    // Utilisateurs de ce même département.
    const chargerCandidatsAdmin = async (deptId) => {
        try {
            const r = await adminAPI.getUsers(deptId);
            setCandidatsAdmin((r.data.users || []).filter(u => u.actif && !u.is_super_admin));
        } catch {
            setCandidatsAdmin([]);
        }
    };

    const openEditDept = (d) => {
        setFormDeptCode(d.code);
        setFormDeptNom(d.nom);
        setFormDeptDesc(d.description || '');
        // Une ligne par administrateur déjà nommé, plus une ligne vide s'il n'y en a aucun
        const dejaNommes = (d.admins || []).map(a => String(a.id));
        setFormDeptAdmins(dejaNommes.length > 0 ? dejaNommes : ['']);
        setCandidatsAdmin([]);
        setEditingDept(d);
        setShowDeptForm(true);
        chargerCandidatsAdmin(d.id);
    };

    /** Une ligne de la pile change de valeur ('' = aucun, la ligne est retirée à l'enregistrement). */
    const changerDeptAdmin = (index, valeur) => {
        setFormDeptAdmins(prev => prev.map((v, i) => (i === index ? valeur : v)));
    };

    const ajouterLigneDeptAdmin = () => setFormDeptAdmins(prev => [...prev, '']);

    const retirerLigneDeptAdmin = (index) => {
        setFormDeptAdmins(prev => (prev.length <= 1 ? [''] : prev.filter((v, i) => i !== index)));
    };

    const handleSaveDept = async () => {
        if (!formDeptNom) {
            // Vu au banc (lot CO-b) : en création, le message en haut de page restait caché derrière la fiche
            if (!editingDept) setErreurDept('Code, nom et premier administrateur sont obligatoires.');
            else showErr('Nom obligatoire');
            return;
        }

        try {
            let deptId = editingDept?.id ?? null;
            if (editingDept) {
                await departementsAPI.update(editingDept.id, {
                    nom: formDeptNom, description: formDeptDesc
                });
                showMsg('Département modifié');
            } else {
                // Maquette : dErr — l'erreur s'affiche DANS la fiche, à côté de ce qui manque
                if (!formDeptCode || !formPremierAdmin) {
                    setErreurDept('Code, nom et premier administrateur sont obligatoires.');
                    return;
                }
                const res = await departementsAPI.create({
                    code: formDeptCode.toUpperCase(), nom: formDeptNom, description: formDeptDesc,
                    composanteId: formDeptComposante ? parseInt(formDeptComposante, 10) : null,
                    premierAdminId: parseInt(formPremierAdmin, 10)
                });
                // Lot CO-b : le récapitulatif de ce qui a été installé (modale de réussite, jamais alert)
                setCreationReussie({
                    departement: res.data.departement,
                    semis: res.data.semis || {},
                    composante: composantes.find(c => String(c.id) === formDeptComposante)?.nom || '',
                    admin: candidatsPremier.find(u => String(u.id) === formPremierAdmin)?.nom_affiche || ''
                });
                resetDeptForm();
                await loadAll();
                return;
            }
            // L'administrateur est enregistré à part : il vit dans
            // `user_departements.role_dept`, pas dans la table `departements`.
            // On n'envoie rien à la création : le département n'a encore aucun membre.
            // R4 (lot CO-a) : l'administrateur de la composante ou le super-administrateur nomme.
            if (peutNommer && deptId && editingDept) {
                const ids = [...new Set(
                    formDeptAdmins.filter(v => v !== '').map(v => parseInt(v, 10))
                )];
                await adminAPI.setDeptAdmins(deptId, ids);
            }
            resetDeptForm();
            await loadAll();
        } catch (err) {
            if (!editingDept) setErreurDept(err.response?.data?.error || 'Erreur lors de la création');
            else showErr(err.response?.data?.error || 'Erreur sauvegarde');
        }
    };

    // ---- Lot CO-b (CB0) : supprimer DÉFINITIVEMENT un département vide (super-admin) ----
    const ouvrirSuppressionDept = async (d) => {
        setSuppressionDept({ dept: d, inventaire: null, saisie: '' });
        try {
            const r = await departementsAPI.inventaireSuppression(d.id);
            setSuppressionDept(s => (s && s.dept.id === d.id ? { ...s, inventaire: r.data } : s));
        } catch (err) {
            setSuppressionDept(null);
            showErr(err.response?.data?.error || "Erreur lors de l'inventaire du département");
        }
    };

    const confirmerSuppressionDept = async () => {
        const { dept, saisie } = suppressionDept;
        try {
            const r = await departementsAPI.supprimerDefinitivement(dept.id, saisie.trim());
            setSuppressionDept(null);
            showMsg(r.data.message);
            await loadAll();
        } catch (err) {
            showErr(err.response?.data?.error || 'Erreur lors de la suppression');
        }
    };

    const handleLogoUpload = async (deptId, file) => {
        if (!file) return;
        setLogoUploading(prev => ({ ...prev, [deptId]: true }));
        try {
            const formData = new FormData();
            formData.append('logo', file);
            const res = await departementsAPI.uploadLogo(deptId, formData);
            setDepartements(prev => prev.map(d =>
                d.id === deptId ? { ...d, logo_url: res.data.logo_url } : d
            ));
            showMsg('Logo mis à jour');
        } catch (err) {
            showErr(err.response?.data?.error || 'Erreur upload logo');
        } finally {
            setLogoUploading(prev => ({ ...prev, [deptId]: false }));
        }
    };

    const handleLogoDelete = async (deptId) => {
        try {
            await departementsAPI.deleteLogo(deptId);
            setDepartements(prev => prev.map(d =>
                d.id === deptId ? { ...d, logo_url: null } : d
            ));
            showMsg('Logo supprimé');
        } catch (err) {
            showErr(err.response?.data?.error || 'Erreur suppression logo');
        }
    };

    const handleToggleDept = (d) => demanderConfirmation({
        titre: (d.actif ? 'Désactiver' : 'Réactiver') + ' un département',
        message: `${d.actif ? 'Désactiver' : 'Réactiver'} le département ${d.nom} ?`
            + (d.actif ? " Il n'apparaîtra plus dans les sélecteurs ; ses données sont conservées." : ''),
        libelleConfirmer: d.actif ? 'Désactiver' : 'Réactiver',
        danger: d.actif,
        action: () => basculerDept(d)
    });

    const basculerDept = async (d) => {
        try {
            await departementsAPI.update(d.id, { actif: !d.actif });
            showMsg('Département ' + (d.actif ? 'désactivé' : 'réactivé'));
            await loadAll();
        } catch (err) {
            showErr(err.response?.data?.error || 'Erreur');
        }
    };

    // ============================================
    // CONFIGURATION GLOBALE (0.C.8)
    // ============================================

    const chargerOccupation = async () => {
        try {
            setOccupation((await adminAPI.getOccupation()).data);
        } catch (err) {
            // Un défaut de lecture de l'occupation ne doit pas empêcher l'onglet
            // Configuration de fonctionner : la carte affiche simplement moins.
            console.error('Erreur lecture occupation:', err);
            setOccupation(null);
        }
    };

    const chargerEtatsEntete = async () => {
        try {
            const r = await adminAPI.getEtatsEntete();
            setEtatsEntete(Object.fromEntries((r.data.etats || []).map(e => [e.departement_id, e])));
        } catch (err) {
            console.error('Erreur lecture des en-têtes de documents:', err);
        }
    };

    const chargerEtatsCourriel = async () => {
        try {
            const r = await adminAPI.getEtatsCourriel();
            setEtatsCourriel(Object.fromEntries((r.data.etats || []).map(e => [e.departement_id, e])));
            setEnvCourriel(r.data.environnement || null);
        } catch (err) {
            // Une carte sans état d'envoi reste utilisable : on n'empêche pas l'onglet de vivre.
            console.error('Erreur lecture des états d\x27envoi:', err);
        }
    };

    const chargerSettings = async () => {
        try {
            const res = await adminAPI.getSettings();
            const s = res.data.settings;
            setSettings(s);
            setEnvCourriel(res.data.courriel || null);
            setFormNomAppli(s.nom_application || '');
            setFormUrlPublique(s.app_url_publique || '');
            setFormTailleDoc(s.document_taille_max_mo || '10');
            setFormDureeSession(s.duree_session_heures || '24');
            setFormLongueurMdp(s.longueur_min_mot_de_passe || '10');
        } catch (err) {
            showErr(err.response?.data?.error || 'Erreur chargement de la configuration');
        }
    };

    const handleSaveSettings = async () => {
        try {
            const res = await adminAPI.saveSettings({
                nom_application: formNomAppli,
                app_url_publique: formUrlPublique,
                document_taille_max_mo: formTailleDoc,
                duree_session_heures: formDureeSession,
                longueur_min_mot_de_passe: formLongueurMdp
            });
            setSettings(res.data.settings);
            showMsg(res.data.message);
        } catch (err) {
            showErr(err.response?.data?.error || 'Erreur enregistrement');
        }
    };

    const handleLogoUniversite = async (fichier) => {
        if (!fichier) return;
        setLogoUnivEnCours(true);
        try {
            const formData = new FormData();
            formData.append('logo', fichier);
            const res = await adminAPI.uploadLogoUniversite(formData);
            setSettings(prev => ({ ...prev, logo_universite: res.data.logo_universite }));
            showMsg(res.data.message);
        } catch (err) {
            showErr(err.response?.data?.error || 'Erreur téléversement du logo');
        } finally {
            setLogoUnivEnCours(false);
        }
    };

    const handleRetirerLogoUniversite = () => demanderConfirmation({
        titre: "Retirer le logo de l'université",
        message: "Le logo disparaîtra de la page de connexion et de l'en-tête de l'application."
            + " Les documents générés ne sont pas concernés : ils utilisent le logo du DÉPARTEMENT.",
        libelleConfirmer: 'Retirer',
        danger: true,
        action: async () => {
            try {
                const res = await adminAPI.deleteLogoUniversite();
                setSettings(prev => ({ ...prev, logo_universite: '' }));
                showMsg(res.data.message);
            } catch (err) {
                showErr(err.response?.data?.error || 'Erreur lors du retrait');
            }
        }
    });

    // ============================================
    // SIGNATURES ET DÉLÉGATIONS (0.C.5)
    // ============================================

    const chargerSignatures = async (deptId) => {
        try {
            const [sigRes, delRes] = await Promise.all([
                adminAPI.getSignatures(deptId),
                adminAPI.getDelegations(deptId)
            ]);
            setSignatures(sigRes.data.personnes || []);
            setChiffrementOk(sigRes.data.chiffrement_disponible !== false);
            setDelegations(delRes.data.delegations || []);
        } catch (err) {
            showErr(err.response?.data?.error || 'Erreur chargement des signatures');
        }
    };

    const ouvrirDepot = (personne) => {
        setDepotPour(personne);
        setFichierSignature(null); setFichierTampon(null); setMotDePasseReauth('');
    };

    const fermerDepot = () => {
        setDepotPour(null);
        setFichierSignature(null); setFichierTampon(null); setMotDePasseReauth('');
    };

    /**
     * Dépôt : les images partent chiffrées côté serveur, et l'action exige le mot de
     * passe de celui qui la fait (ré-authentification, spec §5) — on engage la
     * signature de quelqu'un, ça ne se fait pas d'un clic distrait.
     */
    const handleDeposerSignature = async () => {
        if (!fichierSignature && !fichierTampon) { showErr('Choisissez au moins une image'); return; }
        if (!motDePasseReauth) { showErr('Confirmez avec votre mot de passe'); return; }

        setDepotEnCours(true);
        try {
            const formData = new FormData();
            if (fichierSignature) formData.append('signature', fichierSignature);
            if (fichierTampon) formData.append('tampon', fichierTampon);
            formData.append('motDePasse', motDePasseReauth);

            const res = await adminAPI.deposerSignature(depotPour.id, formData);
            showMsg(res.data.message);
            fermerDepot();
            await chargerSignatures(perimetreSigEffectif);
        } catch (err) {
            showErr(err.response?.data?.error || 'Erreur lors du dépôt');
        } finally {
            setDepotEnCours(false);
        }
    };

    const handleRetirerSignature = (personne, type) => demanderConfirmation({
        titre: type === 'tampon' ? 'Retirer le tampon' : 'Retirer la signature',
        message: `Retirer ${type === 'tampon' ? 'le tampon' : 'la signature'} de ${personne.nom_affiche} ?`
            + ' Les documents déjà générés ne changent pas ; les prochains ne pourront plus être signés'
            + " tant qu'une nouvelle image n'aura pas été déposée.",
        libelleConfirmer: 'Retirer',
        danger: true,
        action: async () => {
            try {
                const res = await adminAPI.retirerSignature(personne.id, type);
                showMsg(res.data.message);
                await chargerSignatures(perimetreSigEffectif);
            } catch (err) {
                showErr(err.response?.data?.error || 'Erreur lors du retrait');
            }
        }
    });

    const ouvrirDelegation = () => {
        setFormDelegTitulaire(''); setFormDelegDelegue('');
        setFormDelegDu(''); setFormDelegAu(''); setFormDelegMotif('');
        setShowDelegForm(true);
    };

    const handleCreerDelegation = async () => {
        if (!formDelegTitulaire || !formDelegDelegue) { showErr('Titulaire et délégué obligatoires'); return; }
        if (!formDelegDu || !formDelegAu) { showErr('Une délégation est bornée dans le temps : indiquez les deux dates'); return; }
        try {
            const res = await adminAPI.createDelegation({
                titulaireUserId: parseInt(formDelegTitulaire, 10),
                delegueUserId: parseInt(formDelegDelegue, 10),
                // S2 : le département du périmètre choisi (jamais une composante entière : S4 reporté)
                departementId: String(perimetreSig).startsWith('d') ? parseInt(perimetreSig.slice(1), 10) : deptCourantId,
                dateDebut: formDelegDu,
                dateFin: formDelegAu,
                motif: formDelegMotif || null
            });
            showMsg(res.data.message);
            setShowDelegForm(false);
            await chargerSignatures(perimetreSigEffectif);
        } catch (err) {
            showErr(err.response?.data?.error || 'Erreur création de la délégation');
        }
    };

    const handleRevoquerDelegation = (d) => demanderConfirmation({
        titre: 'Révoquer une délégation',
        message: `Révoquer la délégation de ${d.titulaire_nom} à ${d.delegue_nom} ?`
            + ' Le délégué perdra immédiatement le droit de signer pour le titulaire.'
            + ' La délégation reste visible, révoquée : elle est journalisée.',
        libelleConfirmer: 'Révoquer',
        danger: true,
        action: async () => {
            try {
                const res = await adminAPI.revoquerDelegation(d.id);
                showMsg(res.data.message);
                await chargerSignatures(perimetreSigEffectif);
            } catch (err) {
                showErr(err.response?.data?.error || 'Erreur révocation');
            }
        }
    });

    // ============================================
    // CLÉS D'API ET CONNEXIONS (0.C.6 / 0.C.7)
    // ============================================

    const chargerApi = async () => {
        try {
            const [clesRes, journalRes, ssoRes] = await Promise.all([
                adminAPI.getApiKeys(),
                adminAPI.getApiJournal(100),
                adminAPI.getSso()
            ]);
            setApiKeys(clesRes.data.cles || []);
            setApiJournal(journalRes.data.appels || []);
            setSsoConfig(ssoRes.data.sso || null);
        } catch (err) {
            showErr(err.response?.data?.error || "Erreur chargement des clés d'API");
        }
    };

    const ouvrirCreationCle = () => {
        setFormApiNom(''); setFormApiScopes([]); setFormApiPerso(false); setFormApiExpiration('');
        setShowApiForm(true);
    };

    const toggleScopeApi = (moduleKey) => {
        setFormApiScopes(prev =>
            prev.includes(moduleKey) ? prev.filter(k => k !== moduleKey) : [...prev, moduleKey]
        );
    };

    const handleCreerCle = async () => {
        if (!formApiNom.trim()) { showErr('Nom de la clé obligatoire'); return; }
        if (formApiScopes.length === 0) { showErr('Choisissez au moins un module à exposer en lecture'); return; }
        try {
            const res = await adminAPI.createApiKey({
                nom: formApiNom.trim(),
                scopes: formApiScopes,
                autoriseDonneesPerso: formApiPerso,
                dateExpiration: formApiExpiration || null
            });
            setShowApiForm(false);
            setCleCopiee(false);
            // Seule occasion de la lire : on l'affiche et on prévient que c'est la dernière
            setCleRevelee(res.data.cleComplete);
            await chargerApi();
        } catch (err) {
            showErr(err.response?.data?.error || 'Erreur création de la clé');
        }
    };

    const copierCle = async () => {
        try {
            await navigator.clipboard.writeText(cleRevelee);
            setCleCopiee(true);
        } catch {
            setCleCopiee(false);
        }
    };

    const handleRevoquerCle = (cle) => demanderConfirmation({
        titre: 'Révoquer une clé d\'API',
        message: `Révoquer « ${cle.nom} » ? Les applications qui l'utilisent perdront l'accès`
            + ' immédiatement. La clé reste dans la liste, barrée, pour que le journal d\'appels'
            + ' garde un sens. Action irréversible : une clé ne se réactive pas.',
        libelleConfirmer: 'Révoquer',
        danger: true,
        action: async () => {
            try {
                const res = await adminAPI.revokeApiKey(cle.id);
                showMsg(res.data.message);
                await chargerApi();
            } catch (err) {
                showErr(err.response?.data?.error || 'Erreur révocation');
            }
        }
    });

    // ============================================
    // PROFILS & DROITS (0.C.3)
    // ============================================

    /** Utilisateurs du département affiché — liste de gauche du sous-onglet « Par utilisateur ». */
    const chargerUsersDuDeptDroits = async (deptId) => {
        if (!deptId) { setUsersDuDeptDroits([]); return; }
        try {
            const r = await adminAPI.getUsers(deptId);
            const liste = (r.data.users || []).filter(u => u.actif);
            setUsersDuDeptDroits(liste);
            return liste;
        } catch {
            setUsersDuDeptDroits([]);
            return [];
        }
    };

    /** Droits résolus d'un utilisateur, avec la source de chaque niveau. */
    const chargerFicheDroits = async (userId, deptId) => {
        if (!userId || !deptId) { setFicheDroits(null); return; }
        try {
            const r = await adminAPI.getUserDroits(userId, deptId);
            setFicheDroits(r.data);
        } catch (err) {
            setFicheDroits(null);
            showErr(err.response?.data?.error || 'Erreur chargement des droits');
        }
    };

    const ouvrirFiche = async (userId) => {
        setFicheUserId(userId);
        await chargerFicheDroits(userId, deptCourantId);
    };

    /**
     * Bouton « Droits » du tableau des utilisateurs : ouvre la fiche de ce compte.
     * La matrice et la fiche travaillent sur UN département : si la ligne cliquée
     * appartient à un autre département, on y bascule le sélecteur.
     */
    const allerAuxDroits = (u) => {
        const dept = deptFiltreId || (u.departements || [])[0]?.id || deptCourantId;
        setFicheUserId(u.id);
        setSousOngletDroits('fiche');
        setActiveTab('droits');
        if (dept && dept !== deptCourantId) setDeptCourantId(dept);
        else { chargerUsersDuDeptDroits(dept); chargerFicheDroits(u.id, dept); }
    };

    /** Clic sur une cellule de la matrice : Modifier -> Consulter -> Masqué (maquette). */
    const cyclerCellule = async (profil, moduleKey) => {
        const actuel = profil.permissions?.[moduleKey] || 'masque';
        const suivant = NIVEAUX[(NIVEAUX.indexOf(actuel) + 1) % NIVEAUX.length];

        // Retour visuel immédiat : la matrice fait 18 colonnes, attendre le serveur
        // à chaque clic serait pénible. En cas d'échec on recharge la vérité.
        setProfils(prev => prev.map(p => p.id === profil.id
            ? { ...p, permissions: { ...(p.permissions || {}), [moduleKey]: suivant } }
            : p));

        try {
            await adminAPI.setProfilPermissions(profil.id, { [moduleKey]: suivant });
        } catch (err) {
            showErr(err.response?.data?.error || 'Erreur enregistrement du droit');
            await loadAll();
        }
    };

    const ouvrirCreationProfil = (depuisGlobal) => {
        setProfilDepuisGlobal(depuisGlobal);
        setFormProfilNom('');
        // Base de duplication : profils globaux, ou tous ceux applicables ici
        const candidats = profils.filter(p => (depuisGlobal
            ? p.scope === 'global'
            : p.scope === 'global' || p.departement_id === deptCourantId));
        setFormProfilBase(candidats[0]?.id ? String(candidats[0].id) : '');
        setShowProfilForm(true);
    };

    const fermerCreationProfil = () => {
        setShowProfilForm(false);
        setFormProfilNom(''); setFormProfilBase('');
    };

    const handleSaveProfil = async () => {
        if (!formProfilNom.trim()) { showErr('Nom du profil obligatoire'); return; }
        if (!deptCourantId) { showErr('Aucun département sélectionné'); return; }
        try {
            await adminAPI.createProfil({
                nom: formProfilNom.trim(),
                departementId: deptCourantId,
                baseProfilId: formProfilBase ? parseInt(formProfilBase, 10) : null
            });
            showMsg(`Profil « ${formProfilNom.trim()} » créé pour ${deptCourant?.code || ''}`);
            fermerCreationProfil();
            await loadAll();
        } catch (err) {
            showErr(err.response?.data?.error || 'Erreur création du profil');
        }
    };

    const handleRetirerProfil = (profil) => demanderConfirmation({
        titre: 'Retirer un profil',
        message: `Retirer le profil « ${profil.nom} » du département ${deptCourant?.code || ''} ?`
            + ' Il ne sera plus proposé ; les droits déjà attribués aux comptes ne changent pas.',
        libelleConfirmer: 'Retirer',
        danger: true,
        action: async () => {
            try {
                await adminAPI.deleteProfil(profil.id);
                showMsg('Profil retiré');
                await loadAll();
            } catch (err) {
                showErr(err.response?.data?.error || 'Erreur retrait du profil');
            }
        }
    });

    /** Changer le profil d'un compte — réinitialise ses exceptions (spec §4.4). */
    const changerProfilFiche = (profilId) => {
        const nbExceptions = ficheDroits
            ? Object.values(ficheDroits.droits || {}).filter(d => d.source === 'exception').length
            : 0;

        const appliquer = async () => {
            try {
                const r = await adminAPI.setUserProfil(ficheUserId, deptCourantId, profilId || null);
                showMsg(r.data.exceptions_supprimees > 0
                    ? `Profil changé — ${r.data.exceptions_supprimees} exception(s) réinitialisée(s)`
                    : 'Profil changé');
                await chargerFicheDroits(ficheUserId, deptCourantId);
                await chargerUsersDuDeptDroits(deptCourantId);
                await loadAll();
            } catch (err) {
                showErr(err.response?.data?.error || 'Erreur changement de profil');
            }
        };

        // On ne prévient que s'il y a réellement quelque chose à perdre
        if (nbExceptions > 0) {
            demanderConfirmation({
                titre: 'Changer le profil',
                message: `Ce compte a ${nbExceptions} exception(s) individuelle(s) sur ce département.`
                    + ' Changer de profil les réinitialise : les droits repartiront du nouveau modèle.',
                libelleConfirmer: 'Changer le profil',
                danger: true,
                action: appliquer
            });
        } else {
            appliquer();
        }
    };

    /**
     * Réglage d'un module dans la fiche. Comme dans la maquette : choisir le niveau
     * du profil RETIRE l'exception (au lieu d'en créer une identique au profil).
     */
    const reglerException = async (moduleKey, niveau) => {
        const ligne = ficheDroits?.droits?.[moduleKey];
        if (!ligne) return;
        const retour = ligne.niveau_profil === niveau;
        try {
            await adminAPI.setUserOverride(ficheUserId, deptCourantId, moduleKey, retour ? null : niveau);
            await chargerFicheDroits(ficheUserId, deptCourantId);
            await loadAll();
        } catch (err) {
            showErr(err.response?.data?.error || "Erreur enregistrement de l'exception");
        }
    };

    /**
     * Navigation vers un module depuis l'Administration.
     *
     * Sans cette interception, un super-administrateur restait prisonnier du panneau :
     * il n'a pas de département chargé, donc la route du module ne trouvait aucune
     * configuration, renvoyait à l'accueil, qui le renvoyait à /admin — le clic
     * semblait ne rien faire. On charge donc le département affiché, puis on ouvre le
     * module demandé.
     */
    const naviguerVersModule = (module) => {
        if (module === 'admin') { if (onNavigate) onNavigate(module); return; }
        if (deptCourant && onViewDepartement) { onViewDepartement(deptCourant, module); return; }
        if (onNavigate) onNavigate(module);
    };

    const handleDragStart = (key) => { dragTab.current = key; };
    const handleDragOver = (e, key) => { e.preventDefault(); setDragOverTab(key); };
    const handleDragLeave = () => setDragOverTab(null);
    const handleDrop = async (targetKey) => {
        setDragOverTab(null);
        if (!dragTab.current || dragTab.current === targetKey) return;
        const from = tabOrder.indexOf(dragTab.current);
        const to = tabOrder.indexOf(targetKey);
        const newOrder = [...tabOrder];
        newOrder.splice(from, 1);
        newOrder.splice(to, 0, dragTab.current);
        dragTab.current = null;
        setTabOrder(newOrder);
        const newPrefs = { ...(user.preferences || {}), adminTabOrder: newOrder };
        try {
            await authAPI.savePreferences(newPrefs);
            localStorage.setItem('user', JSON.stringify({ ...user, preferences: newPrefs }));
        } catch (e) { console.error('Erreur sauvegarde préférences:', e); }
    };

    if (loading) return (<div className="loading"><div className="spinner"></div><p>Chargement...</p></div>);

    // Périmètre : le super-administrateur bascule entre tous les départements
    // actifs ; l'administrateur de département est verrouillé sur ceux qu'il
    // administre (et non sur tous ceux dont il est membre).
    const deptsVisibles = departementsDuPerimetre(user, departements);
    // R2 (lot CO-a) : la fiche d'un compte qu'on ne couvre pas — champs du COMPTE grisés, avec leur raison
    const compteVerrouille = !!(editingUser && editingUser.actions_compte && !editingUser.actions_compte.possible);
    const verrouCompte = compteVerrouille ? { disabled: true, title: editingUser.actions_compte.raison } : {};
    // « Retirer de mon département » : le département filtré, sinon celui de la barre du haut
    const deptRetrait = filtreDept ? parseInt(filtreDept, 10) : deptCourantId;
    // Onglet « depts » : un bloc par composante — toutes (même vides) pour le super-admin, la sienne pour
    // l'administrateur de composante ; celle de son département pour l'administrateur de département.
    const groupesDepts = (superAdmin || adminComposante)
        ? composantes.map(c => ({ ...c, departements: departements.filter(d => d.composante_id === c.id) }))
        : parComposante(departements);
    const deptCourant = deptsVisibles.find(d => d.id === deptCourantId) || deptsVisibles[0] || null;

    // Onglet Utilisateurs — le périmètre affiché est celui du FILTRE Département
    // (« Tous les départements » par défaut), pas celui du sélecteur de la topbar :
    // on veut voir tous les comptes et n'en isoler un département qu'à la demande.
    // Le sélecteur reste le périmètre de travail des autres onglets (matrice des
    // droits, signatures) et la cible du bouton « Aller dans le département ».
    const deptFiltreId = filtreDept ? parseInt(filtreDept, 10) : null;
    const liensAffiches = (u) => (u.departements || [])
        .filter(d => deptFiltreId === null || d.id === deptFiltreId);
    const lienDuDept = (u) => (u.departements || []).find(d => d.id === deptFiltreId);
    const estAdminDuDept = (u) => u.is_super_admin === true
        || liensAffiches(u).some(d => d.role_dept === 'admin');
    // « Sans profil » = aucun profil sur le périmètre affiché : ces comptes ne voient
    // rien de l'application, c'est l'information à traiter.
    const sansProfil = (u) => !u.is_super_admin && !liensAffiches(u).some(d => d.profil);

    const usersFiltres = users.filter(u => {
        const q = rechercheUser.trim().toLowerCase();
        if (q && !`${u.nom_affiche || ''} ${u.prenom} ${u.nom} ${u.email}`.toLowerCase().includes(q)) return false;
        if (filtreProfil && !liensAffiches(u).some(d => String(d.profil?.id || '') === filtreProfil)) return false;
        if (filtreStatut !== '' && String(u.actif ? 1 : 0) !== filtreStatut) return false;
        if (filtreAcces && u.acces?.etat !== filtreAcces) return false;
        return true;
    });

    // Utilisateur affiché dans la fiche « Par utilisateur »
    const ficheUtilisateur = usersDuDeptDroits.find(u => u.id === ficheUserId) || null;

    // Profils applicables à un département : les globaux hérités + les siens.
    // `profils` contient tout le périmètre, chaque écran prend ce qui le concerne.
    const profilsPour = (deptId) => profils.filter(
        p => p.scope === 'global' || p.departement_id === deptId
    );
    const profilsMatrice = profilsPour(deptCourantId);
    // Profils propres à d'AUTRES départements : ils existent mais ne s'appliquent pas
    // ici. On le dit, sinon on croit qu'ils ont disparu.
    const profilsAilleurs = profils.filter(
        p => p.scope === 'dept' && p.departement_id !== deptCourantId
    );

    const ongletsVisibles = tabOrder.filter(k => superAdmin || !SUPER_ONLY.includes(k));
    const ongletActif = ongletsVisibles.includes(activeTab) ? activeTab : (ongletsVisibles[0] || 'users');
    const aVenir = ETAPES_A_VENIR[ongletActif];

    return (
        <AppLayout
            activeKey="admin"
            user={user}
            config={deptCourant ? { departement: deptCourant } : null}
            onNavigate={naviguerVersModule}
            onLogout={onLogout}
        >
        <div className="admin-page" onClick={() => depMenuOuvert && setDepMenuOuvert(false)}>

            {/* ---- TOPBAR (maquette : pg-topbar) ---- */}
            <div className="pg-topbar">
                <i className="fa-solid fa-gear pg-topbar-icon" />
                <div>
                    <div className="pg-topbar-title">Administration</div>
                    <div className="pg-topbar-sub">Gouvernance, droits d'accès et intégrations</div>
                </div>

                {/* Sélecteur de département — scope du panneau */}
                {deptCourant && (
                    <div className="dep-select">
                        <button
                            className={'dep-btn' + (deptsVisibles.length <= 1 ? ' locked' : '')}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (deptsVisibles.length > 1) setDepMenuOuvert(o => !o);
                            }}
                            title={deptsVisibles.length > 1 ? 'Changer de département' : 'Périmètre verrouillé sur votre département'}
                        >
                            <i className="fa-solid fa-building-columns" />
                            <span className="db-code">{deptCourant.code}</span>
                            <span>{deptCourant.nom}</span>
                            {deptsVisibles.length > 1 && <i className="fa-solid fa-chevron-down" style={{ fontSize: 11 }} />}
                        </button>
                        {depMenuOuvert && (
                            <div className="dep-menu" onClick={e => e.stopPropagation()}>
                                {/* Lot CO-a : les départements regroupés sous leur composante */}
                                {parComposante(deptsVisibles).map(g => (
                                    <React.Fragment key={g.id}>
                                        <div className="dm-groupe">{g.nom}</div>
                                        {g.departements.map(d => (
                                            <div
                                                key={d.id}
                                                className={'dm-item' + (d.id === deptCourant.id ? ' active' : '')}
                                                onClick={() => { setDeptCourantId(d.id); setDepMenuOuvert(false); }}
                                            >
                                                <span className="dm-code">{d.code}</span>
                                                <span className="dm-name">{d.nom}</span>
                                            </div>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Sortie du panneau vers les modules du département affiché.
                    Absent de la maquette, qui ne traite pas la navigation entre modules :
                    sans ce bouton, un super-administrateur n'a aucun moyen évident de
                    quitter l'Administration pour travailler dans un département. */}
                {deptCourant && (
                    <button
                        className="dep-btn dep-go"
                        onClick={() => onViewDepartement && onViewDepartement(deptCourant)}
                        title={`Charger ${deptCourant.nom} et ouvrir son accueil`}
                    >
                        <i className="fa-solid fa-arrow-right-to-bracket" />
                        Aller dans le département
                    </button>
                )}

                {/* Onglets — réordonnables (ordre persisté dans les préférences) */}
                <div className="pg-topbar-tabs">
                    {ongletsVisibles.map(key => {
                        const trouve = TABS.find(t => t.key === key);
                        if (!trouve) return null;
                        const onglet = ongletAffiche(trouve, user);
                        return (
                            <button
                                key={key}
                                draggable
                                className={
                                    'pg-topbar-tab'
                                    + (ongletActif === key ? ' active' : '')
                                    + (dragOverTab === key ? ' drag-over' : '')
                                }
                                onClick={() => setActiveTab(key)}
                                onDragStart={() => handleDragStart(key)}
                                onDragOver={(e) => handleDragOver(e, key)}
                                onDragLeave={handleDragLeave}
                                onDrop={() => handleDrop(key)}
                                onDragEnd={() => setDragOverTab(null)}
                                title={SUPER_ONLY.includes(key) ? 'Réservé au super-administrateur' : undefined}
                            >
                                <i className={`fa-solid ${onglet.icon}`} />
                                {onglet.label}
                                {key === 'aide' && aideNouveaux > 0 && <span className="aide-admin-cpt" title="Signalements nouveaux">{aideNouveaux}</span>}
                                {SUPER_ONLY.includes(key) && <i className="fa-solid fa-lock lock" />}
                            </button>
                        );
                    })}
                </div>

                {/* Maquette : la topbar ne porte que le nom de l'utilisateur.
                    La déconnexion vit en bas de la sidebar, inutile de la doubler ici. */}
                <div className="pg-topbar-right">
                    <span className="pg-user">
                        <i className="fa-solid fa-circle-user" />
                        {user?.nom_affiche || `${user.prenom} ${user.nom}`}
                    </span>
                </div>
            </div>

            {/* ---- CONTENU ---- */}
            <div className="a-content">

                    {message && <div className="admin-alert admin-alert-success">{message}</div>}
                    {avertissement && (
                        <div className="admin-alert admin-alert-warn">
                            <i className="fa-solid fa-triangle-exclamation" /> {avertissement}
                        </div>
                    )}
                    {error && <div className="admin-alert admin-alert-error">{error}</div>}

                    {/* Périmètre vide : ne pas laisser un écran muet (département désactivé
                        entre-temps, ou application encore sans département) */}
                    {!deptCourant && (
                        <div className="note warn">
                            <i className="fa-solid fa-triangle-exclamation" />
                            <div>
                                {superAdmin
                                    ? <>Aucun département actif. Créez-en un dans l'onglet <b>Départements</b>, ou réactivez un département existant.</>
                                    : <>Aucun département à administrer : celui dont vous êtes administrateur a été désactivé, ou votre rôle a été retiré. Contactez le super-administrateur.</>}
                            </div>
                        </div>
                    )}

                    {/* Onglets encore à construire — la coquille est complète, le contenu arrive */}
                    {aVenir && (
                        <div className="a-placeholder">
                            <i className="fa-solid fa-screwdriver-wrench" />
                            <b>{aVenir.titre}</b>
                            {aVenir.detail}
                            <div style={{ marginTop: 10, fontSize: 12 }}>
                                Livré à l'étape <b>{aVenir.etape}</b> (voir <code>doc/SUIVI_DEV.md</code>).
                            </div>
                        </div>
                    )}

            {/* ============================================ */}
            {/* ONGLET UTILISATEURS */}
            {/* ============================================ */}
            {ongletActif === 'users' && (
                <div className="a-view">
                    {/* Périmètre affiché — maquette : scope-hint. Il suit le filtre
                        Département, pas le sélecteur de la topbar. */}
                    <div className="scope-hint">
                        <i className="fa-solid fa-building-columns" />
                        {deptFiltreId
                            ? <>Département : <b>{departements.find(d => d.id === deptFiltreId)?.nom || '—'}</b></>
                            : deptsVisibles.length === 1
                                ? <>Département : <b>{deptsVisibles[0].nom}</b></>
                                : <>Périmètre : <b>{superAdmin ? 'tous les départements' : 'les départements que vous administrez'}</b></>}
                        {' · '}
                        <span className={'tag ' + (superAdmin ? 'tag-info' : 'tag-warn')}>
                            {superAdmin ? 'Super-administrateur' : 'Admin de département'}
                        </span>
                    </div>

                    {/* Indicateurs — maquette : kpi-row. Ils portent sur le périmètre
                        affiché (filtre Département), pas sur le résultat de la recherche. */}
                    <div className="kpi-row">
                        <div className="kpi accent">
                            <div className="k-l">Utilisateurs actifs</div>
                            <div className="k-v">{users.filter(u => u.actif).length}</div>
                            <div className="k-s">{users.length} au total</div>
                        </div>
                        <div className="kpi">
                            <div className="k-l">Admins de département</div>
                            <div className="k-v">{users.filter(u => estAdminDuDept(u)).length}</div>
                            <div className="k-s">Gouvernance locale</div>
                        </div>
                        {/* Lot INV-b : les comptes qui n'ont pas encore de mot de passe (maquette ①) */}
                        <div className="kpi">
                            <div className="k-l">Sans mot de passe</div>
                            <div className="k-v">{users.filter(u => u.actif && ['invite', 'expire', 'non_invite'].includes(u.acces?.etat)).length}</div>
                            <div className="k-s">{users.filter(u => u.actif && u.acces?.etat === 'invite').length} invité(s) · {users.filter(u => u.actif && u.acces?.etat === 'expire').length} lien expiré</div>
                        </div>
                        <div className="kpi">
                            <div className="k-l">Signatures déposées</div>
                            <div className="k-v">{users.filter(u => u.a_signature).length}</div>
                            <div className="k-s">Chiffrées, Niveau 1</div>
                        </div>
                        <div className="kpi">
                            <div className="k-l">Sans profil</div>
                            <div className="k-v">{users.filter(sansProfil).length}</div>
                            <div className="k-s">À paramétrer</div>
                        </div>
                    </div>

                    {/* Recherche et filtres — maquette : toolbar */}
                    <div className="toolbar">
                        <div className="search-wrap">
                            <i className="fa-solid fa-magnifying-glass" />
                            <input
                                value={rechercheUser}
                                onChange={e => setRechercheUser(e.target.value)}
                                placeholder="Rechercher un utilisateur…"
                            />
                        </div>
                        {/* Filtre Département : c'est lui qui borne la liste */}
                        {deptsVisibles.length > 1 && (
                            <div className="filtre-dd">
                                <select value={filtreDept} onChange={e => setFiltreDept(e.target.value)}>
                                    <option value="">Tous les départements</option>
                                    {deptsVisibles.map(d => (
                                        <option key={d.id} value={d.id}>{d.code} — {d.nom}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div className="filtre-dd">
                            <select value={filtreProfil} onChange={e => setFiltreProfil(e.target.value)}>
                                <option value="">Tous les profils</option>
                                {/* Un profil propre à un département est préfixé de son code :
                                    la liste couvre plusieurs départements. */}
                                {profils.map(p => (
                                    <option key={p.id} value={p.id}>
                                        {p.scope === 'dept' && p.departement_code
                                            ? `${p.departement_code} · ${p.nom}`
                                            : p.nom}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="filtre-dd">
                            <select value={filtreStatut} onChange={e => setFiltreStatut(e.target.value)}>
                                <option value="">Tous statuts</option>
                                <option value="1">Actifs</option>
                                <option value="0">Inactifs</option>
                            </select>
                        </div>
                        <div className="filtre-dd">
                            <select value={filtreAcces} onChange={e => setFiltreAcces(e.target.value)} aria-label="Filtrer par accès">
                                <option value="">Tous les accès</option>
                                <option value="actif">Actifs (mot de passe créé)</option>
                                <option value="invite">Invités</option>
                                <option value="expire">Lien expiré</option>
                                <option value="non_invite">Pas encore invités</option>
                                <option value="ent">Connexion ENT</option>
                            </select>
                        </div>
                        <div className="grow" />
                        {/* Lot INV-c (maquette ③ ⑤) */}
                        {aInviter.length > 0 && (
                            <button className="a-btn a-btn-secondary" onClick={ouvrirInviterN}>
                                <i className="fa-solid fa-paper-plane" /> Inviter les {aInviter.length} compte{aInviter.length > 1 ? 's' : ''} sans mot de passe
                            </button>
                        )}
                        <button className="a-btn a-btn-secondary" onClick={() => chargerJournal(30)}>
                            <i className="fa-solid fa-clock-rotate-left" /> Journal des accès
                        </button>
                        <button className="a-btn a-btn-primary" onClick={openCreateUser}>
                            <i className="fa-solid fa-user-plus" /> Nouvel utilisateur
                        </button>
                    </div>


                    {/* Tableau — colonnes de la maquette (renderUsers). Lot INV-b : il DÉFILE en largeur s'il le faut — masquer ce
                        qui dépasse cachait « Statut » et « Actions » (mesuré à l'image, 1500 px). */}
                    <div className="a-card" style={{ padding: 0, overflowX: 'auto' }}>
                        <table className="tbl">
                            <thead>
                                <tr>
                                    <th>Utilisateur</th>
                                    <th>Email</th>
                                    <th>Profil</th>
                                    <th>Département(s)</th>
                                    <th>Compte enseignant</th>
                                    <th className="c">Signature</th>
                                    <th>Accès</th>
                                    <th className="c">Statut</th>
                                    <th className="c">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {usersFiltres.map(u => {
                                    // Le profil est porté par le couple personne x département :
                                    // en périmètre « tous les départements », on affiche une
                                    // pastille par rattachement, préfixée du code du département.
                                    const liens = liensAffiches(u);
                                    return (
                                        <tr key={u.id} className={u.actif ? '' : 'inactive'}>
                                            <td>
                                                <b>{u.nom_affiche || `${u.prenom} ${u.nom}`}</b>
                                                {u.is_super_admin && <span className="tag tag-warn" style={{ marginLeft: 6 }}>Super-admin</span>}
                                                {!u.is_super_admin && estAdminDuDept(u) && <span className="tag tag-warn" style={{ marginLeft: 6 }}>Admin dépt</span>}
                                            </td>
                                            {/* Lot INV-b : une coupure POSSIBLE avant le « @ » — la colonne « Accès » a pris 44 px et le tableau
                                                remplissait déjà toute la largeur (mesuré à 1500 px) ; l'adresse ne passe à la ligne que si la place manque. */}
                                            <td className="cell-email">{String(u.email || '').split('@').map((x, i) => (i ? <React.Fragment key={i}><wbr />@{x}</React.Fragment> : x))}</td>
                                            <td>
                                                {u.is_super_admin && (
                                                    <span className="profil-badge"><span className="dot" style={{ background: '#334155' }} />Tous droits</span>
                                                )}
                                                {!u.is_super_admin && liens.length === 0 && <span className="tag">Aucun profil</span>}
                                                {!u.is_super_admin && liens.map(d => (
                                                    <span key={d.id} className="profil-lien">
                                                        {d.profil
                                                            ? <span className="profil-badge">
                                                                  <span className="dot" style={{ background: d.profil.couleur || '#94a3b8' }} />
                                                                  {deptFiltreId ? d.profil.nom : `${d.code} · ${d.profil.nom}`}
                                                              </span>
                                                            : <span className="tag">{deptFiltreId ? 'Aucun profil' : `${d.code} · aucun profil`}</span>}
                                                        {d.nb_exceptions > 0 && (
                                                            <span className="tag tag-warn" title={`${d.nb_exceptions} exception(s) sur ${d.code}`}>
                                                                +{d.nb_exceptions}
                                                            </span>
                                                        )}
                                                    </span>
                                                ))}
                                            </td>
                                            <td>
                                                <span className="dept-chips">
                                                    {(u.departements || []).map(d => <span key={d.id} className="dept-chip">{d.code}</span>)}
                                                    {(u.departements || []).length === 0 && <span className="tag">—</span>}
                                                </span>
                                                {/* R5 (lot CO-a) : les rattachements hors de mon périmètre, dits par le serveur */}
                                                {(u.aussi_dans || []).length > 0 && (
                                                    <span className="aussi-dans" title={u.actions_compte?.raison || ''}>
                                                        <i className="fa-solid fa-arrow-up-right-from-square" /> aussi dans : {u.aussi_dans.join(', ')}
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                {u.enseignant_nom
                                                    ? <span className="tag tag-info"><i className="fa-solid fa-link" /> {u.enseignant_nom}</span>
                                                    : <span className="tag">—</span>}
                                            </td>
                                            <td className="c">
                                                <i className={u.a_signature ? 'fa-solid fa-circle-check' : 'fa-regular fa-circle'}
                                                   style={{ color: u.a_signature ? '#15803d' : '#94a3b8' }}
                                                   title={u.a_signature ? 'Signature déposée' : 'Aucune signature'} />
                                            </td>
                                            <td><CelluleAcces a={u.acces} /></td>
                                            <td className="c">
                                                <span className={'tag ' + (u.actif ? 'tag-ok' : 'tag-ko')}>{u.actif ? 'Actif' : 'Inactif'}</span>
                                            </td>
                                            <td className="c">
                                                <div className="row-act">
                                                    <button className="mini-btn" title="Modifier" onClick={() => openEditUser(u)}>
                                                        <i className="fa-solid fa-pen" />
                                                    </button>
                                                    <button
                                                        className="mini-btn"
                                                        title="Droits de ce compte (profil + exceptions)"
                                                        onClick={() => allerAuxDroits(u)}
                                                    >
                                                        <i className="fa-solid fa-user-shield" />
                                                    </button>
                                                    {/* Lot INV-b : le menu « … » de l'accès au compte (maquette ①) */}
                                                    <MenuAcces u={u} ouvert={menuAcces === u.id} onOuvrir={() => setMenuAcces(menuAcces === u.id ? null : u.id)}
                                                               onFermer={() => setMenuAcces(null)} boite={boiteReglee(u)}
                                                               onInviter={() => handleInviter(u)} onReinitialiser={() => handleReinitialiser(u)} onCopier={() => ouvrirLienCopie(u)} />
                                                    {/* R2 / R5 (lot CO-a) : l'acte sur le COMPTE n'est offert qu'à qui couvre
                                                        toute la personne ; sinon grisé avec sa raison, et « Retirer de mon département » */}
                                                    {u.actions_compte && !u.actions_compte.possible
                                                        ? <button className="mini-btn del" disabled title={u.actions_compte.raison}>
                                                              <i className={`fa-solid ${u.actif ? 'fa-user-slash' : 'fa-rotate-left'}`} />
                                                          </button>
                                                        : u.actif
                                                        ? <button className="mini-btn del" title="Désactiver le compte" onClick={() => handleDeleteUser(u)}>
                                                              <i className="fa-solid fa-user-slash" />
                                                          </button>
                                                        : <button className="mini-btn ok" title="Réactiver le compte" onClick={() => handleReactivateUser(u)}>
                                                              <i className="fa-solid fa-rotate-left" />
                                                          </button>}
                                                    {u.actions_compte && !u.actions_compte.possible && !u.is_super_admin && (() => {
                                                        const d = (u.departements || []).find(x => x.id === deptRetrait);
                                                        return d && (
                                                            <button className="mini-btn" title={`Retirer de mon département (${d.code})`}
                                                                    onClick={() => handleRetirerDuDepartement(u, d)}>
                                                                <i className="fa-solid fa-user-minus" />
                                                            </button>
                                                        );
                                                    })()}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {usersFiltres.length === 0 && (
                                    <tr>
                                        <td colSpan={9} style={{ textAlign: 'center', color: '#64748b', padding: 20 }}>
                                            Aucun utilisateur
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ============================================ */}
            {/* ONGLET PROFILS & DROITS */}
            {/* ============================================ */}
            {ongletActif === 'droits' && (
                <div className="a-view">
                    <div className="scope-hint">
                        <i className="fa-solid fa-building-columns" />
                        Droits du département : <b>{deptCourant ? deptCourant.nom : '—'}</b>
                        {deptsVisibles.length > 1 && (
                            <span className="hint">— se change dans le sélecteur de la topbar</span>
                        )}
                    </div>

                    <div className="sub-tabs">
                        <button
                            className={'sub-tab' + (sousOngletDroits === 'matrice' ? ' active' : '')}
                            onClick={() => setSousOngletDroits('matrice')}
                        ><i className="fa-solid fa-table-cells" /> Matrice des profils</button>
                        <button
                            className={'sub-tab' + (sousOngletDroits === 'fiche' ? ' active' : '')}
                            onClick={() => setSousOngletDroits('fiche')}
                        ><i className="fa-solid fa-user-shield" /> Par utilisateur</button>
                    </div>

                    {/* ---- Sous-onglet MATRICE ---- */}
                    {sousOngletDroits === 'matrice' && (
                        <>
                            <div className="note">
                                <i className="fa-solid fa-circle-info" />
                                <div>
                                    Chaque profil définit un niveau d'accès <b>par module</b> pour le département
                                    affiché. Les profils <span className="scope-badge scope-global">Globaux</span> sont
                                    publiés par le super-administrateur et hérités par tous les départements
                                    {!superAdmin && ' (lecture seule ici)'} ; les profils{' '}
                                    <span className="scope-badge scope-dept">Département</span> sont créés localement.
                                    Cliquez une cellule pour faire défiler Modifier → Consulter → Masqué.
                                </div>
                            </div>

                            <div className="matrix-legend">
                                <div className="lg-item"><span className="lg-chip lv-mod">M</span> Modifier — écriture (créer / éditer / supprimer)</div>
                                <div className="lg-item"><span className="lg-chip lv-con">C</span> Consulter — lecture seule (contrôles d'écriture masqués)</div>
                                <div className="lg-item"><span className="lg-chip lv-mask">–</span> Masqué — module absent de la sidebar</div>
                            </div>

                            <div className="toolbar">
                                <div className="grow" />
                                <button
                                    className="a-btn a-btn-secondary a-btn-sm"
                                    onClick={() => ouvrirCreationProfil(true)}
                                    disabled={!deptCourant}
                                ><i className="fa-solid fa-copy" /> Dupliquer un profil global</button>
                                <button
                                    className="a-btn a-btn-primary a-btn-sm"
                                    onClick={() => ouvrirCreationProfil(false)}
                                    disabled={!deptCourant}
                                ><i className="fa-solid fa-plus" /> Nouveau profil département</button>
                            </div>

                            <div className="matrix-wrap">
                                <table className="matrix">
                                    <thead>
                                        <tr>
                                            <th className="mx-corner">Profil</th>
                                            {MODULES_MATRICE.map(m => (
                                                <th key={m.key} className="mx-mod" title={m.label}>
                                                    <span className="mx-cat">{m.cat}</span>
                                                    <i className={`fa-solid ${m.icon}`} />
                                                    {m.label}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {profilsMatrice.map(p => {
                                            // Verrouillé : profil système, ou profil global vu par un
                                            // administrateur de département (lecture seule, spec §3)
                                            const verrou = p.est_systeme || (!superAdmin && p.scope === 'global');
                                            const nbComptes = Number(p.nb_users) || 0;
                                            return (
                                                <tr key={p.id}>
                                                    <td className="mx-prof">
                                                        <div className="mx-prof-name">
                                                            <span className="dot" style={{ background: p.couleur || '#94a3b8' }} />
                                                            {p.nom}
                                                            <span className={'scope-badge ' + (p.scope === 'global' ? 'scope-global' : 'scope-dept')}>
                                                                {p.scope === 'global' ? 'Global' : 'Dépt'}
                                                            </span>
                                                            {p.scope === 'dept' && !verrou && (
                                                                <button
                                                                    className="mini-btn del"
                                                                    title={nbComptes > 0
                                                                        ? `Attribué à ${nbComptes} compte(s) : impossible de le retirer`
                                                                        : 'Retirer ce profil'}
                                                                    disabled={nbComptes > 0}
                                                                    onClick={() => handleRetirerProfil(p)}
                                                                ><i className="fa-solid fa-trash" /></button>
                                                            )}
                                                        </div>
                                                        <div className="mx-prof-sub">
                                                            {p.description ? p.description + ' · ' : ''}
                                                            {nbComptes} compte(s)
                                                            {p.est_systeme && ' · non modifiable'}
                                                        </div>
                                                    </td>
                                                    {MODULES_MATRICE.map(m => {
                                                        const niveau = p.permissions?.[m.key] || 'masque';
                                                        return (
                                                            <td key={m.key}>
                                                                <div
                                                                    className={`mx-cell ${NIVEAU_CLASSE[niveau]}${verrou ? ' locked' : ''}`}
                                                                    title={`${p.nom} · ${m.label} — ${NIVEAU_LONG[niveau]}${verrou ? ' (verrouillé)' : ''}`}
                                                                    onClick={verrou ? undefined : () => cyclerCellule(p, m.key)}
                                                                >
                                                                    {NIVEAU_COURT[niveau]}
                                                                </div>
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Les profils des autres départements existent, mais ne
                                s'appliquent pas ici : on dit où les trouver. */}
                            {profilsAilleurs.length > 0 && (
                                <p className="hint" style={{ marginTop: 10 }}>
                                    <i className="fa-solid fa-circle-info" />{' '}
                                    {profilsAilleurs.length} profil(s) propre(s) à d'autres départements —
                                    ils n'apparaissent que sur le leur :{' '}
                                    {Object.entries(
                                        profilsAilleurs.reduce((acc, p) => {
                                            const code = p.departement_code || '?';
                                            acc[code] = [...(acc[code] || []), p.nom];
                                            return acc;
                                        }, {})
                                    ).map(([code, noms]) => `${code} (${noms.join(', ')})`).join(' · ')}
                                    . Changez de département dans le sélecteur de la topbar pour les régler.
                                </p>
                            )}

                            <div className="note warn" style={{ marginTop: 14 }}>
                                <i className="fa-solid fa-shield-halved" />
                                <div>
                                    Les droits sont appliqués <b>à la fois côté interface</b> (masquage de la sidebar
                                    et mode lecture seule) <b>et côté serveur</b> (chaque route d'écriture vérifie le
                                    niveau). Le profil <b>Super-administrateur</b> conserve tout et n'est pas
                                    modifiable. Un changement prend effet en quelques secondes, au prochain appel.
                                </div>
                            </div>
                        </>
                    )}

                    {/* ---- Sous-onglet PAR UTILISATEUR ---- */}
                    {sousOngletDroits === 'fiche' && (
                        <>
                            <div className="note">
                                <i className="fa-solid fa-circle-info" />
                                <div>
                                    Droits résolus d'un utilisateur = <b>son profil</b> dans ce département
                                    <b> + ses exceptions</b>. Une exception surcharge le profil sur un module précis ;
                                    choisir le niveau du profil la retire. Changer de profil réinitialise les exceptions.
                                </div>
                            </div>

                            <div className="fiche-grid">
                                <div className="a-card" style={{ padding: 10 }}>
                                    <div className="a-card-h" style={{ fontSize: 12, marginBottom: 8 }}>
                                        <i className="fa-solid fa-users" /> Utilisateurs du département
                                    </div>
                                    <div className="user-list">
                                        {usersDuDeptDroits.map(u => {
                                            const lien = (u.departements || []).find(d => d.id === deptCourantId);
                                            return (
                                                <div
                                                    key={u.id}
                                                    className={'user-list-item' + (u.id === ficheUserId ? ' active' : '')}
                                                    onClick={() => ouvrirFiche(u.id)}
                                                >
                                                    <div className="uli-name">{u.nom_affiche || `${u.prenom} ${u.nom}`}</div>
                                                    <div className="uli-sub">
                                                        {u.is_super_admin
                                                            ? 'Super-administrateur'
                                                            : (lien?.profil?.nom || 'Sans profil')}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {usersDuDeptDroits.length === 0 && (
                                            <span className="hint">Aucun utilisateur actif dans ce département.</span>
                                        )}
                                    </div>
                                </div>

                                <div className="a-card">
                                    <div className="a-card-h">
                                        <i className="fa-solid fa-user-shield" />
                                        {ficheUtilisateur
                                            ? (ficheUtilisateur.nom_affiche || `${ficheUtilisateur.prenom} ${ficheUtilisateur.nom}`)
                                            : '—'}
                                        <div className="grow" />
                                        <div className="filtre-dd">
                                            <select
                                                value={ficheDroits?.profil?.id ?? ''}
                                                onChange={e => changerProfilFiche(e.target.value)}
                                                disabled={!ficheDroits || ficheDroits.is_super_admin}
                                            >
                                                <option value="">Sans profil (tout masqué)</option>
                                                {profilsMatrice.filter(p => !p.est_systeme).map(p => (
                                                    <option key={p.id} value={p.id}>{p.nom}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {!ficheDroits && <p className="hint">Choisissez un utilisateur dans la liste.</p>}

                                    {ficheDroits?.is_super_admin && (
                                        <div className="note warn">
                                            <i className="fa-solid fa-shield-halved" />
                                            <div>
                                                Ce compte est <b>super-administrateur</b> : il conserve « Modifier » sur
                                                tous les modules de tous les départements. Ni profil ni exception ne
                                                s'appliquent.
                                            </div>
                                        </div>
                                    )}

                                    {ficheDroits && MODULES_MATRICE.map(m => {
                                        const d = ficheDroits.droits?.[m.key] || { niveau: 'masque', source: 'defaut' };
                                        const verrou = ficheDroits.is_super_admin === true;
                                        return (
                                            <div key={m.key} className="perm-row">
                                                <span className="pm-ic"><i className={`fa-solid ${m.icon}`} /></span>
                                                <div style={{ flex: 1 }}>
                                                    <div className="pm-name">{m.label}</div>
                                                    <div className="pm-cat">{m.cat}</div>
                                                </div>
                                                <span
                                                    className={'perm-src ' + (d.source === 'exception' ? 'src-except' : 'src-profil')}
                                                    title={d.source === 'exception'
                                                        ? `Exception individuelle — le profil dit « ${NIVEAU_LONG[d.niveau_profil] || '—'} »`
                                                        : d.source === 'defaut'
                                                            ? 'Aucun profil et aucune exception : masqué par défaut'
                                                            : 'Hérité du profil du département'}
                                                >
                                                    {SOURCE_LABEL[d.source] || 'Profil'}
                                                </span>
                                                <div className="seg">
                                                    {NIVEAUX.map(n => (
                                                        <button
                                                            key={n}
                                                            className={d.niveau === n ? SEG_CLASSE[n] : ''}
                                                            disabled={verrou}
                                                            onClick={() => reglerException(m.key, n)}
                                                        >{NIVEAU_LONG[n]}</button>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* ============================================ */}
            {/* ONGLET CONFIGURATION */}
            {/* ============================================ */}
            {ongletActif === 'docs' && <AdminDocuments onMessage={showMsg} onErreur={showErr} />}
            {ongletActif === 'aide' && superAdmin && <AdminAide onMessage={showMsg} onErreur={showErr} onCompteur={setAideNouveaux}
                auteur={user?.nom_affiche || [user?.prenom, user?.nom].filter(Boolean).join(' ') || user?.email || ''} />}

            {ongletActif === 'config' && (
                <div className="a-view">
                    <div className="note">
                        <i className="fa-solid fa-lock" />
                        <div>Réservé au <b>super-administrateur</b> : paramètres globaux de l'application.</div>
                    </div>

                    <div className="grid-2">
                        <div className="a-card">
                            <div className="a-card-h"><i className="fa-solid fa-image" /> Logo de l'université</div>

                            <label className="logo-drop" style={{ cursor: logoUnivEnCours ? 'wait' : 'pointer' }}
                                   onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('over'); }}
                                   onDragLeave={e => e.currentTarget.classList.remove('over')}
                                   onDrop={e => {
                                       e.preventDefault();
                                       e.currentTarget.classList.remove('over');
                                       handleLogoUniversite(e.dataTransfer.files[0]);
                                   }}>
                                <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp,image/svg+xml"
                                    style={{ display: 'none' }}
                                    disabled={logoUnivEnCours}
                                    onChange={e => handleLogoUniversite(e.target.files[0])}
                                />
                                {logoUnivEnCours ? (
                                    <><i className="fa-solid fa-spinner fa-spin" /><span>Envoi en cours…</span></>
                                ) : settings?.logo_universite ? (
                                    <img src={settings.logo_universite} alt="logo de l'université" className="logo-apercu" />
                                ) : (
                                    <><i className="fa-solid fa-cloud-arrow-up" />
                                      <span>Glisser ou cliquer — JPG · PNG · WEBP · SVG, 500 Ko max</span></>
                                )}
                            </label>

                            {settings?.logo_universite && !logoUnivEnCours && (
                                <button className="a-btn a-btn-secondary a-btn-sm" style={{ marginTop: 8 }}
                                        onClick={handleRetirerLogoUniversite}>
                                    <i className="fa-solid fa-trash" /> Retirer le logo
                                </button>
                            )}

                            <p className="hint" style={{ marginTop: 8 }}>
                                <b>Branding applicatif</b> : page de connexion et en-tête de l'application.
                                Ce n'est <b>pas</b> le logo des documents générés — ceux-ci utilisent le{' '}
                                <b>logo du département</b>, dans l'onglet Départements (spec §2.1).
                            </p>
                        </div>

                        <div className="a-card">
                            <div className="a-card-h"><i className="fa-solid fa-sliders" /> Paramètres généraux</div>

                            <div className="fg">
                                <label>Nom de l'application</label>
                                <input value={formNomAppli} onChange={e => setFormNomAppli(e.target.value)} />
                                <span className="hint">Affiché sur la page de connexion.</span>
                            </div>

                            {/* ⚠️ L'adresse publique part dans les liens envoyés aux
                                étudiants et aux entreprises (module Stage, écart E9).
                                Elle se saisit PAR ENVIRONNEMENT : dev et la production
                                rejouent les mêmes migrations, donc elle ne peut pas
                                être semée — sans quoi un lien de test partirait chez
                                une entreprise sans que rien ne le signale. */}
                            <div className="fg">
                                <label>Adresse publique de l'application</label>
                                <input
                                    value={formUrlPublique}
                                    placeholder="https://iut.magfel.dns-cloud.net/"
                                    onChange={e => setFormUrlPublique(e.target.value)}
                                />
                                <span className="hint">
                                    Utilisée pour fabriquer les liens envoyés par courriel
                                    (fiches de liaison, dépôts de documents, grilles d'évaluation).
                                    {' '}<b>Elle diffère entre développement et production</b> — à
                                    revérifier après chaque mise en production et après toute copie
                                    de la base de production. Tant qu'elle est vide, le module Stage
                                    refuse de fabriquer un lien.
                                </span>
                            </div>

                            {/* ⚠️ Écart E85 : un RÉGLAGE, pas une constante. La borne haute
                                (25) est mesurée — c'est ce que le proxy du NAS laisse passer. */}
                            <div className="fg">
                                <label>Taille maximale d'un document déposé (Mo)</label>
                                <input type="number" min={1} max={25}
                                       value={formTailleDoc}
                                       onChange={e => setFormTailleDoc(e.target.value)} />
                                <span className="hint">
                                    Entre 1 et 25. S'applique aux conventions et aux dépôts des étudiants.
                                    {' '}<b>Chaque document s'ajoute en entier aux sauvegardes</b> : un fichier
                                    chiffré ne se compresse plus.
                                </span>
                            </div>

                            <div className="fg">
                                <label>Durée de session (heures)</label>
                                <input type="number" min={1} max={720}
                                       value={formDureeSession}
                                       onChange={e => setFormDureeSession(e.target.value)} />
                                <span className="hint">
                                    Entre 1 et 720 (30 jours). Appliqué <b>à la prochaine connexion</b> : les
                                    sessions déjà ouvertes gardent leur durée.
                                </span>
                            </div>

                            <div className="fg">
                                <label>Longueur minimale des mots de passe</label>
                                <input type="number" min={8} max={128}
                                       value={formLongueurMdp}
                                       onChange={e => setFormLongueurMdp(e.target.value)} />
                                <span className="hint">
                                    Entre 8 et 128. Imposé à la <b>création</b> d'un compte et à tout changement
                                    de mot de passe ; les mots de passe existants ne sont pas invalidés.
                                </span>
                            </div>

                            <button className="a-btn a-btn-primary a-btn-sm" onClick={handleSaveSettings}>
                                <i className="fa-solid fa-check" /> Enregistrer
                            </button>
                        </div>
                    </div>

                    {/* Lot 3.S-a — maquette : réglage par défaut + redirection (serveur de test) */}
                    <CourrielConfiguration settings={settings} environnement={envCourriel}
                                           onEnregistre={chargerSettings} showMsg={showMsg} showErr={showErr} />

                    <div className="a-card">
                        <div className="a-card-h"><i className="fa-solid fa-grip-vertical" /> Ordre des onglets de l'Administration</div>
                        <p className="hint" style={{ marginBottom: 10 }}>
                            Glisser-déposer pour réordonner. L'ordre est <b>propre à chaque utilisateur</b>
                            {' '}(enregistré dans ses préférences) — il n'affecte personne d'autre.
                        </p>
                        <div className="tab-order">
                            {tabOrder.map(cle => {
                                const trouve = TABS.find(t => t.key === cle);
                                if (!trouve) return null;
                                const onglet = ongletAffiche(trouve, user);
                                return (
                                    <div
                                        key={cle}
                                        className={'tab-order-item' + (dragOverTab === cle ? ' over' : '')}
                                        draggable
                                        onDragStart={() => handleDragStart(cle)}
                                        onDragOver={(e) => handleDragOver(e, cle)}
                                        onDragLeave={handleDragLeave}
                                        onDrop={() => handleDrop(cle)}
                                        onDragEnd={() => setDragOverTab(null)}
                                    >
                                        <span className="handle"><i className="fa-solid fa-grip-vertical" /></span>
                                        <i className={`fa-solid ${onglet.icon} mi`} />
                                        {onglet.label}
                                        {SUPER_ONLY.includes(cle) && <i className="fa-solid fa-lock lock" />}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Lot 5.3 — cette carte annonçait « migration v32 » avec le numéro ÉCRIT
                        EN DUR : elle devenait fausse à chaque migration (v33 la contredisait).
                        Elle lit désormais `schema_migrations`, et sert au passage le suivi de
                        l'occupation — LECTURE SEULE, aucune route d'écriture. C'est ce chiffre
                        qui dira un jour s'il faut sortir les documents de la base
                        (cf. doc/SUIVI_DEV.md §3, règle de stockage des documents). */}
                    <div className="a-card">
                        <div className="a-card-h">
                            <i className="fa-solid fa-circle-info" /> À propos
                            <button className="a-btn a-btn-secondary" style={{ marginLeft: 'auto' }}
                                    onClick={chargerOccupation} title="Rafraîchir">
                                <i className="fa-solid fa-rotate" />
                            </button>
                        </div>
                        <p className="hint">
                            <b>{settings?.nom_application || 'Département Manager'}</b> — version{' '}
                            <b>{process.env.REACT_APP_VERSION || 'développement'}</b>.
                            {occupation?.migration ? (
                                <> Base de données à la migration <b>{occupation.migration.version}</b>
                                    {occupation.migration.applique_le && (
                                        <> (appliquée le {new Date(occupation.migration.applique_le).toLocaleDateString('fr-FR')})</>
                                    )}.
                                </>
                            ) : ' Version de base de données indisponible.'}
                        </p>

                        {occupation?.base && (
                            <>
                                <p className="hint" style={{ marginTop: 8 }}>
                                    <i className="fa-solid fa-database" /> Poids de la base :{' '}
                                    <b>{occupation.base.lisible}</b>
                                </p>
                                {occupation.tables?.length > 0 && (
                                    <table className="tbl" style={{ marginTop: 8 }}>
                                        <thead>
                                            <tr><th>Ce qui pèse le plus</th><th style={{ textAlign: 'right' }}>Taille</th></tr>
                                        </thead>
                                        <tbody>
                                            {occupation.tables.map(t => (
                                                <tr key={t.table_nom}>
                                                    <td>{t.table_nom}</td>
                                                    <td style={{ textAlign: 'right' }}>{t.lisible}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* ============================================ */}
            {/* ONGLET SIGNATURES & DÉLÉGATIONS */}
            {/* ============================================ */}
            {ongletActif === 'sig' && (
                <div className="a-view">
                    {/* S2 (lot CO-a) — maquette : sigPerimetre. L'administrateur de composante et le super-admin
                        choisissent un département ou toute une composante ; l'administrateur de département voit le sien. */}
                    {(superAdmin || adminComposante) && composantes.length > 0 && (
                        <div className="toolbar">
                            <label className="hint" htmlFor="sig-perimetre" style={{ fontWeight: 700 }}>Périmètre</label>
                            <select id="sig-perimetre" value={perimetreSig || (deptCourantId ? 'd' + deptCourantId : '')}
                                    onChange={e => setPerimetreSig(e.target.value)}>
                                {composantes.map(c => (
                                    <React.Fragment key={c.id}>
                                        <option value={'c' + c.id}>Composante {c.nom} — tous ses départements</option>
                                        {departements.filter(d => d.composante_id === c.id && d.actif).map(d => (
                                            <option key={d.id} value={'d' + d.id}>{c.code} › {d.code} — {d.nom}</option>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </select>
                        </div>
                    )}
                    <div className="scope-hint">
                        {String(perimetreSig).startsWith('c')
                            ? <><i className="fa-solid fa-sitemap" /> Composante : <b>{composantes.find(c => 'c' + c.id === perimetreSig)?.nom || '—'}</b></>
                            : <><i className="fa-solid fa-building-columns" /> Département : <b>{(departements.find(d => 'd' + d.id === perimetreSig) || deptCourant)?.nom || '—'}</b></>}
                    </div>

                    <div className="note rgpd">
                        <i className="fa-solid fa-shield-halved" />
                        <div>
                            <b>Dépôt unique.</b> C'est le seul endroit où l'on téléverse une signature et
                            un tampon. Les images sont <b>chiffrées côté serveur</b> et{' '}
                            <b>jamais téléchargeables</b> : aucune page, aucune adresse ne les renvoie —
                            elles ne ressortent qu'incrustées dans un document généré. Les modules{' '}
                            <b>Poursuite d'études</b> et <b>Vie du département</b> ne re-téléversent
                            jamais : ils désignent un signataire. Chaque dépôt exige une{' '}
                            <b>ré-authentification</b> et laisse une trace au journal d'audit.
                        </div>
                    </div>

                    {!chiffrementOk && (
                        <div className="admin-alert admin-alert-warn">
                            <i className="fa-solid fa-triangle-exclamation" />{' '}
                            Le chiffrement n'est pas configuré sur ce serveur : le dépôt est désactivé.
                            Une image ne sera jamais stockée en clair. (Variable d'environnement
                            <code> SIGNATURE_KEY</code> — voir <code>doc/specs_techniques/deploiement.md</code>.)
                        </div>
                    )}

                    <div className="grid-2">
                        <div className="a-card">
                            <div className="a-card-h"><i className="fa-solid fa-signature" /> Signatures &amp; tampons déposés</div>
                            <table className="tbl">
                                <thead>
                                    <tr>
                                        <th>Utilisateur</th>
                                        <th>Signe pour</th>
                                        <th className="c">Signature</th>
                                        <th className="c">Tampon</th>
                                        <th className="c">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {signatures.map(p => (
                                        <tr key={p.id}>
                                            <td>
                                                <b>{p.nom_affiche}</b>
                                                <div className="hint">{p.email}</div>
                                            </td>
                                            {/* S3 : la signature est à la PERSONNE — ses départements et, le cas échéant, sa composante */}
                                            <td>
                                                {(p.signe_pour_departements || []).map(c => <span key={c} className="portee">{c}</span>)}{' '}
                                                {(p.signe_pour_composantes || []).map(n => <span key={n} className="portee portee-compo">{n}</span>)}
                                            </td>
                                            <td className="c">
                                                {p.a_signature
                                                    ? <span className="sig-status sig-ok" title={`Déposée le ${new Date(p.signature_deposee_le).toLocaleDateString('fr-FR')}`}>
                                                          <i className="fa-solid fa-circle-check" /> Déposée
                                                      </span>
                                                    : <span className="sig-status sig-ko">Absente</span>}
                                            </td>
                                            <td className="c">
                                                {p.a_tampon
                                                    ? <span className="sig-status sig-ok" title={`Déposé le ${new Date(p.tampon_depose_le).toLocaleDateString('fr-FR')}`}>
                                                          <i className="fa-solid fa-circle-check" /> Déposé
                                                      </span>
                                                    : <span className="sig-status sig-ko">Absent</span>}
                                            </td>
                                            <td className="c">
                                                <div className="row-act">
                                                    <button
                                                        className="a-btn a-btn-secondary a-btn-sm"
                                                        onClick={() => ouvrirDepot(p)}
                                                        disabled={!chiffrementOk}
                                                        title={chiffrementOk ? undefined : 'Chiffrement non configuré'}
                                                    >
                                                        <i className="fa-solid fa-upload" />{' '}
                                                        {p.a_signature || p.a_tampon ? 'Remplacer' : 'Déposer'}
                                                    </button>
                                                    {p.a_signature && (
                                                        <button className="mini-btn del" title="Retirer la signature"
                                                                onClick={() => handleRetirerSignature(p, 'signature')}>
                                                            <i className="fa-solid fa-signature" />
                                                        </button>
                                                    )}
                                                    {p.a_tampon && (
                                                        <button className="mini-btn del" title="Retirer le tampon"
                                                                onClick={() => handleRetirerSignature(p, 'tampon')}>
                                                            <i className="fa-solid fa-stamp" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {signatures.length === 0 && (
                                        <tr>
                                            <td colSpan={5} style={{ textAlign: 'center', color: '#64748b', padding: 16 }}>
                                                Aucun compte actif dans ce périmètre.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="a-card">
                            <div className="a-card-h">
                                <i className="fa-solid fa-people-arrows" /> Délégations de signature
                                <div className="grow" />
                                <button className="a-btn a-btn-primary a-btn-sm" onClick={ouvrirDelegation}
                                        disabled={String(perimetreSig).startsWith('c')}
                                        title={String(perimetreSig).startsWith('c') ? 'Choisissez un département : une délégation se crée dans un département' : undefined}>
                                    <i className="fa-solid fa-plus" /> Déléguer
                                </button>
                            </div>
                            <table className="tbl">
                                <thead>
                                    <tr>
                                        <th>Portée</th>
                                        <th>Titulaire</th>
                                        <th>Délégué</th>
                                        <th>Période</th>
                                        <th>Motif</th>
                                        <th className="c">Statut</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {delegations.map(d => (
                                        <tr key={d.id} className={d.statut === 'active' ? '' : 'inactive'}>
                                            {/* S1 : un département OU une composante */}
                                            <td>
                                                <span className={'portee' + (d.portee_composante ? ' portee-compo' : '')}>
                                                    {d.portee_composante ? `Composante ${d.portee_composante}` : `Département ${d.portee_departement}`}
                                                </span>
                                            </td>
                                            <td>
                                                {d.titulaire_nom}
                                                {!d.titulaire_a_signature && (
                                                    <div className="hint" title="Le titulaire n'a pas déposé de signature">
                                                        signature manquante
                                                    </div>
                                                )}
                                            </td>
                                            <td>{d.delegue_nom}</td>
                                            <td className="hint">
                                                {new Date(d.date_debut).toLocaleDateString('fr-FR')} →{' '}
                                                {new Date(d.date_fin).toLocaleDateString('fr-FR')}
                                            </td>
                                            <td>{d.motif || <span className="hint">—</span>}</td>
                                            <td className="c">
                                                {d.statut === 'active' && <span className="tag tag-ok">Active</span>}
                                                {d.statut === 'a_venir' && <span className="tag tag-info">À venir</span>}
                                                {d.statut === 'expiree' && <span className="tag">Expirée</span>}
                                                {d.statut === 'revoquee' && <span className="tag tag-ko">Révoquée</span>}
                                            </td>
                                            <td className="c">
                                                {d.statut !== 'revoquee' && (
                                                    <button className="mini-btn del" title="Révoquer"
                                                            onClick={() => handleRevoquerDelegation(d)}>
                                                        <i className="fa-solid fa-xmark" />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    {delegations.length === 0 && (
                                        <tr>
                                            <td colSpan={7} style={{ textAlign: 'center', color: '#64748b', padding: 16 }}>
                                                Aucune délégation
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                            {/* S4 REPORTÉ (décision du 2026-10-07) : l'écran le dit */}
                            <p className="hint" style={{ marginTop: 8 }}>
                                <i className="fa-solid fa-circle-info" /> Une délégation se crée dans un département. La délégation
                                « de composante » viendra avec le premier document signé au nom de la composante (et son signataire).
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* ============================================ */}
            {/* ONGLET API & CONNEXIONS */}
            {/* ============================================ */}
            {ongletActif === 'api' && (
                <div className="a-view">
                    <div className="note rgpd">
                        <i className="fa-solid fa-plug" />
                        <div>
                            Générez des <b>clés d'API en lecture seule</b> pour que des applications
                            tierces consomment les données (REST JSON). Le périmètre est limité{' '}
                            <b>par module</b> et n'expose <b>aucune donnée personnelle par défaut</b>
                            {' '}(agrégats, calendrier, programme). La clé est <b>affichée une seule fois</b>
                            {' '}à la création : seule son empreinte est conservée, personne ne peut la
                            retrouver ensuite.
                        </div>
                    </div>

                    <div className="sub-tabs">
                        <button
                            className={'sub-tab' + (sousOngletApi === 'cles' ? ' active' : '')}
                            onClick={() => setSousOngletApi('cles')}
                        ><i className="fa-solid fa-key" /> Clés d'API (sortant)</button>
                        <button
                            className={'sub-tab' + (sousOngletApi === 'journal' ? ' active' : '')}
                            onClick={() => setSousOngletApi('journal')}
                        ><i className="fa-solid fa-list-check" /> Journal d'appels</button>
                        <button
                            className={'sub-tab' + (sousOngletApi === 'sso' ? ' active' : '')}
                            onClick={() => setSousOngletApi('sso')}
                        ><i className="fa-brands fa-microsoft" /> Connexion SSO</button>
                    </div>

                    {/* ---- Sous-onglet CLÉS ---- */}
                    {sousOngletApi === 'cles' && (
                        <>
                            <div className="toolbar">
                                <div className="grow" />
                                <button className="a-btn a-btn-primary" onClick={ouvrirCreationCle}>
                                    <i className="fa-solid fa-plus" /> Nouvelle clé d'API
                                </button>
                            </div>

                            <div className="a-card" style={{ padding: 0, overflow: 'hidden' }}>
                                <table className="tbl">
                                    <thead>
                                        <tr>
                                            <th>Nom</th>
                                            <th>Préfixe</th>
                                            <th>Périmètre (lecture)</th>
                                            <th className="c">Perso</th>
                                            <th>Expiration</th>
                                            <th>Dernier appel</th>
                                            <th className="c">Statut</th>
                                            <th className="c"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {apiKeys.map(k => (
                                            <tr key={k.id} className={k.statut === 'active' ? '' : 'inactive'}>
                                                <td>
                                                    <b>{k.nom}</b>
                                                    {k.cree_par && <div className="hint">créée par {k.cree_par}</div>}
                                                </td>
                                                <td className="cell-email">{k.prefixe}…</td>
                                                <td>
                                                    {(k.scopes || []).map(s => {
                                                        const m = MODULES_MATRICE.find(x => x.key === s);
                                                        return (
                                                            <span key={s} className="scope-pill">
                                                                <i className={`fa-solid ${m ? m.icon : 'fa-cube'}`} />
                                                                {m ? m.label : s}
                                                            </span>
                                                        );
                                                    })}
                                                </td>
                                                <td className="c">
                                                    {k.autorise_donnees_perso
                                                        ? <span className="tag tag-warn" title="Cette clé peut lire des données personnelles">Oui</span>
                                                        : <span className="tag" title="Agrégats et données non personnelles uniquement">Non</span>}
                                                </td>
                                                <td className="hint">
                                                    {k.date_expiration
                                                        ? new Date(k.date_expiration).toLocaleDateString('fr-FR')
                                                        : 'sans'}
                                                </td>
                                                <td className="hint">
                                                    {k.derniere_utilisation
                                                        ? new Date(k.derniere_utilisation).toLocaleString('fr-FR')
                                                        : 'jamais'}
                                                    {Number(k.nb_appels) > 0 && ` · ${k.nb_appels} appel(s)`}
                                                </td>
                                                <td className="c">
                                                    {k.statut === 'active' && <span className="tag tag-ok">Active</span>}
                                                    {k.statut === 'revoquee' && <span className="tag tag-ko">Révoquée</span>}
                                                    {k.statut === 'expiree' && <span className="tag tag-warn">Expirée</span>}
                                                </td>
                                                <td className="c">
                                                    {k.statut !== 'revoquee' && (
                                                        <button
                                                            className="mini-btn del"
                                                            title="Révoquer cette clé"
                                                            onClick={() => handleRevoquerCle(k)}
                                                        ><i className="fa-solid fa-ban" /></button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {apiKeys.length === 0 && (
                                            <tr>
                                                <td colSpan={8} style={{ textAlign: 'center', color: '#64748b', padding: 20 }}>
                                                    Aucune clé d'API. Créez-en une pour ouvrir un accès en lecture à une application tierce.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    {/* ---- Sous-onglet JOURNAL ---- */}
                    {sousOngletApi === 'journal' && (
                        <>
                            <div className="note">
                                <i className="fa-solid fa-circle-info" />
                                <div>
                                    Les 100 derniers appels, <b>refus compris</b> : c'est ainsi qu'on voit
                                    qu'une clé révoquée continue d'être utilisée quelque part, ou qu'une
                                    application se trompe de point d'accès.
                                </div>
                            </div>
                            <div className="a-card" style={{ padding: 0, overflow: 'hidden' }}>
                                <table className="tbl">
                                    <thead>
                                        <tr>
                                            <th>Horodatage</th>
                                            <th>Clé</th>
                                            <th>Endpoint</th>
                                            <th className="c">Code</th>
                                            <th>Origine (IP)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {apiJournal.map(a => (
                                            <tr key={a.id}>
                                                <td className="hint">{new Date(a.horodatage).toLocaleString('fr-FR')}</td>
                                                <td>{a.api_key_id ? a.cle_nom : <span className="hint">clé non reconnue</span>}</td>
                                                <td className="cell-email">{a.endpoint}</td>
                                                <td className="c">
                                                    <span className={'tag ' + (a.code_http < 400 ? 'tag-ok' : 'tag-ko')}>
                                                        {a.code_http}
                                                    </span>
                                                </td>
                                                <td className="cell-email">{a.ip_source || '—'}</td>
                                            </tr>
                                        ))}
                                        {apiJournal.length === 0 && (
                                            <tr>
                                                <td colSpan={5} style={{ textAlign: 'center', color: '#64748b', padding: 20 }}>
                                                    Aucun appel enregistré.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    {/* ---- Sous-onglet SSO (livré désactivé, spec §6.2) ---- */}
                    {sousOngletApi === 'sso' && (
                        <>
                            <div className="sso-status">
                                <div>
                                    <i className="fa-brands fa-microsoft" /> <b>Connexion Microsoft 365 (Entra ID)</b>
                                    {' — '}{ssoConfig?.actif ? 'Activée' : 'Désactivée'}
                                </div>
                                <div className="sso-status-sub">
                                    Fonctionnalité <b>planifiée</b>. À activer lorsque l'accès à l'administration
                                    Azure sera disponible (enregistrement de l'application + secret). En attendant,
                                    la connexion se fait par <b>e-mail + mot de passe attribué</b>, et les champs
                                    ci-dessous sont en lecture seule.
                                </div>
                            </div>

                            <div className="grid-2">
                                <div className="a-card">
                                    <div className="a-card-h">
                                        <i className="fa-solid fa-plug-circle-bolt" /> Paramètres du fournisseur
                                        <div className="grow" />
                                        <label className="switch" title="Activation possible une fois l'accès Azure disponible">
                                            <input type="checkbox" checked={ssoConfig?.actif === true} disabled readOnly />
                                            <span className="sl-tr" />
                                        </label>
                                    </div>
                                    <fieldset className="sso-champs" disabled>
                                        <div className="fg">
                                            <label>Tenant ID (Directory / annuaire)</label>
                                            <input value={ssoConfig?.tenant_id || ''} placeholder="00000000-0000-0000-0000-000000000000" readOnly />
                                        </div>
                                        <div className="fg">
                                            <label>Client ID (Application)</label>
                                            <input value={ssoConfig?.client_id || ''} placeholder="00000000-0000-0000-0000-000000000000" readOnly />
                                        </div>
                                        <div className="fg">
                                            <label>
                                                Client Secret <span className="hint">— chiffré côté serveur, jamais ré-affiché</span>
                                            </label>
                                            <input type="password" value="" placeholder={ssoConfig?.secret_enregistre ? '••••••••••' : 'aucun secret enregistré'} readOnly />
                                        </div>
                                        <div className="fg">
                                            <label>Redirect URI (callback) <span className="hint">— à déclarer dans Azure</span></label>
                                            <div className="api-key-box" style={{ fontSize: 12 }}>
                                                <span>{ssoConfig?.redirect_uri || `${window.location.origin}/api/auth/sso/microsoft/callback`}</span>
                                            </div>
                                        </div>
                                        <div className="fg">
                                            <label>Domaines autorisés <span className="hint">— séparés par des virgules</span></label>
                                            <input
                                                value={(ssoConfig?.domaines_autorises || []).join(', ')}
                                                placeholder="univ-evry.fr, iut.univ-evry.fr"
                                                readOnly
                                            />
                                        </div>
                                        <div className="fg">
                                            <label>Scopes</label>
                                            <input value={ssoConfig?.scopes || ''} readOnly />
                                        </div>
                                    </fieldset>
                                </div>

                                <div className="a-card">
                                    <div className="a-card-h"><i className="fa-solid fa-user-check" /> Règles de connexion</div>
                                    <div className="fg">
                                        <label>Provisioning des comptes (1re connexion)</label>
                                        <label className="radio-row">
                                            <input type="radio" name="ssoProv" checked={ssoConfig?.provisioning !== 'auto_en_attente'} disabled readOnly />
                                            <span><b>Pré-provisionné</b> — seuls les e-mails déjà déclarés peuvent se connecter</span>
                                        </label>
                                        <label className="radio-row">
                                            <input type="radio" name="ssoProv" checked={ssoConfig?.provisioning === 'auto_en_attente'} disabled readOnly />
                                            <span><b>Création automatique</b> → nouveau compte en <b>« attente de validation »</b> (profil Invité), l'administrateur affecte profil + département</span>
                                        </label>
                                    </div>
                                    <div className="fg">
                                        <label>Mot de passe local</label>
                                        <label className="radio-row">
                                            <input type="radio" name="ssoPw" checked={ssoConfig?.mode_mdp_local !== 'forcer_sso'} disabled readOnly />
                                            <span><b>Coexistence</b> — SSO + e-mail/mot de passe (secours super-administrateur, comptes techniques)</span>
                                        </label>
                                        <label className="radio-row">
                                            <input type="radio" name="ssoPw" checked={ssoConfig?.mode_mdp_local === 'forcer_sso'} disabled readOnly />
                                            <span><b>Forcer SSO</b> — mot de passe local désactivé (sauf super-administrateur)</span>
                                        </label>
                                    </div>
                                    <div className="fg">
                                        <label>Correspondance des attributs</label>
                                        <div className="hint">
                                            email → <b>email</b> · displayName → <b>nom_affiche</b> · oid →{' '}
                                            <b>identifiant SSO</b> (rattachement du compte)
                                        </div>
                                    </div>
                                    <div className="note rgpd" style={{ margin: '6px 0 0' }}>
                                        <i className="fa-solid fa-shield-halved" />
                                        <div>
                                            Échange de jetons <b>côté serveur</b> (flux OpenID Connect). Le secret client
                                            n'est jamais exposé au navigateur, et l'API ne le renvoie jamais.
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="a-card">
                                <div className="a-card-h"><i className="fa-solid fa-eye" /> Aperçu — page de connexion</div>
                                <div className="login-preview">
                                    <div className="lp-card">
                                        <div className="lp-logo"><i className="fa-solid fa-graduation-cap" /></div>
                                        <div className="lp-title">Département Manager</div>
                                        <button className="lp-sso" disabled={!ssoConfig?.actif}>
                                            <i className="fa-brands fa-microsoft" /> Se connecter avec Microsoft 365
                                        </button>
                                        <div className="lp-or"><span>ou</span></div>
                                        <input className="lp-inp" placeholder="Adresse e-mail" disabled />
                                        <input className="lp-inp" type="password" placeholder="Mot de passe" disabled />
                                        <button className="lp-btn" disabled>Se connecter</button>
                                    </div>
                                    <p className="hint" style={{ maxWidth: 320 }}>
                                        Le bouton <b>Microsoft 365</b> n'apparaîtra que si le SSO est activé. Le
                                        formulaire e-mail / mot de passe reste présent tant que la coexistence est
                                        choisie — c'est le mode actuel, et c'est ce qui évite tout verrouillage.
                                    </p>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* ============================================ */}
            {/* ONGLET DÉPARTEMENTS */}
            {/* ============================================ */}
            {ongletActif === 'depts' && (
                <div className="a-view">
                    {/* Maquette : note d'en-tête de la vue Départements */}
                    <div className="note">
                        <i className={`fa-solid ${superAdmin ? 'fa-lock' : 'fa-circle-info'}`} />
                        <div>
                            {superAdmin
                                ? <>Réservé au <b>super-administrateur</b> : les <b>composantes</b> (IUT, UFR…) et
                                   leurs administrateurs ; les départements de chacune.</>
                                : adminComposante
                                ? <>Administrateur de la composante <b>{composantes.map(c => c.nom).join(', ')}</b> :
                                   vous créez ses départements, nommez leurs administrateurs et les désactivez. Vous
                                   n'avez <b>aucun droit sur les modules</b> : pour travailler dans un module, il faut
                                   un profil dans le département.</>
                                : <>Vous administrez ce département : vous pouvez modifier son <b>nom</b>, sa{' '}
                                   <b>description</b>, son <b>logo</b> et l'<b>en-tête des documents</b> (adresse,
                                   signataire…) — ce qui s'imprime sur les documents générés. L'activer ou nommer ses administrateurs relève de
                                   l'administrateur de sa composante ou du super-administrateur.</>}
                        </div>
                    </div>

                    {superAdmin && (
                        <div className="toolbar">
                            <button className="a-btn a-btn-secondary a-btn-sm" onClick={() => toutReplier(true)}>
                                <i className="fa-solid fa-compress" /> Tout replier
                            </button>
                            <button className="a-btn a-btn-secondary a-btn-sm" onClick={() => toutReplier(false)}>
                                <i className="fa-solid fa-expand" /> Tout déplier
                            </button>
                            <div className="grow" />
                            <button className="a-btn a-btn-primary" onClick={() => openCompo(null)}>
                                <i className="fa-solid fa-sitemap" /> Nouvelle composante
                            </button>
                        </div>
                    )}

                    {/* Lot CO-a : un bloc par composante (maquette : compo-bloc), ses départements en cartes */}
                    {groupesDepts.map(g => {
                        const replie = superAdmin && replies.includes(g.id);
                        const nbActifs = g.departements.filter(d => d.actif).length;
                        return (
                    <div key={g.id} className={'compo-bloc' + (replie ? ' replie' : '')} data-compo={g.id}>
                        <div className="compo-hd">
                            {superAdmin && (
                                <button className="compo-pli" title={replie ? 'Déplier' : 'Replier'} onClick={() => basculerRepli(g.id)}>
                                    <i className={`fa-solid fa-chevron-${replie ? 'right' : 'down'}`} />
                                </button>
                            )}
                            <div className="compo-ic"><i className="fa-solid fa-sitemap" /></div>
                            <div>
                                <span className="dc-code">{g.code}</span> <span className="compo-nom">{g.nom}</span>
                                {replie && (
                                    <div className="compo-resume">
                                        {g.departements.length} département{g.departements.length > 1 ? 's' : ''}
                                        {' '}({nbActifs} actif{nbActifs > 1 ? 's' : ''})
                                    </div>
                                )}
                            </div>
                            {g.admins && (
                                <div className="compo-admins">
                                    Administrateur(s) de composante :{' '}
                                    {g.admins.length > 0
                                        ? g.admins.map(a => (
                                            <span key={a.id} className="adm" title={a.email}>
                                                <i className="fa-solid fa-user-tie" /> {a.nom_affiche}
                                            </span>
                                        ))
                                        : <span className="hint">aucun</span>}
                                </div>
                            )}
                            {superAdmin && (
                                <button className="a-btn a-btn-secondary a-btn-sm" onClick={() => openCompo(g)}>
                                    <i className="fa-solid fa-pen" /> Modifier / nommer
                                </button>
                            )}
                        </div>
                        {!replie && (
                    <div className="dept-cards">
                        {g.departements.map(d => (
                            <div key={d.id} className={'dept-card' + (d.actif ? '' : ' inactive')}>
                                <div className="dc-hd">
                                    <div className="dc-ic">
                                        {d.logo_url && d.logo_url.startsWith('data:')
                                            ? <img src={d.logo_url} alt="" />
                                            : <i className="fa-solid fa-building-columns" />}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <span className="dc-code">{d.code}</span>
                                        <div className="dc-name">{d.nom}</div>
                                    </div>
                                    <span className={'tag ' + (d.actif ? 'tag-ok' : 'tag-ko')}>
                                        {d.actif ? 'Actif' : 'Inactif'}
                                    </span>
                                </div>

                                {d.description && <p className="hint">{d.description}</p>}

                                <div className="dc-stats">
                                    <span><b>{d.nb_users}</b> utilisateurs</span>
                                    <span><b>{d.nb_sous_niveaux}</b> groupes</span>
                                </div>

                                {/* Admin(s) du département — maquette : ligne dc-admins */}
                                <div className="dc-admins">
                                    Admin(s) :{' '}
                                    {(d.admins || []).length > 0
                                        ? (d.admins || []).map(a => (
                                              <span key={a.id} className="adm" title={a.email}>
                                                  <i className="fa-solid fa-user-shield" /> {a.nom_affiche}
                                              </span>
                                          ))
                                        : <span className="hint">aucun</span>}
                                </div>

                                {/* Logo du département — maquette : zone logo-drop, ici réellement
                                    fonctionnelle (clic ou glisser-déposer, aperçu, retrait) */}
                                <div style={{ position: 'relative' }}>
                                    <label
                                        className="logo-drop"
                                        style={{ cursor: logoUploading[d.id] ? 'wait' : 'pointer' }}
                                        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('over'); }}
                                        onDragLeave={(e) => e.currentTarget.classList.remove('over')}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            e.currentTarget.classList.remove('over');
                                            const file = e.dataTransfer.files[0];
                                            if (file) handleLogoUpload(d.id, file);
                                        }}
                                    >
                                        <input
                                            type="file"
                                            accept="image/jpeg,image/png,image/bmp,image/gif,image/webp"
                                            style={{ display: 'none' }}
                                            disabled={logoUploading[d.id]}
                                            onChange={(e) => handleLogoUpload(d.id, e.target.files[0])}
                                        />
                                        {logoUploading[d.id] ? (
                                            <>
                                                <i className="fa-solid fa-spinner fa-spin" />
                                                <span>Envoi en cours…</span>
                                            </>
                                        ) : d.logo_url && d.logo_url.startsWith('data:') ? (
                                            <img src={d.logo_url} alt="logo du département" className="logo-apercu" />
                                        ) : (
                                            <>
                                                <i className="fa-solid fa-image" />
                                                <span>Logo du département — glisser ou cliquer</span>
                                            </>
                                        )}
                                        <span className="hint">JPG · PNG · BMP · GIF · WEBP — 500 Ko max</span>
                                    </label>
                                    {d.logo_url && d.logo_url.startsWith('data:') && !logoUploading[d.id] && (
                                        <button
                                            className="mini-btn del"
                                            style={{ position: 'absolute', top: 6, right: 6 }}
                                            title="Retirer le logo"
                                            onClick={(e) => { e.stopPropagation(); handleLogoDelete(d.id); }}
                                        ><i className="fa-solid fa-trash" /></button>
                                    )}
                                </div>

                                {/* Lot 3.S-a — maquette : ligne dc-mail + « Régler » */}
                                <CourrielLigneCarte etat={etatsCourriel[d.id]} environnement={envCourriel}
                                                    onRegler={() => setCourrielDept(d)} />
                                {/* Lot ADM-E (D28) — maquette : ligne « En-tête des documents » + « Régler » */}
                                <EnteteLigneCarte etat={etatsEntete[d.id]} onRegler={() => setEnteteDept(d)} />

                                <div className="row-act" style={{ marginTop: 4 }}>
                                    <button className="a-btn a-btn-secondary a-btn-sm" onClick={() => openEditDept(d)}>
                                        <i className="fa-solid fa-pen" /> Modifier
                                    </button>
                                    {/* Activer / désactiver : sa composante ou le super-admin (CO4) */}
                                    {peutNommer && (
                                        <button
                                            className={'a-btn a-btn-sm ' + (d.actif ? 'a-btn-danger' : 'a-btn-ok')}
                                            onClick={() => handleToggleDept(d)}
                                        >
                                            <i className={`fa-solid ${d.actif ? 'fa-ban' : 'fa-rotate-left'}`} />
                                            {' '}{d.actif ? 'Désactiver' : 'Réactiver'}
                                        </button>
                                    )}
                                    {/* Lot CO-b (CB0) — maquette : corbeille, super-administrateur ; DÉFINITIF, seulement vide */}
                                    {superAdmin && (
                                        <button className="a-btn a-btn-sm a-btn-danger" title="Suppression définitive — seulement si le département n'a aucune donnée"
                                                onClick={() => ouvrirSuppressionDept(d)}>
                                            <i className="fa-solid fa-trash" />
                                        </button>
                                    )}
                                    {d.actif && (
                                        <button className="a-btn a-btn-secondary a-btn-sm" onClick={() => onViewDepartement(d)}>
                                            <i className="fa-solid fa-calendar-days" /> Voir le planning
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}

                        {/* Carte d'appel à la création — maquette : dc-new. Lot CO-b : le super-admin et
                            l'administrateur de la composante (le serveur sème le département). */}
                        {peutNommer && (
                            <div className="dept-card dc-new" onClick={() => openCreateDept(g.id)}>
                                <i className="fa-solid fa-plus" />
                                <span>Nouveau département</span>
                                <span className="hint" style={{ fontWeight: 400 }}>dans {g.nom}</span>
                            </div>
                        )}
                    </div>
                        )}
                    </div>
                        );
                    })}
                    {groupesDepts.length === 0 && <p className="hint">Aucun département dans votre périmètre.</p>}
                </div>
            )}
            {/* ============================================ */}
            {/* ONGLET CALENDRIERS */}
            {/* ============================================ */}
            {ongletActif === 'calendriers' && (
                <div className="a-view">
                    {/* Maquette : note d'en-tête de la vue Calendriers */}
                    <div className="note">
                        <i className="fa-solid fa-lock" />
                        <div>
                            Réservé au <b>super-administrateur</b> : les années universitaires sont
                            <b> transverses à tous les départements</b> et alimentent le sélecteur d'année
                            de l'application.
                        </div>
                    </div>

                    <div className="toolbar">
                        <div className="grow" />
                        <button className="a-btn a-btn-primary" onClick={ouvrirCreationAnnee}>
                            <i className="fa-solid fa-plus" /> Nouvelle année universitaire
                        </button>
                    </div>

                    <div className="a-card" style={{ padding: 0, overflow: 'hidden' }}>
                        <table className="tbl">
                            <thead>
                                <tr>
                                    <th>Année universitaire</th>
                                    <th className="c">Zone</th>
                                    <th className="c">Jours fériés</th>
                                    <th className="c">Vacances</th>
                                    <th className="c">Généré le</th>
                                    <th className="c">Statut</th>
                                    <th className="c">Visibilité</th>
                                    <th className="c">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {annees.map(a => (
                                    <tr key={a.id} className={a.visible ? '' : 'inactive'}>
                                        <td><b>{a.libelle}</b></td>
                                        <td className="c">
                                            {a.zone
                                                ? <span className="tag">{a.zone === 'NC' ? 'Aucune' : `Zone ${a.zone}`}</span>
                                                : <span className="hint" title="Année créée avant l'enregistrement de la zone : la prochaine régénération la renseignera">
                                                      non renseignée
                                                  </span>}
                                        </td>

                                        {/* État du calendrier : « présent ou absent »,
                                            pas « complet » — le nombre de périodes varie
                                            d'une année à l'autre (Pont de l'Ascension). */}
                                        <td className="c">
                                            {Number(a.nb_jours_feries) > 0
                                                ? <span className="tag tag-ok" title={`${a.nb_jours_feries} jours fériés dans l'année`}>Présents</span>
                                                : <span className="tag tag-warn" title="Aucun jour férié : la génération a échoué, régénérez le calendrier">Absents</span>}
                                        </td>
                                        <td className="c">
                                            {Number(a.nb_periodes_vacances) > 0
                                                ? <span className="tag tag-ok" title={`${a.nb_periodes_vacances} période(s), ${a.nb_jours_vacances} jours`}>Présentes</span>
                                                : <span className="tag tag-warn" title="Aucune vacance : les dates officielles n'étaient pas publiées lors de la génération. Régénérez le calendrier.">Absentes</span>}
                                        </td>
                                        <td className="c">
                                            {a.calendrier_genere_le
                                                ? <span className="hint" title={new Date(a.calendrier_genere_le).toLocaleString('fr-FR')}>
                                                      {new Date(a.calendrier_genere_le).toLocaleDateString('fr-FR')}
                                                  </span>
                                                : <span className="hint" title="Année générée avant l'enregistrement de cette information">—</span>}
                                        </td>

                                        <td className="c">
                                            <span className={'tag ' + (a.active ? 'tag-ok' : '')}>
                                                {a.active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="c">
                                            <span className={'tag ' + (a.visible ? 'tag-info' : '')}>
                                                {a.visible ? 'Visible' : 'Cachée'}
                                            </span>
                                        </td>
                                        <td className="c">
                                            <div className="row-act">
                                                {/* Activer — seulement sur une année inactive */}
                                                {!a.active && (
                                                    <button
                                                        className="a-btn a-btn-ok a-btn-sm"
                                                        onClick={() => handleActiverAnnee(a)}
                                                        title="Rendre cette année active (désactive l'année courante)"
                                                    >
                                                        <i className="fa-solid fa-bolt" /> Activer
                                                    </button>
                                                )}

                                                {/* Régénérer — jours fériés et vacances depuis l'API officielle */}
                                                <button
                                                    className="a-btn a-btn-secondary a-btn-sm"
                                                    onClick={() => ouvrirRegeneration(a)}
                                                    title="Recalculer les jours fériés et les vacances de cette année"
                                                >
                                                    <i className="fa-solid fa-rotate" /> Régénérer
                                                </button>

                                                {/* Cacher / afficher — impossible sur l'année active */}
                                                <button
                                                    className="a-btn a-btn-secondary a-btn-sm"
                                                    onClick={() => handleToggleVisibilite(a)}
                                                    disabled={a.active}
                                                    title={a.active ? "L'année active ne peut pas être cachée" : (a.visible ? 'Cacher du menu déroulant' : 'Rendre visible')}
                                                >
                                                    <i className={`fa-solid ${a.visible ? 'fa-eye-slash' : 'fa-eye'}`} />
                                                    {' '}{a.visible ? 'Cacher' : 'Afficher'}
                                                </button>

                                                {/* Suppression — confirmation en modale, avec re-saisie
                                                    du libellé exact (spec §9) */}
                                                <button
                                                    className="mini-btn del"
                                                    onClick={() => ouvrirSuppressionAnnee(a)}
                                                    disabled={a.active}
                                                    title={a.active
                                                        ? "L'année active ne peut pas être supprimée : activez d'abord une autre année"
                                                        : 'Supprimer définitivement'}
                                                ><i className="fa-solid fa-trash" /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {annees.length === 0 && (
                                    <tr>
                                        <td colSpan={8} style={{ textAlign: 'center', color: '#64748b', padding: 20 }}>
                                            Aucune année universitaire — créez-en une avec le bouton ci-dessus.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            </div> {/* a-content */}

            {/* ---- FICHE UTILISATEUR (maquette : modale userModal) ---- */}
            {showUserForm && (
                <div className="admin-modal-overlay" onClick={resetUserForm}>
                    <div className="admin-modal" onClick={e => e.stopPropagation()}>
                        <div className="admin-modal-h">
                            <i className={`fa-solid ${editingUser ? 'fa-pen' : 'fa-user-plus'}`} />
                            {editingUser ? `Modifier ${editingUser.nom_affiche || editingUser.email}` : 'Nouvel utilisateur'}
                            <button className="x" onClick={resetUserForm} aria-label="Fermer">&times;</button>
                        </div>

                        <div className="admin-modal-b admin-page">
                            {/* R2 / R5 (lot CO-a) : le compte (nom, e-mail, mot de passe, compte enseignant) ne se
                                modifie que par qui couvre toute la personne ; ses droits ICI restent modifiables. */}
                            {compteVerrouille && (
                                <div className="note" style={{ marginBottom: 10 }}>
                                    <i className="fa-solid fa-lock" />
                                    <div>{editingUser.actions_compte.raison} Vous pouvez régler ses départements et son profil dans votre périmètre.</div>
                                </div>
                            )}
                            <div className="field-grid">
                                <div className="fg">
                                    <label>Prénom</label>
                                    <input value={formPrenom} onChange={e => setFormPrenom(e.target.value)} placeholder="Jean" {...verrouCompte} />
                                </div>
                                <div className="fg">
                                    <label>Nom</label>
                                    <input value={formNom} onChange={e => setFormNom(e.target.value)} placeholder="Dupont" {...verrouCompte} />
                                </div>
                            </div>

                            <div className="fg">
                                <label>
                                    Nom d'affichage <span className="hint">— utilisé pour les signatures d'e-mails (Stage), convocations…</span>
                                </label>
                                <input
                                    value={formNomAffiche}
                                    {...verrouCompte}
                                    onChange={e => setFormNomAffiche(e.target.value)}
                                    placeholder={`${formPrenom} ${formNom}`.trim() || 'Jean Dupont'}
                                />
                            </div>

                            <div className="field-grid">
                                <div className="fg">
                                    <label>Email</label>
                                    <input type="email" value={formEmail} onChange={e => setFormEmail(e.target.value)} placeholder="j.dupont@iut.fr" {...verrouCompte} />
                                </div>
                                {/* Lot INV-b (Q5) : en modification, « Nouveau mot de passe » pour le seul super-administrateur ;
                                    les autres envoient un lien de réinitialisation (menu « … » de la ligne). */}
                                {editingUser && superAdmin && (
                                    <div className="fg">
                                        <label>Nouveau mot de passe (vide = inchangé)</label>
                                        <input type="password" value={formPassword} onChange={e => setFormPassword(e.target.value)} placeholder="••••••••••" {...verrouCompte} />
                                    </div>
                                )}
                                {editingUser && !superAdmin && (
                                    <div className="fg">
                                        <label>Mot de passe</label>
                                        <div className="hint acces-hint">Vous ne le voyez ni ne le choisissez : menu « … » de la ligne → <b>Envoyer un lien de réinitialisation</b>.</div>
                                    </div>
                                )}
                            </div>

                            <div className="fg">
                                <label>
                                    Compte enseignant lié <span className="hint">— active le bouton « Mon espace » du module Enseignants</span>
                                </label>
                                {(() => {
                                    // 2026-10-10 : UNE ligne par personne et par département (la fiche la plus récente, `ids` =
                                    // toutes ses fiches) ; seulement les départements cochés pour ce compte (plus bas), et
                                    // toujours la ligne déjà liée — même si le compte visait une fiche d'une année antérieure.
                                    const liee = enseignants.find(e => (e.ids || [e.id]).map(String).includes(String(formEnseignant)));
                                    const vues = enseignants.filter(e => e === liee || !formDepts.length || formDepts.includes(e.departement_id));
                                    return (
                                        <select value={liee ? String(liee.id) : (formEnseignant || '')} onChange={e => setFormEnseignant(e.target.value)} {...verrouCompte}>
                                            <option value="">Aucun</option>
                                            {vues.map(e => (
                                                <option key={e.id} value={e.id}>
                                                    {e.nom_complet}{e.departement_code ? ` — ${e.departement_code}` : ''}{e.email ? ` · ${e.email}` : ''}{e.homonyme ? ' ⚠️ homonyme' : ''}
                                                </option>
                                            ))}
                                        </select>
                                    );
                                })()}
                                {!formDepts.length && <div className="hint" style={{ marginTop: 3 }}>Cochez d'abord le(s) département(s) du compte : la liste se limite à leurs enseignants.</div>}
                            </div>

                            <div className="fg">
                                <label>Département(s) d'affectation</label>
                                <div className="chk-grid">
                                    {/* Périmètre : un administrateur de département ne rattache un compte
                                        qu'aux départements qu'il administre (le serveur refuse le reste) */}
                                    {deptsVisibles.map(d => (
                                        <label key={d.id} className={'chk-chip' + (formDepts.includes(d.id) ? ' on' : '')}>
                                            <input type="checkbox" checked={formDepts.includes(d.id)} onChange={() => toggleDept(d.id)} />
                                            {d.code}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="fg">
                                <label>
                                    Profil
                                    {deptFiche && (
                                        <span className="hint">
                                            {' '}— dans le département {departements.find(d => d.id === deptFiche)?.code || ''}
                                        </span>
                                    )}
                                </label>
                                {/* Seuls les profils applicables à CE département */}
                                <select value={formProfil} onChange={e => setFormProfil(e.target.value)}>
                                    <option value="">Aucun profil (tout masqué)</option>
                                    {profilsPour(deptFiche).filter(p => !p.est_systeme).map(p => (
                                        <option key={p.id} value={p.id}>{p.nom}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Lot INV-b (maquette ②) : l'ACCÈS AU COMPTE remplace le mot de passe saisi par l'administrateur */}
                            {!editingUser && (() => {
                                const boite = formDepts.some(d => boitesReglees.includes(d));
                                return (
                                    <div className="fg">
                                        <label>Accès au compte</label>
                                        <label className={'acces-choix' + (formAcces === 'invitation' ? ' on' : '')}>
                                            <input type="radio" name="acces" checked={formAcces === 'invitation'} onChange={() => setFormAcces('invitation')} />
                                            <span><b>Envoyer l'invitation par courriel</b> — {formPrenom || 'la personne'} reçoit un lien personnel (valable <b>72 h</b>) pour créer elle-même son mot de passe. Personne d'autre ne le connaît.</span>
                                        </label>
                                        <label className={'acces-choix' + (formAcces === 'plus_tard' ? ' on' : '')}>
                                            <input type="radio" name="acces" checked={formAcces === 'plus_tard'} onChange={() => setFormAcces('plus_tard')} />
                                            <span><b>Ne pas envoyer maintenant</b> — le compte est créé « Pas encore invité » ; l'invitation partira plus tard (menu « … » de la ligne).</span>
                                        </label>
                                        {superAdmin && (
                                            <label className={'acces-choix' + (formAcces === 'mot_de_passe' ? ' on' : '')}>
                                                <input type="radio" name="acces" checked={formAcces === 'mot_de_passe'} onChange={() => setFormAcces('mot_de_passe')} />
                                                <span><b>Définir le mot de passe moi-même</b> — compte de test, secours (super-administrateur seulement).</span>
                                            </label>
                                        )}
                                        {formAcces === 'mot_de_passe' && (
                                            <input type="password" value={formPassword} onChange={e => setFormPassword(e.target.value)} placeholder="Mot de passe" style={{ marginTop: 6 }} />
                                        )}
                                        {formAcces === 'invitation' && formDepts.length > 0 && !boite && (
                                            <div className="note warn" style={{ marginTop: 8 }}>
                                                <i className="fa-solid fa-triangle-exclamation" />
                                                <div><b>Boîte d'envoi du département non réglée</b> (Administration → Mon département → Envoi des courriels) : l'invitation ne peut pas partir par courriel. Le compte sera créé, puis son lien vous sera donné, à lui transmettre vous-même.</div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                            <div className="note">
                                <i className="fa-solid fa-circle-info" />
                                <div>
                                    Les exceptions individuelles (surcharges par module) se règlent ensuite dans
                                    <b> Profils &amp; droits → Par utilisateur</b> (étape 0.C.3).
                                </div>
                            </div>
                        </div>

                        <div className="admin-modal-f">
                            <button className="a-btn a-btn-secondary" onClick={resetUserForm}>Annuler</button>
                            <button className="a-btn a-btn-primary" onClick={handleSaveUser}>
                                <i className="fa-solid fa-check" /> {editingUser ? 'Enregistrer'
                                    : formAcces === 'invitation' && formDepts.some(d => boitesReglees.includes(d)) ? "Créer et envoyer l'invitation" : 'Créer le compte'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ---- DÉPÔT SIGNATURE / TAMPON (maquette : sigModal + reauthModal fusionnés) ---- */}
            {depotPour && (
                <div className="admin-modal-overlay" onClick={depotEnCours ? undefined : fermerDepot}>
                    <div className="admin-modal" onClick={e => e.stopPropagation()}>
                        <div className="admin-modal-h">
                            <i className="fa-solid fa-signature" />
                            Déposer signature &amp; tampon
                            <button className="x" onClick={fermerDepot} aria-label="Fermer">&times;</button>
                        </div>

                        <div className="admin-modal-b admin-page">
                            <div className="note rgpd">
                                <i className="fa-solid fa-shield-halved" />
                                <div>
                                    Images <b>chiffrées côté serveur</b>, jamais téléchargeables.
                                    Utilisateur : <b>{depotPour.nom_affiche}</b>.
                                </div>
                            </div>

                            <div className="note">
                                <i className="fa-solid fa-circle-info" />
                                <div>
                                    Formats acceptés : <b>PNG, JPG, WEBP</b> — <b>300 Ko maximum</b>.
                                    Le <b>PNG à fond transparent</b> est recommandé : une signature sur
                                    fond blanc opaque masquerait le texte du document sur lequel elle
                                    s'appose. GIF, BMP et PDF sont refusés.
                                </div>
                            </div>

                            <div className="fg">
                                <label>Signature (image) <span className="hint">— PNG à fond transparent recommandé</span></label>
                                <input
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp"
                                    onChange={e => setFichierSignature(e.target.files[0] || null)}
                                />
                                {fichierSignature && (
                                    <span className="hint">
                                        {fichierSignature.name} — {Math.round(fichierSignature.size / 1024)} Ko
                                    </span>
                                )}
                            </div>

                            <div className="fg">
                                <label>Tampon (image)</label>
                                <input
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp"
                                    onChange={e => setFichierTampon(e.target.files[0] || null)}
                                />
                                {fichierTampon && (
                                    <span className="hint">
                                        {fichierTampon.name} — {Math.round(fichierTampon.size / 1024)} Ko
                                    </span>
                                )}
                            </div>

                            {/* Ré-authentification : la maquette en fait une modale séparée ;
                                la demander ici évite un aller-retour pour la même action. */}
                            <div className="note warn">
                                <i className="fa-solid fa-fingerprint" />
                                <div>
                                    Action sensible : elle engage la signature de cette personne. Confirmez
                                    avec <b>votre</b> mot de passe. L'opération sera <b>journalisée</b>.
                                </div>
                            </div>
                            <div className="fg">
                                <label>Votre mot de passe</label>
                                <input
                                    type="password"
                                    value={motDePasseReauth}
                                    onChange={e => setMotDePasseReauth(e.target.value)}
                                    placeholder="••••••••••"
                                    autoComplete="current-password"
                                />
                            </div>
                        </div>

                        <div className="admin-modal-f">
                            <button className="a-btn a-btn-secondary" onClick={fermerDepot} disabled={depotEnCours}>Annuler</button>
                            <button className="a-btn a-btn-primary" onClick={handleDeposerSignature} disabled={depotEnCours}>
                                {depotEnCours
                                    ? <><i className="fa-solid fa-spinner fa-spin" /> Dépôt en cours…</>
                                    : <><i className="fa-solid fa-lock" /> Enregistrer (chiffré)</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ---- ENVOI DES COURRIELS D'UN DÉPARTEMENT (lot 3.S-a, maquette : courrielModal) ---- */}
            {courrielDept && (
                <CourrielModale departement={courrielDept} onFermer={() => setCourrielDept(null)}
                                onChange={chargerEtatsCourriel} showMsg={showMsg} />
            )}

            {/* ---- EN-TÊTE DES DOCUMENTS D'UN DÉPARTEMENT (lot ADM-E, D28 ; maquette : enteteModal) ---- */}
            {enteteDept && (
                <EnteteModale departement={enteteDept} onFermer={() => setEnteteDept(null)}
                              onChange={chargerEtatsEntete} showMsg={showMsg} />
            )}

            {/* ---- NOUVELLE DÉLÉGATION (maquette : modale delegModal) ---- */}
            {showDelegForm && (
                <div className="admin-modal-overlay" onClick={() => setShowDelegForm(false)}>
                    <div className="admin-modal sm" onClick={e => e.stopPropagation()}>
                        <div className="admin-modal-h">
                            <i className="fa-solid fa-people-arrows" />
                            Nouvelle délégation de signature
                            <button className="x" onClick={() => setShowDelegForm(false)} aria-label="Fermer">&times;</button>
                        </div>

                        <div className="admin-modal-b admin-page">
                            <div className="fg">
                                <label>Titulaire (signataire)</label>
                                <select value={formDelegTitulaire} onChange={e => setFormDelegTitulaire(e.target.value)}>
                                    <option value="">— Choisir —</option>
                                    {signatures.map(p => (
                                        <option key={p.id} value={p.id}>
                                            {p.nom_affiche}{p.a_signature ? '' : ' (signature manquante)'}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="fg">
                                <label>Délégué</label>
                                <select value={formDelegDelegue} onChange={e => setFormDelegDelegue(e.target.value)}>
                                    <option value="">— Choisir —</option>
                                    {signatures
                                        .filter(p => String(p.id) !== formDelegTitulaire)
                                        .map(p => <option key={p.id} value={p.id}>{p.nom_affiche}</option>)}
                                </select>
                            </div>
                            <div className="field-grid">
                                <div className="fg">
                                    <label>Du</label>
                                    <ChampDate value={formDelegDu} onChange={e => setFormDelegDu(e.target.value)} />
                                </div>
                                <div className="fg">
                                    <label>Au</label>
                                    <ChampDate value={formDelegAu} onChange={e => setFormDelegAu(e.target.value)} />
                                </div>
                            </div>
                            <div className="fg">
                                <label>Motif</label>
                                <input
                                    value={formDelegMotif}
                                    onChange={e => setFormDelegMotif(e.target.value)}
                                    placeholder="Ex. Congé du chef de département"
                                />
                            </div>

                            <div className="note rgpd">
                                <i className="fa-solid fa-clock-rotate-left" />
                                <div>
                                    La délégation est <b>limitée dans le temps</b> et <b>journalisée</b>. Le
                                    délégué pourra apposer la signature du titulaire pendant la période, avec
                                    ré-authentification et trace d'audit.
                                </div>
                            </div>
                        </div>

                        <div className="admin-modal-f">
                            <button className="a-btn a-btn-secondary" onClick={() => setShowDelegForm(false)}>Annuler</button>
                            <button className="a-btn a-btn-primary" onClick={handleCreerDelegation}>
                                <i className="fa-solid fa-check" /> Créer la délégation
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ---- NOUVELLE CLÉ D'API (maquette : modale apiModal) ---- */}
            {showApiForm && (
                <div className="admin-modal-overlay" onClick={() => setShowApiForm(false)}>
                    <div className="admin-modal" onClick={e => e.stopPropagation()}>
                        <div className="admin-modal-h">
                            <i className="fa-solid fa-key" />
                            Nouvelle clé d'API (lecture seule)
                            <button className="x" onClick={() => setShowApiForm(false)} aria-label="Fermer">&times;</button>
                        </div>

                        <div className="admin-modal-b admin-page">
                            <div className="fg">
                                <label>Nom de la clé</label>
                                <input
                                    value={formApiNom}
                                    onChange={e => setFormApiNom(e.target.value)}
                                    placeholder="Ex. BI Université, ENT étudiant…"
                                    autoFocus
                                />
                                <span className="hint">Sert à la reconnaître dans la liste et dans le journal d'appels.</span>
                            </div>

                            <div className="fg">
                                <label>Périmètre — modules exposés en lecture</label>
                                <div className="chk-grid">
                                    {MODULES_MATRICE.map(m => (
                                        <label key={m.key} className={'chk-chip' + (formApiScopes.includes(m.key) ? ' on' : '')}>
                                            <input
                                                type="checkbox"
                                                checked={formApiScopes.includes(m.key)}
                                                onChange={() => toggleScopeApi(m.key)}
                                            />
                                            <i className={`fa-solid ${m.icon}`} /> {m.label}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="fg" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                                <label className="switch">
                                    <input
                                        type="checkbox"
                                        checked={formApiPerso}
                                        onChange={e => setFormApiPerso(e.target.checked)}
                                    />
                                    <span className="sl-tr" />
                                </label>
                                <div>
                                    <label style={{ margin: 0 }}>Autoriser les données personnelles</label>
                                    <div className="hint">
                                        Désactivé par défaut (agrégats uniquement). RGPD : à n'activer que si
                                        l'application tierce en a réellement besoin.
                                    </div>
                                </div>
                            </div>

                            <div className="fg">
                                <label>Expiration <span className="hint">— vide = pas d'expiration</span></label>
                                <ChampDate value={formApiExpiration} onChange={e => setFormApiExpiration(e.target.value)} />
                            </div>

                            <div className="note warn">
                                <i className="fa-solid fa-triangle-exclamation" />
                                <div>
                                    La clé sera <b>affichée une seule fois</b>, juste après sa création. Seule son
                                    empreinte est enregistrée : ni l'application ni la base ne pourront la retrouver.
                                    Conservez-la en lieu sûr.
                                </div>
                            </div>
                        </div>

                        <div className="admin-modal-f">
                            <button className="a-btn a-btn-secondary" onClick={() => setShowApiForm(false)}>Annuler</button>
                            <button className="a-btn a-btn-primary" onClick={handleCreerCle}>
                                <i className="fa-solid fa-check" /> Générer la clé
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ---- CLÉ RÉVÉLÉE (maquette : modale apiRevealModal) ---- */}
            {cleRevelee && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal sm" onClick={e => e.stopPropagation()}>
                        <div className="admin-modal-h">
                            <i className="fa-solid fa-circle-check" style={{ color: '#15803d' }} />
                            Clé générée
                        </div>
                        <div className="admin-modal-b admin-page">
                            <p className="hint">Copiez cette clé maintenant — elle ne sera plus jamais affichée.</p>
                            <div className="api-key-box">
                                <span>{cleRevelee}</span>
                                <button className="copy" onClick={copierCle}>
                                    <i className={`fa-regular ${cleCopiee ? 'fa-circle-check' : 'fa-copy'}`} />
                                    {' '}{cleCopiee ? 'Copiée' : 'Copier'}
                                </button>
                            </div>
                            <div className="note" style={{ marginTop: 12, marginBottom: 0 }}>
                                <i className="fa-solid fa-circle-info" />
                                <div>
                                    Utilisation : en-tête <code>X-API-Key</code> (ou{' '}
                                    <code>Authorization: Bearer …</code>) sur les points d'accès{' '}
                                    <code>/api/public/…</code>.
                                </div>
                            </div>
                        </div>
                        <div className="admin-modal-f">
                            <button className="a-btn a-btn-primary" onClick={() => setCleRevelee(null)}>
                                <i className="fa-solid fa-check" /> J'ai copié la clé
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ---- SUPPRESSION D'UNE ANNÉE UNIVERSITAIRE ---- */}
            {anneeASupprimer && (
                <div className="admin-modal-overlay" onClick={fermerSuppressionAnnee}>
                    <div className="admin-modal sm" onClick={e => e.stopPropagation()}>
                        <div className="admin-modal-h">
                            <i className="fa-solid fa-triangle-exclamation" style={{ color: '#dc2626' }} />
                            Supprimer l'année {anneeASupprimer.libelle}
                            <button className="x" onClick={fermerSuppressionAnnee} aria-label="Fermer">&times;</button>
                        </div>

                        <div className="admin-modal-b admin-page">
                            <div className="note warn">
                                <i className="fa-solid fa-triangle-exclamation" />
                                <div>
                                    Suppression <b>définitive</b> de l'année et de tout ce qui s'y rattache :
                                    calendrier, vacances, événements posés, périodes de formation, exports.
                                    Cette action est irréversible.
                                </div>
                            </div>

                            <div className="fg">
                                <label>
                                    Pour confirmer, saisissez le libellé exact : <b>{anneeASupprimer.libelle}</b>
                                </label>
                                <input
                                    value={texteSuppression}
                                    onChange={e => setTexteSuppression(e.target.value)}
                                    placeholder="Saisissez le libellé ici"
                                    autoFocus
                                />
                                {texteSuppression !== anneeASupprimer.libelle && (
                                    <span className="hint">
                                        Le bouton s'activera quand le libellé saisi correspondra exactement.
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="admin-modal-f">
                            <button className="a-btn a-btn-secondary" onClick={fermerSuppressionAnnee}>Annuler</button>
                            <button
                                className="a-btn a-btn-danger"
                                disabled={texteSuppression !== anneeASupprimer.libelle}
                                onClick={() => handleDeleteAnnee(anneeASupprimer)}
                            >
                                <i className="fa-solid fa-trash" /> Supprimer définitivement
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ---- ANNÉE UNIVERSITAIRE : création ou régénération ---- */}
            {showAnneeForm && (
                <div className="admin-modal-overlay" onClick={anneeEnCours ? undefined : fermerFormAnnee}>
                    <div className="admin-modal" onClick={e => e.stopPropagation()}>
                        <div className="admin-modal-h">
                            <i className={`fa-solid ${anneeARegenerer ? 'fa-rotate' : 'fa-plus'}`} />
                            {anneeARegenerer
                                ? `Régénérer le calendrier ${anneeARegenerer.libelle}`
                                : 'Nouvelle année universitaire'}
                            <button className="x" onClick={fermerFormAnnee} aria-label="Fermer">&times;</button>
                        </div>

                        <div className="admin-modal-b admin-page">
                            <div className="fg">
                                <label>Année de début</label>
                                <input
                                    type="number"
                                    min={2020}
                                    max={2050}
                                    value={formAnneeDebut}
                                    onChange={e => setFormAnneeDebut(e.target.value)}
                                    disabled={!!anneeARegenerer}
                                />
                                <span className="hint">
                                    Année universitaire <b>{formAnneeDebut}-{parseInt(formAnneeDebut, 10) + 1}</b>
                                    {' '}— le calendrier couvre 13 mois, de septembre à septembre.
                                </span>
                            </div>

                            <div className="fg">
                                <label>Zone de vacances</label>
                                <select value={formAnneeZone} onChange={e => setFormAnneeZone(e.target.value)}>
                                    {ZONES.map(z => <option key={z.valeur} value={z.valeur}>{z.libelle}</option>)}
                                </select>
                                <span className="hint">
                                    Les vacances sont récupérées sur le calendrier scolaire officiel
                                    (data.education.gouv.fr) pour cette zone. Elle est <b>enregistrée</b> :
                                    inutile de la ressaisir à chaque régénération.
                                </span>
                            </div>

                            <div className="note">
                                <i className="fa-solid fa-circle-info" />
                                <div>
                                    {anneeARegenerer
                                        ? <>Les <b>jours fériés</b> et les <b>vacances</b> de cette année seront
                                           recalculés. Les événements déjà posés dans le Planning annuel ne sont
                                           pas touchés. L'année reste {anneeARegenerer.active ? 'active' : 'inactive'}.</>
                                        : <>L'année est créée <b>inactive</b> : elle n'apparaît pas comme année
                                           courante tant que vous ne l'avez pas activée. Si les dates de vacances
                                           ne sont pas encore publiées — elles ne sortent qu'au printemps —
                                           l'année est créée quand même, et « Régénérer » les récupérera plus tard.</>}
                                </div>
                            </div>
                        </div>

                        <div className="admin-modal-f">
                            <button className="a-btn a-btn-secondary" onClick={fermerFormAnnee} disabled={anneeEnCours}>
                                Annuler
                            </button>
                            <button className="a-btn a-btn-primary" onClick={handleSaveAnnee} disabled={anneeEnCours}>
                                {anneeEnCours
                                    ? <><i className="fa-solid fa-spinner fa-spin" /> Génération en cours…</>
                                    : <><i className="fa-solid fa-check" /> {anneeARegenerer ? 'Régénérer' : 'Créer'}</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ---- NOUVEAU PROFIL (maquette : modale profilModal) ---- */}
            {showProfilForm && (
                <div className="admin-modal-overlay" onClick={fermerCreationProfil}>
                    <div className="admin-modal sm" onClick={e => e.stopPropagation()}>
                        <div className="admin-modal-h">
                            <i className={`fa-solid ${profilDepuisGlobal ? 'fa-copy' : 'fa-plus'}`} />
                            {profilDepuisGlobal ? 'Dupliquer un profil global' : 'Nouveau profil département'}
                            <button className="x" onClick={fermerCreationProfil} aria-label="Fermer">&times;</button>
                        </div>

                        <div className="admin-modal-b admin-page">
                            <div className="note">
                                <i className="fa-solid fa-circle-info" />
                                <div>
                                    Le profil est <b>propre au département {deptCourant?.code || ''}</b>.
                                    {profilDepuisGlobal
                                        ? ' Ses niveaux partent du profil global choisi, puis vous les ajustez dans la matrice.'
                                        : ' Ses niveaux partent du profil de base choisi, puis vous les ajustez dans la matrice.'}
                                </div>
                            </div>

                            <div className="fg">
                                <label>Nom du profil</label>
                                <input
                                    value={formProfilNom}
                                    onChange={e => setFormProfilNom(e.target.value)}
                                    placeholder="Ex. Référent stages"
                                    autoFocus
                                />
                            </div>

                            <div className="fg">
                                <label>Partir d'un profil existant</label>
                                <select value={formProfilBase} onChange={e => setFormProfilBase(e.target.value)}>
                                    <option value="">Aucun — tout masqué au départ</option>
                                    {profils
                                        .filter(p => (profilDepuisGlobal
                                            ? p.scope === 'global'
                                            : p.scope === 'global' || p.departement_id === deptCourantId))
                                        .map(p => (
                                            <option key={p.id} value={p.id}>
                                                {p.nom} ({p.scope === 'global' ? 'global' : 'dépt'})
                                            </option>
                                        ))}
                                </select>
                            </div>
                        </div>

                        <div className="admin-modal-f">
                            <button className="a-btn a-btn-secondary" onClick={fermerCreationProfil}>Annuler</button>
                            <button className="a-btn a-btn-primary" onClick={handleSaveProfil}>
                                <i className="fa-solid fa-check" /> Créer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ---- FICHE DÉPARTEMENT (maquette : modale deptModal) ---- */}
            {/* Lot CO-b — récapitulatif de création (maquette : showSuccess de saveDept) */}
            {creationReussie && (
                <div className="admin-modal-overlay" onClick={() => setCreationReussie(null)}>
                    <div className="admin-modal sm" role="dialog" aria-label="Département créé" onClick={e => e.stopPropagation()}>
                        <div className="admin-modal-h">
                            <i className="fa-solid fa-circle-check" style={{ color: '#15803d' }} />
                            Département {creationReussie.departement.code} créé
                            <button className="x" onClick={() => setCreationReussie(null)} aria-label="Fermer">&times;</button>
                        </div>
                        <div className="admin-modal-b" style={{ fontSize: 13, color: '#334155' }}>
                            <p><b>{creationReussie.departement.nom}</b>, dans {creationReussie.composante}. Administrateur : <b>{creationReussie.admin}</b>.</p>
                            <p style={{ marginBottom: 4 }}>Installé :</p>
                            <ul className="recap-semis">
                                {Object.entries({
                                    types_journees: ['type de journée', 'types de journées'], stages_modeles_mail: ['modèle de courriel', 'modèles de courriel'],
                                    stages_modeles_eval: ["grille d'évaluation", "grilles d'évaluation"], stages_modeles_visite: ['grille de visite', 'grilles de visite'],
                                    stages_modeles_fiche_liaison: ['fiche de liaison', 'fiches de liaison'],
                                    stages_modeles_documents_retour: ['liste de documents retour', 'listes de documents retour'],
                                    evenements_categories: ["catégorie d'événements", "catégories d'événements"],
                                    certif_reglages: ['réglages de la Certification', 'réglages de la Certification'],
                                    stages_contacts: ['liste des contacts du Stage', 'listes des contacts du Stage']
                                }).filter(([k]) => creationReussie.semis[k]).map(([k, [un, plusieurs]]) => (
                                    <li key={k}>{k === 'certif_reglages' ? un : `${creationReussie.semis[k]} ${creationReussie.semis[k] > 1 ? plusieurs : un}`}</li>
                                ))}
                            </ul>
                            <p style={{ marginTop: 8 }}>Reste à faire par le département : ses <b>formations</b> (Planning annuel → Paramétrage).</p>
                        </div>
                        <div className="admin-modal-f">
                            <button className="a-btn a-btn-primary" onClick={() => setCreationReussie(null)}><i className="fa-solid fa-check" /> Compris</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Lot CO-b (CB0) — suppression DÉFINITIVE : l'inventaire d'abord, puis le code retapé */}
            {suppressionDept && (
                <div className="admin-modal-overlay" onClick={() => setSuppressionDept(null)}>
                    <div className="admin-modal sm" role="dialog" aria-label="Supprimer un département" onClick={e => e.stopPropagation()}>
                        <div className="admin-modal-h">
                            <i className="fa-solid fa-triangle-exclamation" style={{ color: '#dc2626' }} />
                            Supprimer {suppressionDept.dept.code}
                            <button className="x" onClick={() => setSuppressionDept(null)} aria-label="Fermer">&times;</button>
                        </div>
                        <div className="admin-modal-b" style={{ fontSize: 13, color: '#334155' }}>
                            {!suppressionDept.inventaire && <p><i className="fa-solid fa-spinner fa-spin" /> Inventaire du département…</p>}
                            {suppressionDept.inventaire && !suppressionDept.inventaire.supprimable && (
                                <>
                                    <p><b>Ce département contient des données</b> : il ne se supprime pas, il se <b>désactive</b>
                                       (il disparaît des sélecteurs, rien n'est perdu).</p>
                                    <ul className="recap-semis">
                                        {suppressionDept.inventaire.donnees.slice(0, 8).map(x => <li key={x.table}>{x.n} {x.libelle}</li>)}
                                        {suppressionDept.inventaire.donnees.length > 8 && <li>… et {suppressionDept.inventaire.donnees.length - 8} autre(s)</li>}
                                    </ul>
                                </>
                            )}
                            {suppressionDept.inventaire?.supprimable && (
                                <>
                                    <p>Aucune donnée : seule sa configuration (modèles, réglages, administrateurs) existe. La suppression est
                                       <b> définitive</b>.</p>
                                    <label htmlFor="confirmation-suppression">Retapez son code (<b>{suppressionDept.dept.code}</b>) pour confirmer</label>
                                    <input id="confirmation-suppression" value={suppressionDept.saisie} autoFocus
                                           onChange={e => setSuppressionDept(s => ({ ...s, saisie: e.target.value }))} />
                                </>
                            )}
                        </div>
                        <div className="admin-modal-f">
                            <button className="a-btn a-btn-secondary" onClick={() => setSuppressionDept(null)}>Annuler</button>
                            <button className="a-btn a-btn-danger" onClick={confirmerSuppressionDept}
                                    disabled={!suppressionDept.inventaire?.supprimable
                                        || suppressionDept.saisie.trim().toUpperCase() !== suppressionDept.dept.code.toUpperCase()}
                                    title={!suppressionDept.inventaire?.supprimable
                                        ? 'Un département qui contient des données se désactive'
                                        : 'Retapez le code du département pour activer la suppression'}>
                                <i className="fa-solid fa-trash" /> Supprimer définitivement
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Lot CO-a — fiche d'une composante (maquette : compoModal), super-administrateur */}
            {compoForm && (
                <div className="admin-modal-overlay" onClick={() => setCompoForm(null)}>
                    <div className="admin-modal" role="dialog" aria-label="Fiche composante" onClick={e => e.stopPropagation()}>
                        <div className="admin-modal-h">
                            <i className={`fa-solid ${compoForm.id ? 'fa-pen' : 'fa-sitemap'}`} />
                            {compoForm.id ? compoForm.nom : 'Nouvelle composante'}
                            <button className="x" onClick={() => setCompoForm(null)} aria-label="Fermer">&times;</button>
                        </div>
                        <div className="admin-modal-b admin-page">
                            <div className="field-grid">
                                <div className="fg">
                                    <label>Code</label>
                                    <input id="compo-code" value={compoForm.code} disabled={!!compoForm.id} maxLength={20} placeholder="UFR-ST"
                                           onChange={e => setCompoForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} />
                                </div>
                                <div className="fg">
                                    <label>Nom</label>
                                    <input id="compo-nom" value={compoForm.nom} placeholder="UFR Sciences et technologies"
                                           onChange={e => setCompoForm(f => ({ ...f, nom: e.target.value }))} />
                                </div>
                            </div>
                            <div className="fg">
                                <label>Administrateur(s) de composante</label>
                                {compoForm.admins.map((valeur, index) => (
                                    <div key={index} className="admin-ligne">
                                        <select value={valeur} onChange={e => setCompoForm(f => ({ ...f, admins: f.admins.map((v, i) => (i === index ? e.target.value : v)) }))}>
                                            <option value="">— Aucun —</option>
                                            {candidatsCompo
                                                .filter(u => String(u.id) === valeur || !compoForm.admins.includes(String(u.id)))
                                                .map(u => <option key={u.id} value={u.id}>{u.nom_affiche || `${u.prenom} ${u.nom}`}</option>)}
                                        </select>
                                        {compoForm.admins.length > 1 && (
                                            <button className="mini-btn del" title="Retirer cette ligne"
                                                    onClick={() => setCompoForm(f => ({ ...f, admins: f.admins.filter((v, i) => i !== index) }))}>
                                                <i className="fa-solid fa-xmark" />
                                            </button>
                                        )}
                                    </div>
                                ))}
                                {compoForm.admins.every(v => v !== '') && (
                                    <button className="a-btn a-btn-secondary a-btn-sm ajout-admin"
                                            onClick={() => setCompoForm(f => ({ ...f, admins: [...f.admins, ''] }))}>
                                        <i className="fa-solid fa-plus" /> Ajouter un administrateur
                                    </button>
                                )}
                                <span className="hint">
                                    Ils administrent chaque département de la composante sans en être membres : comptes,
                                    droits, administrateurs, activation. Ils n'ont <b>aucun droit sur les modules</b>.
                                </span>
                            </div>
                        </div>
                        <div className="admin-modal-f">
                            {compoForm.id && (
                                <button className="a-btn a-btn-danger" disabled={compoForm.nbDepartements > 0}
                                        title={compoForm.nbDepartements > 0
                                            ? `Elle a encore ${compoForm.nbDepartements} département(s) : une composante ne se supprime que vide`
                                            : 'Supprimer cette composante'}
                                        onClick={() => supprimerCompo(compoForm)}>
                                    <i className="fa-solid fa-trash" /> Supprimer
                                </button>
                            )}
                            <div className="grow" style={{ flex: 1 }} />
                            <button className="a-btn a-btn-secondary" onClick={() => setCompoForm(null)}>Annuler</button>
                            <button className="a-btn a-btn-primary" onClick={saveCompo}>
                                <i className="fa-solid fa-check" /> Enregistrer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showDeptForm && (
                <div className="admin-modal-overlay" onClick={resetDeptForm}>
                    <div className="admin-modal" onClick={e => e.stopPropagation()}>
                        <div className="admin-modal-h">
                            <i className={`fa-solid ${editingDept ? 'fa-pen' : 'fa-plus'}`} />
                            {editingDept ? `Modifier ${editingDept.nom}` : 'Nouveau département'}
                            <button className="x" onClick={resetDeptForm} aria-label="Fermer">&times;</button>
                        </div>

                        <div className="admin-modal-b admin-page">
                            <div className="field-grid">
                                <div className="fg">
                                    <label>Code</label>
                                    <input
                                        value={formDeptCode}
                                        onChange={e => setFormDeptCode(e.target.value)}
                                        placeholder="INFO"
                                        disabled={!!editingDept}
                                    />
                                </div>
                                <div className="fg">
                                    <label>Nom</label>
                                    <input value={formDeptNom} onChange={e => setFormDeptNom(e.target.value)} placeholder="BUT Informatique" />
                                </div>
                            </div>

                            <div className="fg">
                                <label>Description</label>
                                <input value={formDeptDesc} onChange={e => setFormDeptDesc(e.target.value)} placeholder="Description optionnelle…" />
                            </div>

                            {/* Lot CO-a — maquette : select#dCompo. Obligatoire à la création ; un département
                                ne change pas de composante ici. */}
                            {(superAdmin || adminComposante) && (
                                <div className="fg">
                                    <label>Composante</label>
                                    <select
                                        value={editingDept ? String(editingDept.composante_id) : formDeptComposante}
                                        onChange={e => { setFormDeptComposante(e.target.value); chargerCandidatsPremier(e.target.value); }}
                                        disabled={!!editingDept || !superAdmin}
                                    >
                                        {composantes.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                                    </select>
                                    <span className="hint">
                                        {editingDept ? 'Un département ne change pas de composante ici.' : adminComposante ? 'Votre composante.' : ''}
                                    </span>
                                </div>
                            )}

                            {/* Lot CO-b (CB3) — maquette : dAdmin, « Premier administrateur du département * » */}
                            {!editingDept && (
                                <div className="fg">
                                    <label>Premier administrateur du département <span style={{ color: '#dc2626' }}>*</span></label>
                                    <select id="dept-premier-admin" value={formPremierAdmin} onChange={e => setFormPremierAdmin(e.target.value)}>
                                        <option value="">— Choisir —</option>
                                        {candidatsPremier.map(u => (
                                            <option key={u.id} value={u.id}>{u.nom_affiche}{u.departements ? ` (${u.departements})` : ''}</option>
                                        ))}
                                    </select>
                                    <span className="hint">
                                        {candidatsPremier.length === 0
                                            ? "Aucun compte dans cette composante : créez-le d'abord dans l'onglet Utilisateurs (rattaché à un département de la composante)."
                                            : "Il est rattaché au département et l'administre (utilisateurs, profils, signatures, logo). Les droits sur les modules se règlent ensuite par profil."}
                                    </span>
                                </div>
                            )}
                            {!editingDept && (
                                <div className="installe">
                                    <b><i className="fa-solid fa-wand-magic-sparkles" /> À la création, l'application installe :</b>
                                    <ul>
                                        <li>les <b>types de journées</b> par défaut (le département s'ouvre tout de suite) ;</li>
                                        <li>les <b>modèles de courriel</b> du Stage (8) et de la Certification (3), leurs grilles et leurs réglages par défaut ;</li>
                                        <li>le <b>premier administrateur</b> choisi ci-dessus.</li>
                                    </ul>
                                    <div style={{ marginTop: 6, color: '#475569' }}>Reste à faire par le département : ses <b>formations</b> (Planning annuel → Paramétrage).</div>
                                </div>
                            )}
                            {erreurDept && <div className="champ-erreur-dept">{erreurDept}</div>}

                            {/* Nomination — maquette : liste déroulante « Admin(s) de département »,
                                étendue à une ligne par administrateur. R4 (lot CO-a) : l'administrateur
                                de la composante ou le super-administrateur. À la création, c'est le
                                premier administrateur ci-dessus (lot CO-b). */}
                            <div className="fg" style={{ display: peutNommer && editingDept ? undefined : 'none' }}>
                                <label>Admin(s) de département</label>
                                {formDeptAdmins.map((valeur, index) => (
                                    <div key={index} className="admin-ligne">
                                        <select
                                            value={valeur}
                                            onChange={e => changerDeptAdmin(index, e.target.value)}
                                            disabled={!editingDept}
                                        >
                                            <option value="">— Aucun —</option>
                                            {candidatsAdmin
                                                // un compte déjà nommé sur une autre ligne n'est plus proposé
                                                .filter(u => String(u.id) === valeur
                                                    || !formDeptAdmins.includes(String(u.id)))
                                                .map(u => (
                                                    <option key={u.id} value={u.id}>
                                                        {u.nom_affiche || `${u.prenom} ${u.nom}`}
                                                    </option>
                                                ))}
                                        </select>
                                        {formDeptAdmins.length > 1 && (
                                            <button
                                                className="mini-btn del"
                                                title="Retirer cette ligne"
                                                onClick={() => retirerLigneDeptAdmin(index)}
                                            ><i className="fa-solid fa-xmark" /></button>
                                        )}
                                    </div>
                                ))}

                                {editingDept
                                    && formDeptAdmins.every(v => v !== '')
                                    && formDeptAdmins.length < candidatsAdmin.length && (
                                    <button className="a-btn a-btn-secondary a-btn-sm ajout-admin" onClick={ajouterLigneDeptAdmin}>
                                        <i className="fa-solid fa-plus" /> Ajouter un administrateur
                                    </button>
                                )}

                                <span className="hint">
                                    {!editingDept
                                        ? "Enregistrez d'abord le département, rattachez-lui des comptes dans l'onglet Utilisateurs, puis revenez ici pour nommer ses administrateurs."
                                        : candidatsAdmin.length === 0
                                            ? "Aucun compte rattaché à ce département : rattachez-en un dans l'onglet Utilisateurs (filtre Département), puis revenez ici."
                                            : "Ces utilisateurs administreront uniquement ce département : ses comptes et leurs droits, sans accès aux autres départements ni aux onglets réservés. Choisir « Aucun » retire le rôle sans retirer le rattachement."}
                                </span>
                            </div>
                        </div>

                        <div className="admin-modal-f">
                            <button className="a-btn a-btn-secondary" onClick={resetDeptForm}>Annuler</button>
                            <button className="a-btn a-btn-primary" onClick={handleSaveDept}>
                                <i className="fa-solid fa-check" /> Enregistrer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Lot INV-c (maquette ③) : « Inviter les N comptes sans mot de passe » */}
            {inviterN && (() => {
                const boite = boitesReglees.includes(deptGroupe()) || (superAdmin && boitesReglees.length > 0);
                const premier = candidatsInvitation.find(u => inviterN.coches.includes(u.id));
                const n = inviterN.coches.length;
                const fermer = () => { if (!inviterN.enCours) setInviterN(null); };
                return (
                    <div className="admin-modal-overlay" onClick={fermer}>
                        <div className="admin-modal lg" onClick={e => e.stopPropagation()} role="dialog" aria-label="Inviter les comptes sans mot de passe">
                            <div className="admin-modal-h">
                                <i className="fa-solid fa-paper-plane" /> Inviter les comptes sans mot de passe
                                <button className="x" onClick={fermer} aria-label="Fermer">&times;</button>
                            </div>
                            <div className="admin-modal-b admin-page">
                                {inviterN.bilan ? (
                                    <>
                                        <div className={'note ' + (inviterN.bilan.echecs ? 'warn' : '')}>
                                            <i className={'fa-solid ' + (inviterN.bilan.echecs ? 'fa-triangle-exclamation' : 'fa-circle-check')} />
                                            <div><b>{inviterN.bilan.envoyees} invitation{inviterN.bilan.envoyees > 1 ? 's' : ''} envoyée{inviterN.bilan.envoyees > 1 ? 's' : ''}</b>{inviterN.bilan.echecs ? <> · {inviterN.bilan.echecs} non partie{inviterN.bilan.echecs > 1 ? 's' : ''}</> : null}. Chaque envoi est au journal des accès.</div>
                                        </div>
                                        {inviterN.bilan.resultats.filter(x => !x.ok).length > 0 && (
                                            <table className="tbl" style={{ marginTop: 10 }}><thead><tr><th>Compte</th><th>Pourquoi</th></tr></thead><tbody>
                                                {inviterN.bilan.resultats.filter(x => !x.ok).map(x => {
                                                    const u = users.find(v => v.id === x.id);
                                                    return <tr key={x.id}><td>{u ? nomDe(u) : x.id}</td><td>{x.erreur}</td></tr>;
                                                })}
                                            </tbody></table>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {/* décision du 2026-10-18 (point 4) : sur la version de test, les comptes du département reçoivent VRAIMENT */}
                                        {versionTest && (
                                            <div className="note warn acces-test">
                                                <i className="fa-solid fa-flask" />
                                                <div><b>Version de test : ces {n} personne{n > 1 ? 's' : ''} recevr{n > 1 ? 'ont' : 'a'} vraiment ce message</b> (leur compte est rattaché au département). Pour un essai, utilisez plutôt « M'envoyer un essai ».</div>
                                            </div>
                                        )}
                                        <div className="hint" style={{ margin: '0 0 8px' }}>Envoyé <b>en votre nom</b> depuis la boîte du département (« Répondre à » : vous) ; chaque personne reçoit <b>son</b> lien, valable 72 h.</div>
                                        <table className="tbl acces-n">
                                            <thead><tr>
                                                <th style={{ width: 30 }}><input type="checkbox" aria-label="Tout cocher" checked={candidatsInvitation.length > 0 && n === candidatsInvitation.length}
                                                    onChange={e => setInviterN(x => ({ ...x, coches: e.target.checked ? candidatsInvitation.map(u => u.id) : [] }))} /></th>
                                                <th>Compte</th><th>Adresse</th><th>État</th>
                                            </tr></thead>
                                            <tbody>
                                                {candidatsInvitation.map(u => (
                                                    <tr key={u.id}>
                                                        <td><input type="checkbox" checked={inviterN.coches.includes(u.id)} onChange={() => basculerInvite(u.id)} aria-label={`Inviter ${nomDe(u)}`} /></td>
                                                        <td>{nomDe(u)}</td>
                                                        <td className="cell-email">{u.email}</td>
                                                        <td><CelluleAcces a={u.acces} />{u.acces.etat === 'invite' && <div className="acces-sous">le renvoyer rend son lien actuel inutilisable</div>}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        <div className="fg" style={{ marginTop: 10 }}>
                                            <label>Le message (texte fixe)</label>
                                            <div className="acces-apercu">
                                                <div className="acces-apercu-objet">Objet : <b>{versionTest ? '[VERSION DE TEST] ' : ''}Votre accès à l'application</b></div>
                                                Bonjour {premier?.prenom || 'Prénom'},<br />
                                                {user?.nom_affiche || 'Vous'} vous a ouvert un compte sur l'application du département. Pour choisir votre mot de passe, cliquez sur le bouton ci-dessous :
                                                <div><span className="acces-apercu-bouton">Créer mon mot de passe</span></div>
                                                Ce lien est personnel et valable 72 h. Vous n'attendiez pas ce message ? Ignorez-le, ou répondez-y.
                                            </div>
                                        </div>
                                        {!boite && <div className="note warn"><i className="fa-solid fa-triangle-exclamation" /><div>La boîte d'envoi du département n'est pas réglée : rien ne peut partir. Invitez un par un avec « Copier le lien » (menu « … »).</div></div>}
                                        {inviterN.essai && !inviterN.essai.enCours && (
                                            <div className={'note ' + (inviterN.essai.ok ? '' : 'warn')} style={{ marginTop: 8 }}>
                                                <i className={'fa-solid ' + (inviterN.essai.ok ? 'fa-flask' : 'fa-triangle-exclamation')} />
                                                <div>{inviterN.essai.ok ? <>Essai envoyé à <b>{inviterN.essai.a}</b> : son lien ouvre une page de <b>démonstration</b> (rien n'est créé).</> : inviterN.essai.erreur}</div>
                                            </div>
                                        )}
                                        {inviterN.erreur && <div className="admin-alert admin-alert-error" style={{ marginTop: 8 }}>{inviterN.erreur}</div>}
                                    </>
                                )}
                            </div>
                            <div className="admin-modal-f">
                                {inviterN.bilan ? (
                                    <button className="a-btn a-btn-primary" onClick={() => setInviterN(null)}>Fermer</button>
                                ) : (<>
                                    <button className="a-btn a-btn-secondary" disabled={!boite || inviterN.essai?.enCours || inviterN.enCours} onClick={envoyerEssaiInvitation} style={{ marginRight: 'auto' }}>
                                        <i className="fa-solid fa-flask" /> M'envoyer un essai
                                    </button>
                                    <button className="a-btn a-btn-secondary" disabled={inviterN.enCours} onClick={fermer}>Annuler</button>
                                    <button className="a-btn a-btn-primary" disabled={!boite || n === 0 || inviterN.enCours} onClick={envoyerInvitationsN}>
                                        {inviterN.enCours ? <><i className="fa-solid fa-circle-notch fa-spin" /> Envoi…</> : <><i className="fa-solid fa-paper-plane" /> Envoyer {n === 1 ? "l'invitation" : `les ${n} invitations`}</>}
                                    </button>
                                </>)}
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Lot INV-c (maquette ⑤) : le journal des accès */}
            {journal && (() => {
                const q = (journal.recherche || '').trim().toLowerCase();
                const lignes = (journal.lignes || []).filter(l => (!journal.filtre || l.evenement === journal.filtre)
                    && (!q || `${l.compte} ${l.compte_email}`.toLowerCase().includes(q)));
                return (
                    <div className="admin-modal-overlay" onClick={() => setJournal(null)}>
                        <div className="admin-modal lg" onClick={e => e.stopPropagation()} role="dialog" aria-label="Journal des accès">
                            <div className="admin-modal-h">
                                <i className="fa-solid fa-clock-rotate-left" /> Journal des accès
                                <button className="x" onClick={() => setJournal(null)} aria-label="Fermer">&times;</button>
                            </div>
                            <div className="admin-modal-b admin-page">
                                <div className="toolbar">
                                    <div className="search-wrap"><i className="fa-solid fa-magnifying-glass" />
                                        <input value={journal.recherche || ''} onChange={e => setJournal(j => ({ ...j, recherche: e.target.value }))} placeholder="Rechercher un compte…" /></div>
                                    <div className="filtre-dd"><select value={journal.filtre || ''} onChange={e => setJournal(j => ({ ...j, filtre: e.target.value }))} aria-label="Événement">
                                        <option value="">Tous les événements</option>
                                        {(journal.evenements || []).map(ev => <option key={ev.code} value={ev.code}>{ev.libelle}</option>)}
                                    </select></div>
                                    <div className="filtre-dd"><select value={journal.jours} onChange={e => chargerJournal(parseInt(e.target.value, 10))} aria-label="Période">
                                        <option value={7}>7 derniers jours</option><option value={30}>30 derniers jours</option>
                                        <option value={90}>90 derniers jours</option><option value={365}>12 derniers mois</option>
                                    </select></div>
                                </div>
                                {journal.erreur && <div className="admin-alert admin-alert-error">{journal.erreur}</div>}
                                <div className="a-card" style={{ padding: 0, overflowX: 'auto' }}>
                                    <table className="tbl">
                                        <thead><tr><th>Quand</th><th>Compte</th><th>Événement</th><th>Par</th><th>Détail</th></tr></thead>
                                        <tbody>
                                            {journal.chargement && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#64748b', padding: 16 }}><i className="fa-solid fa-circle-notch fa-spin" /> Chargement…</td></tr>}
                                            {!journal.chargement && lignes.map(l => (
                                                <tr key={l.id}>
                                                    <td style={{ whiteSpace: 'nowrap' }}>{dateHeureFr(l.quand)}</td>
                                                    <td>{l.compte}<div className="acces-sous">{l.compte_email}</div></td>
                                                    <td><span className={'tag' + (l.ton ? ' tag-' + l.ton : '')}>{l.libelle}</span></td>
                                                    <td>{l.par ? (l.par === 'lui-même' ? '— (lui-même)' : l.par) : '—'}</td>
                                                    <td className="acces-detail">{l.detail}</td>
                                                </tr>
                                            ))}
                                            {!journal.chargement && lignes.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#64748b', padding: 16 }}>Aucun événement sur la période</td></tr>}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="hint" style={{ marginTop: 8 }}>Les comptes de votre périmètre seulement. Une demande « mot de passe oublié » pour une adresse inconnue n'apparaît pas (elle ne nomme personne). Ni lien, ni mot de passe ne sont jamais affichés.</div>
                            </div>
                            <div className="admin-modal-f"><button className="a-btn a-btn-primary" onClick={() => setJournal(null)}>Fermer</button></div>
                        </div>
                    </div>
                );
            })()}

            {/* Lot INV-b (maquette ④) : le lien, montré UNE fois */}
            {lienCopie && (
                <div className="admin-modal-overlay" onClick={() => setLienCopie(null)}>
                    <div className="admin-modal" onClick={e => e.stopPropagation()} role="dialog" aria-label="Lien du compte">
                        <div className="admin-modal-h">
                            <i className="fa-solid fa-link" />
                            {lienCopie.type === 'invitation' ? "Lien d'invitation" : 'Lien de réinitialisation'} de {nomDe(lienCopie.user)}
                            <button className="x" onClick={() => setLienCopie(null)} aria-label="Fermer">&times;</button>
                        </div>
                        <div className="admin-modal-b admin-page">
                            {lienCopie.raison && <div className="note warn" style={{ marginBottom: 10 }}><i className="fa-solid fa-circle-info" /><div>{lienCopie.raison}</div></div>}
                            <div className="lien-a-copier" id="lien-a-copier">{lienCopie.lien}</div>
                            <div className="hint" style={{ marginTop: 6 }}>Valable jusqu'au <b>{dateHeureFr(lienCopie.expire_le)}</b> ({lienCopie.type === 'invitation' ? '72 h' : '1 h'}) · un seul usage</div>
                            <div className="note warn" style={{ marginTop: 10 }}>
                                <i className="fa-solid fa-user-lock" />
                                <div>Ce lien donne accès au compte de {nomDe(lienCopie.user)} : transmettez-le <b>à cette personne seule</b> (par sa messagerie universitaire). Il ne sera plus jamais affiché ; copier un nouveau lien rend celui-ci inutilisable.</div>
                            </div>
                        </div>
                        <div className="admin-modal-f">
                            <button className="a-btn a-btn-secondary" onClick={() => setLienCopie(null)}>Fermer</button>
                            <button className="a-btn a-btn-primary" onClick={copierLien}>
                                <i className={'fa-solid ' + (lienCopie.copie ? 'fa-check' : 'fa-copy')} /> {lienCopie.copie ? 'Lien copié' : 'Copier le lien'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {confirmation && (
                <ModaleConfirmation
                    titre={confirmation.titre}
                    message={confirmation.message}
                    libelleConfirmer={confirmation.libelleConfirmer}
                    danger={confirmation.danger}
                    onAnnuler={() => setConfirmation(null)}
                    onConfirmer={() => { const a = confirmation.action; setConfirmation(null); if (a) a(); }}
                />
            )}
        </div> {/* admin-page */}
        </AppLayout>
    );
}

/** « 20/10/2026 à 14:05 » */
function dateHeureFr(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return `${d.toLocaleDateString('fr-FR')} à ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
}

/** Lot INV-b (maquette ①) : l'état de l'accès d'un compte, calculé par le serveur (rien à saisir). */
function CelluleAcces({ a }) {
    if (!a) return <span className="tag">—</span>;
    const jour = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '');
    const court = (iso) => { if (!iso) return ''; const d = new Date(iso); return `${d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} à ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`; };
    if (a.etat === 'actif') return <><span className="tag tag-ok"><i className="fa-solid fa-check" /> Actif</span>{a.depuis && <div className="acces-sous">depuis le {jour(a.depuis)}</div>}</>;
    if (a.etat === 'invite') return <><span className="tag tag-info"><i className="fa-solid fa-envelope" /> Invité</span><div className="acces-sous">jusqu'au {court(a.expire_le)}</div></>;
    if (a.etat === 'expire') return <><span className="tag tag-warn"><i className="fa-solid fa-hourglass-end" /> Lien expiré</span><div className="acces-sous">invité le {jour(a.invite_le)}</div></>;
    if (a.etat === 'ent') return <span className="tag"><i className="fa-solid fa-building-columns" /> ENT</span>;
    return <span className="tag"><i className="fa-solid fa-circle-pause" /> Pas encore invité</span>;
}

/**
 * Lot INV-b (maquette ①) : le menu « … » de l'accès au compte. Les actes suivent la règle R2 (`actions_compte`, décidée
 * par le serveur) : grisés avec leur raison quand on ne couvre pas toute la personne.
 */
function MenuAcces({ u, ouvert, onOuvrir, onFermer, boite, onInviter, onReinitialiser, onCopier }) {
    // ⚠️ Position FIXE calculée sur le bouton : dans le tableau (qui peut défiler en largeur), un menu en position absolue
    // était ROGNÉ par son cadre (mesuré à l'image le 2026-10-18).
    const bouton = useRef(null);
    const r = ouvert && bouton.current ? bouton.current.getBoundingClientRect() : null;
    const place = r ? { top: Math.round(r.bottom + 4), right: Math.max(8, Math.round(window.innerWidth - r.right)) } : undefined;
    const etat = u.acces?.etat;
    const refus = !u.actif ? 'Compte désactivé' : u.actions_compte && !u.actions_compte.possible ? u.actions_compte.raison : etat === 'ent' ? "Ce compte se connecte par l'ENT : aucun lien" : null;
    const sansBoite = "Boîte d'envoi du département non réglée : utilisez « Copier le lien »";
    const item = (icone, libelle, faire, raison, aide) => (
        <button type="button" className={'acces-item' + (raison ? ' gris' : '')} disabled={!!raison} title={raison || ''} onClick={faire}>
            <i className={'fa-solid ' + (raison ? 'fa-lock' : icone)} />
            <span>{libelle}{(raison || aide) && <small>{raison || aide}</small>}</span>
        </button>
    );
    return (
        <span className="acces-menu">
            <button ref={bouton} className={'mini-btn' + (ouvert ? ' on' : '')} title="Accès au compte" aria-label="Accès au compte" aria-expanded={ouvert} onClick={onOuvrir}>
                <i className="fa-solid fa-ellipsis" />
            </button>
            {ouvert && (<>
                <span className="acces-voile" onClick={onFermer} />
                <span className="acces-liste" role="menu" style={place}>
                    {refus ? item('fa-lock', etat === 'actif' ? 'Envoyer un lien de réinitialisation' : "Envoyer l'invitation", null, refus)
                    : etat === 'actif' ? (<>
                        {item('fa-key', 'Envoyer un lien de réinitialisation', onReinitialiser, boite ? null : sansBoite, 'valable 1 h, par courriel')}
                        {item('fa-link', 'Copier un lien de réinitialisation', onCopier, null, 'à transmettre vous-même')}
                    </>) : (<>
                        {item('fa-paper-plane', etat === 'non_invite' ? "Envoyer l'invitation" : "Renvoyer l'invitation", onInviter, boite ? null : sansBoite,
                              etat === 'invite' ? "nouveau lien, l'ancien ne marchera plus" : 'valable 72 h, par courriel')}
                        {item('fa-link', "Copier le lien d'invitation", onCopier, null, 'à transmettre vous-même')}
                    </>)}
                </span>
            </>)}
        </span>
    );
}

export default AdminPanel;